import { FormEvent, Fragment, type ReactNode, useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  ChevronRight,
  Clock,
  Download,
  Edit,
  Eye,
  FileSpreadsheet,
  FileText,
  Plus,
  Printer,
  GitBranch,
  Search,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { Account, BankStatementImport, Budget, Fee, FeeStructure, financialService, Invoice, JournalEntry, LedgerPayment, PayrollPeriod, PayrollSettlement, Payslip, StudentOption, VendorBill } from "../services/financialService";
import StudentSearchField from "../components/StudentSearchField";
import { ConfirmDialog, type ConfirmRequest } from "../components/ConfirmDialog";
import AccountRolesPanel from "../components/financial/AccountRolesPanel";
import SalaryAdvancesPanel from "../components/financial/SalaryAdvancesPanel";
import FinancialStatementsView from "../components/financial/FinancialStatementsView";
import StatementTemplateEditor from "../components/financial/StatementTemplateEditor";
import AccountingPeriodsPanel from "../components/financial/AccountingPeriodsPanel";
import FinancialSectionNav from "../components/financial/FinancialSectionNav";
import AccountSelect from "../components/financial/AccountSelect";
import StudentFinancialRecord from "../components/financial/StudentFinancialRecord";
import ChartOfAccountsView from "../components/financial/ChartOfAccountsView";
import { ACCOUNT_TYPES } from "../components/financial/chartOfAccounts";
import { Modal } from "../components/Modal";
import AIAssistField from "../components/AIAssistField";
import { useAIStatusQuery } from "../hooks/useAIStatus";
import { Pager, paginate } from "../components/Pager";
import { Field, FormSection } from "../components/FormLayout";
import { ExportButtons, type ExportFormat } from "../components/ExportButtons";
import { FeeDetailsModal } from "../components/financial/FeeDetailsModal";
import { FeeStructureSummary } from "../components/financial/FeeStructureSummary";
import { PaymentDetailsModal } from "../components/financial/PaymentDetailsModal";
import { RefundRequestForm } from "../components/financial/RefundRequestForm";
import { InvoiceDetailsModal } from "../components/financial/InvoiceDetailsModal";
import { RefundDetailsModal } from "../components/financial/RefundDetailsModal";
import { CreditNoteDetailsModal } from "../components/financial/CreditNoteDetailsModal";
import { CreditNoteFields } from "../components/financial/CreditNoteFields";
import { VendorBillFields } from "../components/financial/VendorBillFields";
import { payrollColumns, payrollConfig, payrollStage } from "../components/financial/payrollRegister";
import { approvalStage, budgetColumns, budgetConfig, budgetTotal, grantColumns, grantConfig, restrictionLabel } from "../components/financial/planningRegisters";
import { bankLineRegister, depositColumns, depositConfig, depositStatus, lineStatus, unidentifiedColumns, unidentifiedConfig, unidentifiedStatus } from "../components/financial/bankingRegisters";
import { billNumber, billOverdue, billPayable, billStage, payablesColumns, payablesConfig } from "../components/financial/payablesRegister";
import { ClientRegister, StatusDot } from "../components/register/ClientRegister";
import { ActionPanel, DetailsModal } from "../components/register/DetailsModal";
import ManagementReportsView from "../components/financial/ManagementReportsView";
import FinanceOverview from "../components/financial/FinanceOverview";
import { humanise, statusDot } from "../components/register/registerLogic";
import {
  boardingColumns,
  boardingConfig,
  chargeIsOpen,
  chargeIsOverdue,
  chargeStatus,
  mealOrderCanRefund,
  mealOrderCanTakePayment,
  mealOrderColumns,
  mealOrderConfig,
  mealPlanCanRefund,
  mealPlanCanTakePayment,
  mealPlanColumns,
  mealPlanConfig,
  PaymentMethodField,
  transportColumns,
  transportConfig,
  uniformBalance,
  uniformCanTakePayment,
  uniformColumns,
  uniformConfig,
} from "../components/financial/serviceRegisters";
import {
  countCreditFilters,
  CREDIT_ORDERINGS,
  CREDIT_PAGE_SIZES,
  CREDIT_STAGE_DOTS,
  CREDIT_VIEWS,
  creditNeedsActor,
  creditNoteExportColumns,
  creditNoteStage,
  creditPreparedOn,
  creditStageLabel,
  creditViewCounts,
  EMPTY_CREDIT_FILTERS,
  filterCreditNotes,
  type CreditNoteFilters,
  type CreditView,
} from "../components/financial/creditNoteRegister";
import {
  countRefundFilters,
  EMPTY_REFUND_FILTERS,
  filterRefunds,
  REFUND_ORDERINGS,
  REFUND_PAGE_SIZES,
  REFUND_STATUS_DOTS,
  REFUND_STATUS_LABELS,
  REFUND_STATUSES,
  refundExportColumns,
  refundRequestedOn,
  refundStatusCounts,
  refundStatusLabel,
  type RefundFilters,
} from "../components/financial/refundRegister";
import {
  activeInvoiceView,
  countInvoiceFilters,
  daysOverdue,
  EMPTY_INVOICE_FILTERS,
  INVOICE_ORDERINGS,
  INVOICE_PAGE_SIZES,
  INVOICE_STATUS_DOTS,
  INVOICE_STATUS_LABELS,
  INVOICE_VIEWS,
  invoiceBilling,
  invoiceExportColumns,
  invoiceIssuedOn,
  invoiceRegisterParams,
  invoiceStatusLabel,
  type InvoiceRegisterFilters,
  type InvoiceView,
} from "../components/financial/invoiceRegister";
import {
  activePeriod,
  countPaymentFilters,
  EMPTY_PAYMENT_FILTERS,
  PAYMENT_METHODS,
  PAYMENT_ORDERINGS,
  PAYMENT_PAGE_SIZES,
  PAYMENT_PERIODS,
  PAYMENT_STATUS_DOTS,
  PAYMENT_STATUS_LABELS,
  paymentDay,
  paymentExportColumns,
  paymentInvoices,
  paymentMethodLabel,
  paymentRegisterParams,
  paymentStatusLabel,
  periodRange,
  type PaymentPeriod,
  type PaymentRegisterFilters,
} from "../components/financial/paymentRegister";
import {
  countFeeStructureFilters,
  EMPTY_FEE_STRUCTURE_FILTERS,
  FEE_STRUCTURE_ORDERINGS,
  FEE_STRUCTURE_PAGE_SIZES,
  feeStructureExportColumns,
  feeStructureInvoicingCounts,
  filterFeeStructures,
  perStudentLabel,
  type FeeStructureContext,
  type FeeStructureFilters,
} from "../components/financial/feeStructures";
import {
  countFeeFilters,
  EMPTY_FEE_FILTERS,
  FEE_CATEGORIES,
  FEE_ORDERINGS,
  FEE_PAGE_SIZES,
  feeAudienceLabel,
  feeCategoryLabel,
  feeExportColumns,
  feeStatusCounts,
  feeTermsLabel,
  filterFees,
  type FeeFilters,
} from "../components/financial/feeCatalogue";
import {
  activeFinanceStudentFilterCount,
  EMPTY_FINANCE_STUDENT_FILTERS,
  FINANCE_ENROLMENT,
  FINANCE_STUDENT_ORDERINGS,
  FINANCE_STUDENT_PAGE_SIZES,
  financeStudentParams,
  initials,
  STUDENT_ENROLMENT_DOTS,
  type FinanceStudentFilters,
} from "./financeStudentFilters";
import { transportService } from "../services/transportService";
import { hostelService } from "../services/hostelService";
import { cafeteriaService } from "../services/cafeteriaService";
import { uniformService } from "../services/uniformService";
import { academicSetupService } from "../services/academicSetupService";
import { useAuthStore } from "../stores/authStore";
import { usePermissions } from "../hooks/usePermissions";
import { getFinancialRouteId } from "../config/financialRoutes";
import { exportAsCsv, exportAsExcel, exportAsPdf } from "../utils/exportTable";
import { nowLocalInput, todayIso } from "../utils/dates";

type GuidedJournalLineState = {
  account: string;
  effect: "increase" | "decrease";
  amount: string;
  description: string;
};

type JournalTemplate = "general" | "internal_transfer" | "immediate_expense" | "correction";

const emptyGuidedJournalLine = (): GuidedJournalLineState => ({
  account: "",
  effect: "increase",
  amount: "",
  description: "",
});

/** Reads a DRF error out of a failed file download, whose body is a Blob. */
const blobError = async (failure: unknown) => {
  const data = (failure as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      return financialError({ response: { data: JSON.parse(await data.text()) } });
    } catch {
      return "The file could not be generated.";
    }
  }
  return financialError(failure);
};

/** Whoever raised a document may correct it until they send it for approval;
 *  after that it belongs to the reviewer, and nobody may correct somebody
 *  else's. The server enforces the same rule -- this is what decides whether
 *  the Edit button is worth offering. */
export const isEditableByInitiator = (
  document: { workflow_status?: string },
  initiatorId: number | null | undefined,
  userId: number | null | undefined,
) =>
  initiatorId != null && userId != null && initiatorId === userId &&
  ["draft", "returned"].includes(document.workflow_status || "");

/** Who may delete an account outright, rather than work it through the
 *  approval chain.
 *
 *  An approver may remove any account nothing has been posted to. The bursar
 *  who raised a request may drop it while it is still theirs -- one sent back
 *  to them that the school turned out not to need, or a name they would
 *  rather retype than correct. The server applies the same rule in
 *  AccountDetailView.perform_destroy. */
export const canDeleteAccount = (
  account: { workflow_status?: string; transaction_count?: number; initiated_by?: number | null },
  userId: number | null | undefined,
  permissions: { canApprove: boolean; canCreateAccountRequests: boolean },
) =>
  (account.transaction_count ?? 0) === 0 &&
  (permissions.canApprove ||
    (permissions.canCreateAccountRequests &&
      isEditableByInitiator(account, account.initiated_by, userId)));

/** What deleting this account actually costs, said before it happens.
 *
 *  Deleting a request the bursar still holds is an ordinary correction --
 *  nothing has relied on it yet. Deleting an account the chart has already
 *  approved is not, even with no postings against it, so the two do not get
 *  the same warning. */
export const accountDeleteMessage = (account: { workflow_status?: string }) =>
  account.workflow_status === "approved"
    ? "This account is part of the approved chart of accounts. Nothing has been posted to it, so it can still go, but it will disappear from every account list and anything already pointing at it will refuse the deletion."
    : "This request never reached the chart of accounts, so deleting it affects nothing else. If the details are simply wrong, Edit corrects them and keeps the request.";

/** Bring the page's edit form back into view.
 *
 *  The app scrolls inside Layout's <main>, not the window: the page root is
 *  h-screen/overflow-hidden, so window.scrollTo moves nothing. Anyone who had
 *  scrolled down to the table saw an Edit button that quietly filled in a form
 *  far above the fold and looked like it had done nothing at all. */
export const scrollPageToTop = (scroller: Element | null = document.querySelector("main")) => {
  if (!scroller) {
    window.scrollTo({ top: 0, behavior: "smooth" });
  } else if (typeof scroller.scrollTo === "function") {
    scroller.scrollTo({ top: 0, behavior: "smooth" });
  } else {
    scroller.scrollTop = 0;
  }
};

/** The ledger stored a debit or a credit; the guided form asks whether the
 *  account went up or down, so an entry being corrected has to be read back
 *  the same way it was written. An account the chart has not loaded is taken
 *  as debit-normal, which is where a blank line starts anyway. */
export const journalLineEffect = (
  line: { debit: string; credit: string },
  account?: { normal_balance: "debit" | "credit" },
): "increase" | "decrease" => {
  const debitNormal = account ? account.normal_balance === "debit" : true;
  return debitNormal === (Number(line.debit) > 0) ? "increase" : "decrease";
};

/** Tells the initiator which document the form is currently correcting, and
 *  why it came back, so the reviewer's reason is in front of them while they
 *  fix it rather than back down the page in the register. */
function EditingNotice({ label, reason, onCancel }: { label: string; reason?: string; onCancel: () => void }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
      <div>
        <strong>Editing {label}</strong>
        {reason ? <div className="mt-1">Sent back for changes: {reason}</div> : null}
        <div className="mt-1 text-xs text-amber-800">
          Saving keeps it a draft — submit it again when it is ready for approval.
        </div>
      </div>
      <button type="button" className="btn btn-outline px-3 py-1.5 text-xs" onClick={onCancel}>
        Cancel edit
      </button>
    </div>
  );
}

const financialError = (failure: unknown) => {
  const payload = (failure as { response?: { data?: unknown } })?.response?.data;
  if (payload && typeof payload === 'object') {
    return Object.entries(payload).map(([field, value]) =>
      `${field}: ${Array.isArray(value) ? value.join(' ') : String(value)}`,
    ).join(' ');
  }
  return 'The financial operation could not be completed.';
};

// An account joins the chart only once its creation is approved -- until then
// it is a request: absent from every report, and refused by the ledger.
const isApproved = (account: Account) => account.workflow_status === "approved";
// The same rule the backend posts by (accounting.available_for_posting): open,
// approved, and not a summary account whose subaccounts carry the balance.
const canReceivePostings = (account: Account) =>
  isApproved(account) && account.is_active && !account.is_reporting_account;

/** Where a payment may leave from: a cash or bank asset, never one like Salary
 * advances owed by staff, which is money owed to the school, not money it holds. */
const canPayFrom = (account: Account) =>
  canReceivePostings(account) && account.account_type === "asset" && account.can_pay_from !== false;

/** Settle a whole run in one bank transfer.
 *
 * The school sends a single instruction covering net pay, PAYE, NSSF and LST,
 * so it fetches what that instruction has to cover -- netting off anything
 * already remitted separately -- and shows the bursar the figure before they
 * commit to it. */
/**
 * A payment raised against a run and still on its way through approval.
 *
 * It matters that this reads as "not paid yet": the run's own status still says
 * what it owes, and the money is only spoken for, not gone.
 */
function PendingSettlement({
  settlement,
  canReview,
  canApprove,
  canInitiate,
  userId,
  onVerify,
  onApprove,
  onReturn,
  onCancel,
}: {
  settlement: PayrollSettlement;
  canReview: boolean;
  canApprove: boolean;
  canInitiate: boolean;
  userId?: number;
  onVerify: () => void;
  onApprove: () => void;
  onReturn: (reason: string) => void;
  onCancel: () => void;
}) {
  const status = settlement.workflow_status || "submitted",
    raisedByMe = settlement.initiated_by === userId,
    label = status === "verified" ? "Reviewed, waiting for approval" : "Waiting for review";

  return (
    <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-gray-900">
            Payment of UGX {Number(settlement.amount).toLocaleString()} — {label}
          </p>
          <p className="mt-0.5 text-xs text-gray-600">
            {settlement.components.map((c) => `${c.label} ${Number(c.amount).toLocaleString()}`).join(" · ")}
          </p>
          <p className="mt-0.5 text-xs text-gray-500">
            From {settlement.payment_account_name} · reference {settlement.reference} ·{" "}
            {settlement.payment_date} · raised by {settlement.initiated_by_name}
          </p>
          {settlement.return_reason && (
            <p className="mt-1 text-xs text-red-700">Returned: {settlement.return_reason}</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          {canReview && status === "submitted" && !raisedByMe && (
            <button className="text-primary-600 hover:text-primary-700" onClick={onVerify}>Mark reviewed</button>
          )}
          {canApprove && status === "verified" && !raisedByMe && (
            <button className="btn btn-primary" onClick={onApprove}>Approve &amp; pay</button>
          )}
          {canReview && ["submitted", "verified"].includes(status) && !raisedByMe && (
            <button
              className="text-amber-700"
              onClick={() => {
                const reason = window.prompt("What needs to change on this payment?")?.trim();
                if (reason) onReturn(reason);
              }}
            >Return</button>
          )}
          {canInitiate && raisedByMe && ["submitted", "returned"].includes(status) && (
            <button className="text-red-600" onClick={onCancel}>Cancel</button>
          )}
        </div>
      </div>
      <p className="mt-2 text-xs text-amber-800">
        Nothing has left the bank yet. The run still shows what it owes until this is approved,
        and this money cannot be raised on a second payment while it waits.
      </p>
    </div>
  );
}

/**
 * Paying an approved run: all of it in one transfer, or one part at a time.
 *
 * The school pays a month at a time and rarely all at once -- staff this week,
 * URA next -- so what to pay is chosen here, under the month it belongs to,
 * rather than on a separate screen where the month has to be found again.
 */
function PayrollPaymentPanel({
  periodId,
  accounts,
  isSelectable,
  onSubmit,
  pending,
}: {
  periodId: number;
  accounts: Account[] | undefined;
  isSelectable: (account: Account) => boolean;
  onSubmit: (
    event: FormEvent<HTMLFormElement>,
    id: number,
    part: { role: string; amount: string } | null,
  ) => void;
  pending: boolean;
}) {
  const preview = useQuery({
    queryKey: ["payroll-settlement-preview", periodId],
    queryFn: () => financialService.getPayrollSettlementPreview(periodId),
  });
  // "" means the whole run in one transfer; otherwise the role being paid.
  const [part, setPart] = useState("");

  const components = preview.data?.components ?? [];
  const settled = preview.data?.settled ?? [];
  const chosen = components.find((row) => row.role === part) ?? null;
  if (!preview.isLoading && components.length === 0) return null;

  return (
    <form
      onSubmit={(event) =>
        onSubmit(event, periodId, chosen ? { role: chosen.role, amount: "" } : null)
      }
      className="mt-3 rounded-md border border-primary-200 bg-primary-50/50 p-3"
    >
      <p className="text-sm font-medium text-gray-900">Pay this run</p>
      <p className="mt-0.5 text-xs text-gray-600">
        Everything in one bank transfer, or one part at a time. Either way the payment goes for
        review and approval before any money moves.
      </p>

      {preview.isLoading ? (
        <p className="mt-2 text-xs text-gray-500">Working out what is still owed…</p>
      ) : (
        <ul className="mt-2 space-y-0.5 text-sm">
          {components.map((row) => (
            <li key={row.role} className="flex justify-between gap-4 text-gray-700">
              <span>{row.label}</span>
              <span className="tabular-nums">UGX {Number(row.amount).toLocaleString()}</span>
            </li>
          ))}
          <li className="flex justify-between gap-4 border-t border-primary-200 pt-1 font-semibold text-gray-900">
            <span>Still owed on this run</span>
            <span className="tabular-nums">
              UGX {Number(preview.data?.total ?? 0).toLocaleString()}
            </span>
          </li>
          {settled.map((row) => (
            <li key={row.role} className="flex justify-between gap-4 text-xs text-gray-500">
              <span>
                {row.label} — already paid
                {row.vouchers.length > 0 ? ` (${row.vouchers.join(", ")})` : ", earlier"}
              </span>
              <span className="tabular-nums">UGX {Number(row.amount).toLocaleString()}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-2 grid gap-2 md:grid-cols-4">
        <label className="text-sm text-gray-600 md:col-span-2">
          What to pay
          <select
            className="input mt-1"
            value={part}
            onChange={(event) => setPart(event.target.value)}
          >
            <option value="">Everything still owed, in one transfer</option>
            {components.map((row) => (
              <option key={row.role} value={row.role}>
                {row.label} — UGX {Number(row.amount).toLocaleString()}
              </option>
            ))}
          </select>
        </label>
        {chosen && (
          <label className="text-sm text-gray-600">
            Amount
            <input
              required
              name="amount"
              type="number"
              step="0.01"
              min="0.01"
              max={chosen.amount}
              // Prefilled with the whole of that part, but editable: a run can
              // go out with one person's bank details wrong and the rest of the
              // staff should not wait for it.
              key={chosen.role}
              defaultValue={chosen.amount}
              className="input mt-1"
            />
          </label>
        )}
        <label className="text-sm text-gray-600 md:col-span-2">
          Paid from
          <AccountSelect
            required
            name="account"
            className="mt-1"
            placeholder="Bank or cash account"
            accounts={accounts}
            isSelectable={isSelectable}
          />
        </label>
        <label className="text-sm text-gray-600">
          Bank reference
          <input required name="reference" className="input mt-1" placeholder="e.g. STANBIC-0091" />
        </label>
        <label className="text-sm text-gray-600">
          Payment date
          <input name="date" type="date" max={todayIso()} className="input mt-1" />
        </label>
      </div>
      <button className="btn btn-primary mt-2" disabled={pending || preview.isLoading}>
        {chosen ? `Pay ${chosen.label.toLowerCase()} for review` : "Pay everything, for review"}
      </button>
    </form>
  );
}

/** "term_2" is how the calendar stores it; "Term 2" is what people read. */
const termLabel = (term: string) => `Term ${term.replace("term_", "")}`;

// What the register filters offer, from the models rather than from whichever
// rows happen to be on the page in front of the user.
const INVOICE_STATUSES = ["draft", "sent", "paid", "overdue", "credited", "cancelled"];
const PAYMENT_STATUSES = ["pending", "processing", "completed", "part_refunded", "failed", "cancelled", "refunded"];
const JOURNAL_STATUSES = ["draft", "posted", "reversed"];
// The dot beside an entry's stage in the journal register.
const JOURNAL_STAGE_DOTS: Record<string, string> = {
  draft: "bg-gray-400",
  submitted: "bg-blue-500",
  verified: "bg-indigo-500",
  returned: "bg-amber-500",
  posted: "bg-green-500",
  approved: "bg-green-500",
  rejected: "bg-red-500",
  reversed: "bg-red-400",
};
const JOURNAL_PAGE_SIZES = [10, 25, 30, 50, 100];
const journalActionClass = "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-primary-700 hover:bg-primary-50";
// The trial balance is read in chart-of-accounts order: what the school owns,
// what it owes, its funds, then what came in and what went out.
const TRIAL_BALANCE_GROUPS = [
  { type: "asset", label: "Assets" },
  { type: "liability", label: "Liabilities" },
  { type: "equity", label: "Equity" },
  { type: "income", label: "Income" },
  { type: "expense", label: "Expenses" },
];

export default function Financial() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const { hasCapability } = usePermissions();
  // Capability-driven (admin-assignable), not a hardcoded job-title list --
  // see apps/core/models.py Capability/RoleCapability/UserCapabilityGrant.
  const canInitiate = hasCapability("financial.initiate_transactions");
  const canReview = hasCapability("financial.review_transactions");
  const canApprove = hasCapability("financial.approve_transactions");
  const canReverse = hasCapability("financial.reverse_transactions");
  const canPostTransactions = hasCapability("financial.post_transactions");
  const canReconcile = hasCapability("financial.reconcile_bank_accounts");
  const canClosePeriod = hasCapability("financial.close_accounting_periods");
  const canConfigureAccounting = hasCapability("financial.configure_accounting");
  const canCreateAccountRequests = hasCapability("financial.create_account_requests");
  const canConfigureFees = hasCapability("financial.configure_fees");
  const canGenerateFeeInvoices = hasCapability("financial.generate_fee_invoices");
  const canRequestPeriodReopen = hasCapability("financial.request_period_reopen");
  const canReopenPeriod = hasCapability("financial.reopen_accounting_periods");
  const canViewAuditLogs = hasCapability("financial.view_audit_logs");
  const canManageSuppliers = hasCapability("financial.manage_suppliers");
  const canExportReports = hasCapability("financial.export_reports");
  const activeTab = getFinancialRouteId(location.pathname);
  const trialBalanceAccountId =
    activeTab === "trial-balance"
      ? location.pathname.replace(/\/+$/, "").split("/")[3] ?? ""
      : "";
  const showOverviewSummary = activeTab === "overview";
  // What is typed in the search box reaches the filters after a pause, so the
  // directory is not re-fetched on every keystroke.
  const [studentDirectorySearch, setStudentDirectorySearch] = useState("");
  const [studentFilters, setStudentFilters] = useState(EMPTY_FINANCE_STUDENT_FILTERS);
  const [studentDirectoryPage, setStudentDirectoryPage] = useState(1);
  const [studentDirectoryPageSize, setStudentDirectoryPageSize] = useState(FINANCE_STUDENT_PAGE_SIZES[0]);
  const [isExportingStudentDirectory, setIsExportingStudentDirectory] = useState(false);
  const [paymentRegisterFilters, setPaymentRegisterFilters] = useState<PaymentRegisterFilters>(EMPTY_PAYMENT_FILTERS);
  const [paymentRegisterPageSize, setPaymentRegisterPageSize] = useState(25);
  const [viewingPaymentId, setViewingPaymentId] = useState<number | null>(null);
  // The receipt whose refund request form is open in its details panel.
  const [refundFormFor, setRefundFormFor] = useState<number | null>(null);
  const [paymentPage, setPaymentPage] = useState(1);
  const [isExportingPayments, setIsExportingPayments] = useState(false);
  const [refundFilters, setRefundFilters] = useState<RefundFilters>(EMPTY_REFUND_FILTERS);
  const [refundPageSize, setRefundPageSize] = useState(25);
  const [viewingRefundId, setViewingRefundId] = useState<number | null>(null);
  const [refundDecisionNotes, setRefundDecisionNotes] = useState("");
  const [refundPage, setRefundPage] = useState(1);
  const [invoiceRegisterFilters, setInvoiceRegisterFilters] = useState<InvoiceRegisterFilters>(EMPTY_INVOICE_FILTERS);
  const [invoiceRegisterPageSize, setInvoiceRegisterPageSize] = useState(25);
  const [viewingInvoiceId, setViewingInvoiceId] = useState<number | null>(null);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  // Bumped after an invoice is issued so the student search starts empty again.
  const [invoiceFormKey, setInvoiceFormKey] = useState(0);
  const [invoicePage, setInvoicePage] = useState(1);
  const [isExportingInvoices, setIsExportingInvoices] = useState(false);
  const [journalSearch, setJournalSearch] = useState("");
  const [journalStatusFilter, setJournalStatusFilter] = useState("all");
  const [journalPage, setJournalPage] = useState(1);
  const [journalPageSize, setJournalPageSize] = useState(JOURNAL_PAGE_SIZES[0]);
  const [isExportingJournals, setIsExportingJournals] = useState(false);
  const [expandedJournalId, setExpandedJournalId] = useState<number | null>(null);
  const [creditFilters, setCreditFilters] = useState<CreditNoteFilters>(EMPTY_CREDIT_FILTERS);
  const [creditPageSize, setCreditPageSize] = useState(25);
  const [viewingCreditId, setViewingCreditId] = useState<number | null>(null);
  const [creditDecisionNotes, setCreditDecisionNotes] = useState("");
  const [showCreditForm, setShowCreditForm] = useState(false);
  // Service charges: the open record on each tab, and the new-charge forms.
  const [viewingTransportId, setViewingTransportId] = useState<number | null>(null);
  const [viewingHostelId, setViewingHostelId] = useState<number | null>(null);
  const [viewingMealOrderId, setViewingMealOrderId] = useState<number | null>(null);
  const [viewingMealPlanId, setViewingMealPlanId] = useState<number | null>(null);
  const [viewingUniformId, setViewingUniformId] = useState<number | null>(null);
  const [showTransportForm, setShowTransportForm] = useState(false);
  const [showHostelForm, setShowHostelForm] = useState(false);
  // Payables: the open bill, its reviewer comment, and the two new-record forms.
  const [viewingBillId, setViewingBillId] = useState<number | null>(null);
  const [billDecisionNotes, setBillDecisionNotes] = useState("");
  const [showBillForm, setShowBillForm] = useState(false);
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  // Banking: which of its four views is shown, the open record, its note, and the forms.
  const [bankingView, setBankingView] = useState<"reconcile" | "deposits" | "unidentified" | "setup">("reconcile");
  const [bankingForm, setBankingForm] = useState<"" | "import" | "line" | "deposit" | "unidentified" | "bank">("");
  const [viewingBankLineId, setViewingBankLineId] = useState<number | null>(null);
  const [viewingDepositId, setViewingDepositId] = useState<number | null>(null);
  const [viewingUnidentifiedId, setViewingUnidentifiedId] = useState<number | null>(null);
  const [bankingNote, setBankingNote] = useState("");
  const [creditPage, setCreditPage] = useState(1);
  const [viewStudent, setViewStudent] = useState<StudentOption | null>(null);
  const [editingFeeId, setEditingFeeId] = useState<number | null>(null);
  const [feeFilters, setFeeFilters] = useState<FeeFilters>(EMPTY_FEE_FILTERS);
  const [feePage, setFeePage] = useState(1);
  const [feePageSize, setFeePageSize] = useState(25);
  const [showFeeForm, setShowFeeForm] = useState(false);
  const [viewingFeeId, setViewingFeeId] = useState<number | null>(null);
  const [structureFilters, setStructureFilters] = useState<FeeStructureFilters>(EMPTY_FEE_STRUCTURE_FILTERS);
  const [structurePage, setStructurePage] = useState(1);
  const [structurePageSize, setStructurePageSize] = useState(25);
  const [showStructureForm, setShowStructureForm] = useState(false);
  const [viewingStructureId, setViewingStructureId] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [invoiceReceipts, setInvoiceReceipts] = useState<Record<number, string>>({});
  const [structureItems, setStructureItems] = useState<Array<{ fee: string; amount: string }>>([{ fee: "", amount: "" }]);
  const [editingAccountId, setEditingAccountId] = useState<number | null>(null);
  // What the screen asks before it acts, in place of the browser's own
  // confirm()/prompt() box (see components/ConfirmDialog).
  const [dialog, setDialog] = useState<ConfirmRequest | null>(null);
  const [accountName, setAccountName] = useState("");
  const [accountDescription, setAccountDescription] = useState("");
  const [accountParent, setAccountParent] = useState("");
  const [newAccountType, setNewAccountType] = useState<"asset" | "liability" | "equity" | "income" | "expense">("asset");
  const [newAccountStructure, setNewAccountStructure] = useState<"main" | "sub">("main");
  const [accountFormOpen, setAccountFormOpen] = useState(false);
  // ?view=roles opens straight on the roles, for pages that find one missing.
  const [ledgerView, setLedgerView] = useState<"chart" | "roles">(() =>
    new URLSearchParams(location.search).get("view") === "roles" ? "roles" : "chart",
  );
  // The account form and role send-backs offer the writing assistant; asking
  // on arrival means its buttons are there the moment either opens.
  useAIStatusQuery(activeTab === "accounts");
  const [providerImportWarnings, setProviderImportWarnings] = useState<string[]>([]);
  const [drillIncludesSubaccounts, setDrillIncludesSubaccounts] = useState(true);
  const [receiptStudentId, setReceiptStudentId] = useState<number | null>(null);
  const [receiptInvoiceId, setReceiptInvoiceId] = useState("");
  const [matchBillId, setMatchBillId] = useState<number | null>(null);
  const [billOrderId, setBillOrderId] = useState("");
  const [newAccountCategory, setNewAccountCategory] = useState("");
  const [editingTemplate, setEditingTemplate] = useState(false);
  const [statementsStart, setStatementsStart] = useState("");
  const [statementsEnd, setStatementsEnd] = useState("");
  const [statementsShown, setStatementsShown] = useState<"income_expenditure" | "financial_position" | "changes_in_equity">("income_expenditure");
  const [comparativeStart, setComparativeStart] = useState("");
  const [comparativeEnd, setComparativeEnd] = useState("");
  const [ledgerStart, setLedgerStart] = useState("");
  const [ledgerEnd, setLedgerEnd] = useState("");
  const [ledgerIncludesSubaccounts, setLedgerIncludesSubaccounts] = useState(true);
  const [journalLines, setJournalLines] = useState<GuidedJournalLineState[]>([
    emptyGuidedJournalLine(),
    emptyGuidedJournalLine(),
  ]);
  const [journalTemplate, setJournalTemplate] = useState<JournalTemplate>("general");
  const [journalDescription, setJournalDescription] = useState("");
  const [journalReference, setJournalReference] = useState("");
  // Which document each tab's form is currently correcting rather than
  // creating. Null means the form is raising a new one.
  const [editingJournalId, setEditingJournalId] = useState<number | null>(null);
  const [editingBillId, setEditingBillId] = useState<number | null>(null);
  const [editingBudgetId, setEditingBudgetId] = useState<number | null>(null);
  const [editingCreditNoteId, setEditingCreditNoteId] = useState<number | null>(null);
  const [editingGrantId, setEditingGrantId] = useState<number | null>(null);
  const [budgetLines, setBudgetLines] = useState<Array<{ account: string; amount: string; notes: string }>>([
    { account: "", amount: "", notes: "" },
  ]);
  const [generateDueDates, setGenerateDueDates] = useState<Record<number, string>>({});
  // Discounts picked for selected students before a structure's invoices are
  // generated. One structure's list is open at a time.
  const [discountsOpenFor, setDiscountsOpenFor] = useState<number | null>(null);
  const [generateDiscounts, setGenerateDiscounts] = useState<
    Record<number, Array<{ student: string; kind: "amount" | "percent"; value: string; reason: string }>>
  >({});
  const [bankMatches, setBankMatches] = useState<Record<number, string>>({});
  const [selectedProviderImport, setSelectedProviderImport] = useState<number | null>(null);
  const [providerInvoiceMatches, setProviderInvoiceMatches] = useState<Record<number, string>>({});
  // The browser's own date: the UTC one is yesterday in Kampala until 3 a.m.
  const today = todayIso();
  const monthStartIso = `${today.slice(0, 7)}-01`;
  const [reportStart, setReportStart] = useState(monthStartIso);
  const [reportEnd, setReportEnd] = useState(today);
  const [asOfDate, setAsOfDate] = useState(today);
  const [ledgerAccountId, setLedgerAccountId] = useState("");
  const [selectedBudgetId, setSelectedBudgetId] = useState<number | null>(null);
  const [bankImportResult, setBankImportResult] = useState<BankStatementImport | null>(null);
  const [receiptMethod, setReceiptMethod] = useState("cash");
  // The registers are read a page at a time and searched and filtered by the server:
  // a full roll issues thousands of invoices, receipts and journal entries a year, and
  // loading every one of them made each Financial page slower with every term.
  const deferredInvoiceSearch = useDeferredValue(invoiceRegisterFilters.search);
  const deferredPaymentSearch = useDeferredValue(paymentRegisterFilters.search);
  const deferredJournalSearch = useDeferredValue(journalSearch);
  const invoiceFilters = invoiceRegisterParams(invoiceRegisterFilters, deferredInvoiceSearch);
  const paymentFilters = paymentRegisterParams(paymentRegisterFilters, deferredPaymentSearch);
  const journalFilters = { search: deferredJournalSearch, status: journalStatusFilter };
  const fees = useQuery({
    queryKey: ["financial-fees"],
    queryFn: financialService.getFees,
  });
  const payments = useQuery({
    queryKey: ["ledger-payments", paymentPage, paymentRegisterPageSize, paymentFilters],
    queryFn: () => financialService.getPaymentPage({ ...paymentFilters, page: paymentPage, page_size: paymentRegisterPageSize }),
    keepPreviousData: true,
  });
  const unidentifiedReceipts = useQuery({
    queryKey: ["unidentified-receipts"],
    queryFn: financialService.getUnidentifiedReceipts,
  });
  const invoices = useQuery({
    queryKey: ["financial-invoices", invoicePage, invoiceRegisterPageSize, invoiceFilters],
    queryFn: () => financialService.getInvoicePage({ ...invoiceFilters, page: invoicePage, page_size: invoiceRegisterPageSize }),
    keepPreviousData: true,
  });
  // Pickers ask for exactly what they offer: invoices that can still take a receipt,
  // credit note or bank match, and receipts whose money is not yet against an invoice.
  const outstandingInvoices = useQuery({
    queryKey: ["financial-invoices", "outstanding"],
    queryFn: () => financialService.getInvoicePage({ outstanding: true, ordering: "due_date", page_size: 100 }),
  });
  const unallocatedReceipts = useQuery({
    queryKey: ["ledger-payments", "unallocated"],
    queryFn: () => financialService.getPaymentPage({ unallocated: true, ordering: "-payment_date", page_size: 100 }),
  });
  const studentOutstandingInvoices = useQuery({
    queryKey: ["financial-invoices", "student", receiptStudentId],
    queryFn: () => financialService.getInvoicePage({ student: receiptStudentId ?? 0, outstanding: true, page_size: 100 }),
    enabled: receiptStudentId !== null,
  });
  const feeStructures = useQuery({
    queryKey: ["financial-fee-structures"],
    queryFn: financialService.getFeeStructures,
  });
  const academicYears = useQuery({
    queryKey: ["financial-academic-years"],
    queryFn: academicSetupService.getYears,
  });
  const academicTerms = useQuery({
    queryKey: ["financial-academic-terms"],
    queryFn: academicSetupService.getTerms,
  });
  const discountStudents = useQuery({
    queryKey: ["fee-structure-students", discountsOpenFor],
    queryFn: () => financialService.getFeeStructureStudents(discountsOpenFor as number),
    enabled: discountsOpenFor !== null,
  });
  const classLevels = useQuery({
    queryKey: ["financial-class-levels"],
    queryFn: financialService.getClassLevels,
  });
  const refundRequests = useQuery({
    queryKey: ["refund-requests"],
    queryFn: financialService.getRefundRequests,
  });
  const creditNotes = useQuery({
    queryKey: ["credit-notes"],
    queryFn: financialService.getCreditNotes,
  });
  const stats = useQuery({
    queryKey: ["financial-stats"],
    queryFn: financialService.getStats,
  });
  const updateStudentFilters = (changes: Partial<FinanceStudentFilters>) => {
    setStudentFilters((current) => ({ ...current, ...changes }));
    setStudentDirectoryPage(1);
  };
  useEffect(() => {
    const term = studentDirectorySearch.trim();
    if (term === studentFilters.search) return;
    const timeout = window.setTimeout(() => {
      setStudentFilters((current) => ({ ...current, search: term }));
      setStudentDirectoryPage(1);
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [studentDirectorySearch, studentFilters.search]);
  const studentDirectoryQuery = useMemo(() => financeStudentParams(studentFilters), [studentFilters]);
  const activeStudentFilters = activeFinanceStudentFilterCount(studentFilters);
  const studentDirectory = useQuery({
    queryKey: ["student-directory", studentDirectoryQuery, studentDirectoryPage, studentDirectoryPageSize],
    queryFn: () => financialService.getStudentsPage({
      ...studentDirectoryQuery,
      page: studentDirectoryPage,
      page_size: studentDirectoryPageSize,
    }),
    enabled: activeTab === "students",
    keepPreviousData: true,
  });
  const studentDirectoryCount = studentDirectory.data?.count ?? 0;
  const studentDirectoryPageCount = Math.max(1, Math.ceil(studentDirectoryCount / studentDirectoryPageSize));
  const firstStudentRow = studentDirectoryCount ? (studentDirectoryPage - 1) * studentDirectoryPageSize + 1 : 0;
  const studentDirectoryFilters = useQuery({
    queryKey: ["student-directory-filter-options"],
    queryFn: () => financialService.getAllStudents(),
    // The class list for the student directory and the invoice register.
    enabled: activeTab === "students" || activeTab === "invoices",
  });
  // Service charges load only for those who may read them; anyone else would just be refused.
  const transportAllocations = useQuery({
    queryKey: ['finance-transport-allocations'],
    queryFn: transportService.getAllocations,
    enabled: hasCapability('access.transport.transport_allocation_read'),
  });
  const transportCharges = useQuery({
    queryKey: ['finance-transport-charges'],
    queryFn: transportService.getPayments,
    enabled: hasCapability('access.transport.transport_finance'),
  });
  const hostelAllocations = useQuery({
    queryKey: ['finance-hostel-allocations'],
    queryFn: hostelService.getAllocations,
    enabled: hasCapability('access.hostel.boarding_allocation_read'),
  });
  const hostelFees = useQuery({
    queryKey: ['finance-hostel-fees'],
    queryFn: hostelService.getFees,
    enabled: hasCapability('access.hostel.boarding_finance_read'),
  });
  const hostelCharges = useQuery({
    queryKey: ['finance-hostel-charges'],
    queryFn: hostelService.getPayments,
    enabled: hasCapability('access.hostel.boarding_finance_read'),
  });
  const cafeteriaOrders = useQuery({
    queryKey: ['finance-cafeteria-orders'],
    queryFn: cafeteriaService.getOrders,
    enabled: hasCapability('access.cafeteria.order_read'),
  });
  const cafeteriaSubscriptions = useQuery({
    queryKey: ['finance-cafeteria-subscriptions'],
    queryFn: cafeteriaService.getSubscriptions,
    enabled: hasCapability('access.cafeteria.order_read'),
  });
  const uniformOrders = useQuery({
    queryKey: ['finance-uniform-orders'],
    queryFn: uniformService.getOrders,
    enabled: hasCapability('access.uniforms.uniform_read'),
  });
  // Queues someone may be sitting on, waiting for work to arrive. These move
  // on their own; everything else refreshes when the tab regains focus or
  // after an action.
  const watchedQueue = { refetchInterval: 20_000, refetchIntervalInBackground: false };
  const accounts = useQuery({
    queryKey: ["financial-accounts"],
    queryFn: financialService.getAccounts,
    ...watchedQueue,
  });
  const journals = useQuery({
    queryKey: ["financial-journals", journalPage, journalPageSize, journalFilters],
    queryFn: () => financialService.getJournalPage({ ...journalFilters, page: journalPage, page_size: journalPageSize }),
    keepPreviousData: true,
    ...watchedQueue,
  });
  const draftJournals = useQuery({
    queryKey: ["financial-journals", "draft"],
    queryFn: () => financialService.getJournalPage({ status: "draft", page_size: 1 }),
    ...watchedQueue,
  });
  // Posted entries no bank line has been matched to yet.
  const matchableJournals = useQuery({
    queryKey: ["financial-journals", "unmatched"],
    queryFn: () => financialService.getJournalPage({ unmatched: true, ordering: "-entry_date", page_size: 100 }),
  });
  const accounting = useQuery({
    queryKey: ["accounting-report", reportStart, reportEnd],
    queryFn: () => financialService.getAccountingReport({
      start_date: reportStart, end_date: reportEnd,
    }),
  });
  const statementSections = useQuery({
    queryKey: ["statement-sections"],
    queryFn: financialService.getStatementSections,
    enabled: activeTab === "accounts" || activeTab === "financial-statements",
  });
  const financialStatements = useQuery({
    queryKey: ["financial-statements", statementsStart, statementsEnd, comparativeStart, comparativeEnd],
    queryFn: () => financialService.getFinancialStatements({
      start_date: statementsStart, end_date: statementsEnd,
      comparative_start_date: comparativeStart, comparative_end_date: comparativeEnd,
    }),
    enabled: activeTab === "financial-statements",
  });
  const drillAccount = useQuery({
    queryKey: ["trial-balance-account", trialBalanceAccountId, reportStart, reportEnd, drillIncludesSubaccounts],
    queryFn: () => financialService.getAccountLedger(Number(trialBalanceAccountId), {
      start_date: reportStart, end_date: reportEnd, include_subaccounts: drillIncludesSubaccounts,
    }),
    enabled: activeTab === "trial-balance" && !!trialBalanceAccountId,
  });
  const ledgerReport = useQuery({
    queryKey: ["account-ledger-report", ledgerAccountId, ledgerStart, ledgerEnd, ledgerIncludesSubaccounts],
    queryFn: () => financialService.getAccountLedger(Number(ledgerAccountId), {
      start_date: ledgerStart, end_date: ledgerEnd, include_subaccounts: ledgerIncludesSubaccounts,
    }),
    enabled: activeTab === "account-statement" && !!ledgerAccountId,
  });
  const suppliers = useQuery({
    queryKey: ["financial-suppliers"],
    queryFn: financialService.getSuppliers,
  });
  const purchaseOrders = useQuery({
    queryKey: ["purchase-orders"],
    queryFn: financialService.getBillablePurchaseOrders,
    enabled: activeTab === "payables",
  });
  const billMatch = useQuery({
    queryKey: ["vendor-bill-match", matchBillId],
    queryFn: () => financialService.getVendorBillMatch(matchBillId as number),
    enabled: matchBillId !== null,
  });
  const bills = useQuery({
    queryKey: ["vendor-bills"],
    queryFn: financialService.getVendorBills,
  });
  const banks = useQuery({
    queryKey: ["bank-accounts"],
    queryFn: financialService.getBankAccounts,
  });
  const bankLines = useQuery({
    queryKey: ["bank-lines"],
    queryFn: financialService.getBankLines,
  });
  const bankDeposits = useQuery({
    queryKey: ["bank-deposits"],
    queryFn: financialService.getBankDeposits,
    enabled: activeTab === "banking",
  });
  const bankImports = useQuery({
    queryKey: ["bank-imports"],
    queryFn: financialService.getBankStatementImports,
  });
  const providerImports = useQuery({
    queryKey: ["provider-payment-imports"],
    queryFn: financialService.getProviderPaymentImports,
  });
  const providerRows = useQuery({
    queryKey: ["provider-payment-rows", selectedProviderImport],
    queryFn: () => financialService.getProviderPaymentRows(selectedProviderImport as number),
    enabled: selectedProviderImport !== null,
  });
  const bankSuggestions = useQuery({
    queryKey: ["bank-suggestions"],
    queryFn: financialService.getBankSuggestions,
  });
  const budgets = useQuery({
    queryKey: ["budgets"],
    queryFn: financialService.getBudgets,
  });
  const budgetVariance = useQuery({
    queryKey: ["budget-variance", selectedBudgetId],
    queryFn: () => financialService.getBudgetVariance(selectedBudgetId!),
    enabled: !!selectedBudgetId,
  });
  const grants = useQuery({
    queryKey: ["grants"],
    queryFn: financialService.getGrants,
  });
  const [expandedGrant, setExpandedGrant] = useState<number | null>(null);
  const grantTransactions = useQuery({
    queryKey: ["grant-transactions", expandedGrant],
    queryFn: () => financialService.getGrantTransactions(expandedGrant as number),
    enabled: expandedGrant !== null,
  });
  const periods = useQuery({
    queryKey: ['accounting-periods'],
    queryFn: financialService.getAccountingPeriods,
  });
  const payrollPeriods = useQuery({
    queryKey: ["payroll-periods"],
    queryFn: financialService.getPayrollPeriods,
  });
  const statutoryBalances = useQuery({
    queryKey: ["statutory-balances"],
    queryFn: financialService.getStatutoryBalances,
  });
  // Payments raised against payroll but not yet signed off. Fetched for the
  // whole screen rather than per run, so one request answers every run.
  const payrollSettlements = useQuery({
    queryKey: ["payroll-settlements"],
    queryFn: () => financialService.getPayrollSettlements(),
  });
  // Payroll: the open run, its reviewer comment, and the generate form.
  const [viewingPayrollId, setViewingPayrollId] = useState<number | null>(null);
  const [payrollDecisionNotes, setPayrollDecisionNotes] = useState("");
  const [showPayrollForm, setShowPayrollForm] = useState(false);
  // Budgets and grants: the forms, and the reviewer comment on the open one.
  const [showBudgetForm, setShowBudgetForm] = useState(false);
  const [showGrantForm, setShowGrantForm] = useState(false);
  const [planningNotes, setPlanningNotes] = useState("");
  const [editingDeduction, setEditingDeduction] = useState<Payslip | null>(null);
  const refresh = () =>
    Promise.all(
      [
        "financial-fees",
        "ledger-payments",
        "financial-invoices",
        "financial-fee-structures",
        "refund-requests",
        "credit-notes",
        "financial-stats",
        "financial-accounts",
        "financial-journals",
        "accounting-report",
        "receivables-ageing",
        "receivables-control-reconciliation",
        "payables-ageing",
        "payables-control-reconciliation",
        "financial-suppliers",
        "purchase-orders",
        "vendor-bill-match",
        "vendor-bills",
        "bank-accounts",
        "bank-lines",
        "bank-imports",
        "bank-deposits",
        "bank-reconciliation-report",
        "bank-suggestions",
        "provider-payment-imports",
        "provider-payment-rows",
        "unidentified-receipts",
        "budgets",
        "budget-variance",
        "grants",
        "grant-transactions",
        "grant-summary-report",
        "grant-statement-report",
        "accounting-periods",
        "payroll-periods",
        "statutory-balances",
        // Settling a deduction on its own changes what a whole-run settlement
        // would still cover, so the preview has to be refetched with them --
        // otherwise the form keeps offering to pay what is already paid.
        "payroll-settlement-preview",
        "payroll-settlements",
        "finance-transport-allocations",
        "finance-transport-charges",
        "finance-hostel-allocations",
        "finance-hostel-fees",
        "finance-hostel-charges",
        "finance-cafeteria-orders",
        "finance-cafeteria-subscriptions",
        "finance-uniform-orders",
        "account-roles",
        "statement-sections",
        "financial-statements",
        "account-ledger-report",
        "trial-balance-account",
        // Refreshing is best-effort: a query this user is not allowed to read
        // (purchase orders, for a bursar) refetches into an error, and letting
        // that reject would take the caller's own onSuccess down with it --
        // which is how an edit could save yet leave its form still open.
      ].map((key) => queryClient.invalidateQueries({ queryKey: [key] }).catch(() => undefined)),
    );
  const useSaveMutation = <T,>(fn: (data: T) => Promise<unknown>) =>
    useMutation({
      mutationFn: fn,
      onSuccess: () => {
        setError('');
        // Started, not awaited: returning it would hold up the caller's own
        // onSuccess -- closing an edit form, say -- behind a refresh that
        // stalls on a query this user is not allowed to read.
        void refresh();
      },
      onError: (failure) => setError(financialError(failure)),
    });
  const createFee = useSaveMutation(financialService.createFee);
  const updateFee = useSaveMutation(financialService.updateFee);
  const deleteFee = useSaveMutation(financialService.deleteFee);
  const recordPayment = useSaveMutation(financialService.recordPayment);
  const createUnidentifiedReceipt = useSaveMutation(financialService.createUnidentifiedReceipt);
  const resolveUnidentifiedReceipt = useSaveMutation(financialService.resolveUnidentifiedReceipt);
  const refundUnidentifiedReceipt = useSaveMutation(financialService.refundUnidentifiedReceipt);
  const requestRefund = useSaveMutation(financialService.requestRefund);
  const approveRefund = useSaveMutation(financialService.approveRefundRequest);
  const rejectRefund = useSaveMutation(financialService.rejectRefundRequest);
  const cancelRefund = useSaveMutation(financialService.cancelRefundRequest);
  const createInvoice = useSaveMutation(financialService.createInvoice);
  const createFeeStructure = useSaveMutation(financialService.createFeeStructure);
  const deleteFeeStructure = useSaveMutation(financialService.deleteFeeStructure);
  const [generationResult, setGenerationResult] = useState("");
  const generateInvoices = useMutation({
    mutationFn: financialService.generateInvoicesFromFeeStructure,
    onSuccess: (result, variables) => {
      setError("");
      setGenerationResult(
        `Generated ${result.created_count} invoice(s) for ${result.total_students} student(s)` +
          (result.skipped_count ? ` (${result.skipped_count} already invoiced)` : "") +
          (result.no_fees_count ? ` (${result.no_fees_count} with no fees that apply to them)` : "") +
          (result.discounted_count
            ? `, with discounts for ${result.discounted_count} student(s) totalling UGX ${Number(result.discount_total).toLocaleString()}.`
            : "."),
      );
      setGenerateDiscounts((current) => ({ ...current, [variables.id]: [] }));
      setDiscountsOpenFor((current) => (current === variables.id ? null : current));
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: ["financial-invoices"] }),
        queryClient.invalidateQueries({ queryKey: ["financial-fee-structures"] }),
        queryClient.invalidateQueries({ queryKey: ["fee-structure-students"] }),
      ]);
    },
    onError: (failure) => setError(financialError(failure)),
  });
  const discountAmount = (total: string, row: { kind: "amount" | "percent"; value: string }) => {
    const value = Number(row.value);
    if (!Number.isFinite(value) || value <= 0) return 0;
    return row.kind === "percent" ? Math.round((Number(total) * value) / 100) : value;
  };
  const discountRowReady = (
    total: string,
    row: { student: string; kind: "amount" | "percent"; value: string; reason: string },
  ) => {
    const amount = discountAmount(total, row);
    return (
      Boolean(row.student) &&
      amount > 0 &&
      amount <= Number(total) &&
      (row.kind !== "percent" || Number(row.value) <= 100) &&
      row.reason.trim().length >= 3
    );
  };
  const applyPayment = useSaveMutation(financialService.applyInvoicePayment);
  const reallocatePayment = useSaveMutation(financialService.reallocatePaymentAllocation);
  const cancelInvoice = useSaveMutation(financialService.cancelInvoice);
  const giveInvoiceDiscount = useSaveMutation(financialService.giveInvoiceDiscount);
  // The invoice in the register a discount is being given on, if any.
  const [invoiceDiscount, setInvoiceDiscount] = useState<
    { id: number; kind: "amount" | "percent"; value: string; reason: string } | null
  >(null);
  const createCreditNote = useSaveMutation(financialService.createCreditNote);
  const updateCreditNote = useSaveMutation(financialService.updateCreditNote);
  const submitCreditNoteForApproval = useSaveMutation(financialService.submitCreditNote);
  const returnCreditNote = useSaveMutation(financialService.returnCreditNote);
  const approveCreditNote = useSaveMutation(financialService.approveCreditNote);
  const rejectCreditNote = useSaveMutation(financialService.rejectCreditNote);
  const createAccount = useSaveMutation(financialService.createAccount);
  const updateAccount = useSaveMutation(financialService.updateAccount);
  const deleteAccount = useSaveMutation(financialService.deleteAccount);
  const submitAccountRequest = useSaveMutation(financialService.submitAccount);
  const accountRoles = useQuery({
    queryKey: ["account-roles"],
    queryFn: financialService.getAccountRoles,
    enabled: activeTab === "accounts",
    ...watchedQueue,
  });
  const proposeRole = useSaveMutation(financialService.proposeAccountRole),
    submitRole = useSaveMutation(financialService.submitAccountRole),
    submitAllRoles = useSaveMutation(financialService.submitAllAccountRoles),
    addSection = useSaveMutation(financialService.createStatementSection),
    renameSection = useSaveMutation(financialService.updateStatementSection),
    removeSection = useSaveMutation(financialService.deleteStatementSection),
    addCategory = useSaveMutation(financialService.createStatementCategory),
    renameCategory = useSaveMutation(financialService.updateStatementCategory),
    removeCategory = useSaveMutation(financialService.deleteStatementCategory),
    reorderTemplate = useSaveMutation(financialService.reorderStatementTemplate),
    fileAccountInStatement = useSaveMutation(financialService.assignAccountStatementCategory),
    verifyAllRoles = useSaveMutation(financialService.verifyAllAccountRoles),
    approveAllRoles = useSaveMutation(financialService.approveAllAccountRoles),
    verifyRole = useSaveMutation(financialService.verifyAccountRole),
    // Review is a required step before approval, so every document that can be
    // approved needs somewhere to record that it was reviewed. Without these
    // the reviewer could only return a document, never pass it on.
    verifyBill = useSaveMutation(financialService.verifyVendorBill),
    verifyBudgetRequest = useSaveMutation(financialService.verifyBudget),
    verifyCreditNoteRequest = useSaveMutation(financialService.verifyCreditNote),
    verifyGrantRequest = useSaveMutation(financialService.verifyGrant),
    verifyPayroll = useSaveMutation(financialService.verifyPayrollPeriod),
    returnRole = useSaveMutation(financialService.returnAccountRole),
    approveRole = useSaveMutation(financialService.approveAccountRole);
  const createJournal = useSaveMutation(financialService.createJournalEntry);
  const updateJournal = useSaveMutation(financialService.updateJournalEntry);
  const submitJournalForApproval = useSaveMutation(financialService.submitJournalEntry);
  const returnJournal = useSaveMutation(financialService.returnJournalEntry);
  const rejectJournal = useSaveMutation(financialService.rejectJournalEntry);
  const postJournal = useSaveMutation(financialService.postJournalEntry);
  const deleteJournal = useSaveMutation(financialService.deleteJournalEntry);
  const reverseJournal = useSaveMutation(financialService.reverseJournalEntry);
  const updateBill = useSaveMutation(financialService.updateVendorBill);
  const createBill = useSaveMutation(financialService.createVendorBill),
    submitBillForApproval = useSaveMutation(financialService.submitVendorBill),
    returnBill = useSaveMutation(financialService.returnVendorBill),
    approveBill = useSaveMutation(financialService.approveVendorBill),
    payBill = useSaveMutation(financialService.payVendorBill),
    createSupplier = useSaveMutation(financialService.createSupplier);
  const createBank = useSaveMutation(financialService.createBankAccount),
    createBankLine = useSaveMutation(financialService.createBankLine),
    reconcileLine = useSaveMutation(financialService.reconcileBankLine),
    unmatchLine = useSaveMutation(financialService.unmatchBankLine),
    recordBankDeposit = useSaveMutation(financialService.recordBankDeposit),
    cancelBankDeposit = useSaveMutation(financialService.cancelBankDeposit),
    recordBankCharge = useSaveMutation(financialService.recordBankCharge),
    prepareBankSignOff = useSaveMutation(financialService.prepareBankSignOff),
    withdrawBankSignOff = useSaveMutation(financialService.withdrawBankSignOff),
    importBankStatement = useSaveMutation(financialService.importBankStatement),
    autoMatchBankLines = useSaveMutation(financialService.autoMatchBankLines),
    importProviderPayments = useSaveMutation(financialService.importProviderPayments),
    postProviderPaymentImport = useSaveMutation(financialService.postProviderPaymentImport),
    resolveProviderPaymentRow = useSaveMutation(financialService.resolveProviderPaymentRow);
  const updateBudget = useSaveMutation(financialService.updateBudget);
  const createBudget = useSaveMutation(financialService.createBudget),
    submitBudgetForApproval = useSaveMutation(financialService.submitBudget),
    returnBudget = useSaveMutation(financialService.returnBudget),
    approveBudget = useSaveMutation(financialService.approveBudget);
  const updateGrant = useSaveMutation(financialService.updateGrant);
  const createGrant = useSaveMutation(financialService.createGrant),
    submitGrantForApproval = useSaveMutation(financialService.submitGrant),
    returnGrant = useSaveMutation(financialService.returnGrant),
    rejectGrant = useSaveMutation(financialService.rejectGrant),
    approveGrant = useSaveMutation(financialService.approveGrant),
    recordGrantReceipt = useSaveMutation(financialService.recordGrantReceipt),
    recordGrantExpense = useSaveMutation(financialService.recordGrantExpense);
  const createPeriod = useSaveMutation(financialService.createAccountingPeriod),
    closePeriod = useSaveMutation(financialService.closeAccountingPeriod),
    requestPeriodReopen = useSaveMutation(financialService.requestAccountingPeriodReopen),
    verifyPeriodReopen = useSaveMutation(financialService.verifyAccountingPeriodReopen),
    returnPeriodReopen = useSaveMutation(financialService.returnAccountingPeriodReopen),
    rejectPeriodReopen = useSaveMutation(financialService.rejectAccountingPeriodReopen),
    approvePeriodReopen = useSaveMutation(financialService.approveAccountingPeriodReopen);
  const generatePayroll = useSaveMutation(financialService.generatePayrollPeriod),
    submitPayrollForApproval = useSaveMutation(financialService.submitPayrollPeriod),
    returnPayroll = useSaveMutation(financialService.returnPayrollPeriod),
    approvePayroll = useSaveMutation(financialService.approvePayrollPeriod),
    settlePayroll = useSaveMutation(financialService.settlePayrollPeriod),
    payPayrollComponent = useSaveMutation(financialService.payPayrollComponent),
    verifySettlement = useSaveMutation(financialService.verifyPayrollSettlement),
    returnSettlement = useSaveMutation(financialService.returnPayrollSettlement),
    approveSettlement = useSaveMutation(financialService.approvePayrollSettlement),
    cancelSettlement = useSaveMutation(financialService.cancelPayrollSettlement),
    editDeductions = useSaveMutation(financialService.setPayslipDeductions),
    cancelPayroll = useSaveMutation(financialService.cancelPayrollPeriod);
  const createTransportCharge = useSaveMutation(transportService.createPaymentCharge),
    processTransportPayment = useSaveMutation(transportService.processPayment),
    cancelTransportCharge = useSaveMutation(transportService.cancelPaymentCharge);
  const createHostelCharge = useSaveMutation(hostelService.createPaymentCharge),
    processHostelPayment = useSaveMutation(hostelService.processPayment),
    cancelHostelCharge = useSaveMutation(hostelService.cancelPaymentCharge);
  const processCafeteriaPayment = useSaveMutation(cafeteriaService.processOrderPayment),
    refundCafeteriaPayment = useSaveMutation(cafeteriaService.refundOrderPayment);
  const processCafeteriaSubscriptionPayment = useSaveMutation(cafeteriaService.processSubscriptionPayment),
    refundCafeteriaSubscriptionPayment = useSaveMutation(cafeteriaService.refundSubscriptionPayment);
  const processUniformPayment = useSaveMutation(uniformService.createPayment),
    refundUniformPayment = useSaveMutation(uniformService.refundPayment);
  const data = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    return new FormData(event.currentTarget);
  };
  const submitFee = (event: FormEvent<HTMLFormElement>) => {
    const d = data(event);
    createFee.mutate({
      name: String(d.get("name")),
      fee_type: String(d.get("type")),
      amount: String(d.get("amount")),
      description: String(d.get("description")),
      income_account: d.get("income_account") ? Number(d.get("income_account")) : null,
      is_active: true,
      allows_partial_payment: d.get("allows_partial_payment") === "on",
      applies_to: String(d.get("applies_to") || "all") as "all" | "day" | "boarding",
      transport_only: d.get("transport_only") === "on",
    }, { onSuccess: () => setShowFeeForm(false) });
    event.currentTarget.reset();
  };
  const submitFeeEdit = (event: FormEvent<HTMLFormElement>, id: number) => {
    const d = data(event);
    updateFee.mutate(
      {
        id,
        name: String(d.get("name")),
        fee_type: String(d.get("type")),
        amount: String(d.get("amount")),
        description: String(d.get("description")),
        income_account: d.get("income_account") ? Number(d.get("income_account")) : null,
        is_active: d.get("is_active") === "on",
        allows_partial_payment: d.get("allows_partial_payment") === "on",
        applies_to: String(d.get("applies_to") || "all") as "all" | "day" | "boarding",
        transport_only: d.get("transport_only") === "on",
      },
      { onSuccess: () => setEditingFeeId(null) },
    );
  };
  // Only this student's unpaid invoices: picking from every invoice in the
  // school made the common case a hunt, and the server rejects a mismatch
  // anyway.
  const receiptStudentInvoices = useMemo(
    () => (receiptStudentId ? studentOutstandingInvoices.data?.results ?? [] : []),
    [studentOutstandingInvoices.data, receiptStudentId],
  );
  useEffect(() => {
    // One unpaid invoice is almost always the one being paid, so it is chosen
    // for them; anything else is left for the bursar to decide.
    setReceiptInvoiceId(
      receiptStudentInvoices.length === 1 ? String(receiptStudentInvoices[0].id) : "",
    );
  }, [receiptStudentInvoices]);
  // A P5 boarder and a P7 day student are billed different fees for different
  // amounts, so the whole catalogue is the wrong list to choose from. What a
  // student is actually being charged is written on their invoices, so the
  // charges on the invoice being settled are the choices offered. A student
  // with nothing outstanding is paying ahead, and then the catalogue is all
  // there is to go on.
  const receiptFeeChoices = useMemo(() => {
    const selectedInvoices = receiptInvoiceId
      ? receiptStudentInvoices.filter((invoice) => String(invoice.id) === receiptInvoiceId)
      : receiptStudentInvoices;
    const charged = new Map<number, { id: number; name: string; amount: string }>();
    for (const invoice of selectedInvoices) {
      for (const line of invoice.lines ?? []) {
        if (line.fee && !charged.has(line.fee)) {
          charged.set(line.fee, {
            id: line.fee,
            name: line.fee_name || line.description,
            amount: line.line_total,
          });
        }
      }
    }
    return [...charged.values()];
  }, [receiptStudentInvoices, receiptInvoiceId]);
  const receiptFeeFallback = useMemo(
    () => (fees.data ?? []).filter((fee) => fee.is_active),
    [fees.data],
  );
  // Money received lands in cash, a bank, or a collection clearing account --
  // never in a receivable or a fixed asset, though all four are assets. The
  // server says which accounts hold money (can_pay_from): those marked cash or
  // bank, and every registered bank account. It refuses any other.
  const moneyLandsHere = useCallback(
    (account: Account) =>
      canReceivePostings(account) && account.account_type === "asset" && account.can_pay_from === true,
    [],
  );
  // A supplier bill is a cost, or the purchase of something being capitalised.
  // It is never charged to the cash it will be paid from, and never to the
  // receivable or payable controls, whose balances belong to the ledgers
  // behind them.
  const controlAccountIds = useMemo(
    () => new Set((accounts.data ?? []).filter((a) => a.is_control_account).map((a) => a.id)),
    [accounts.data],
  );
  const chargeableForABill = useCallback(
    (account: Account) => {
      if (!canReceivePostings(account)) return false;
      if (account.account_type === "expense") return true;
      if (account.account_type !== "asset") return false;
      // Not the cash it will be paid from, not a control account, not one of
      // the per-student or per-supplier accounts beneath one, and not a contra
      // account whose balance runs the other way.
      return (
        !account.is_cash_equivalent &&
        !account.is_control_account &&
        !account.is_contra_account &&
        !(account.parent !== undefined && account.parent !== null && controlAccountIds.has(account.parent))
      );
    },
    [controlAccountIds],
  );
  const receiptStudentOwes = useMemo(
    () =>
      receiptStudentInvoices.reduce(
        (total, invoice) => total + Number(invoice.balance_due),
        0,
      ),
    [receiptStudentInvoices],
  );

  const submitPayment = (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    const d = data(event);
    recordPayment.mutate(
      {
        student: Number(d.get("student")),
        fee: Number(d.get("fee")),
        amount: String(d.get("amount")),
        payment_method: String(d.get("method")),
        reference_number: String(d.get("reference")),
        invoice: d.get("invoice") ? Number(d.get("invoice")) : null,
        receipt_account: d.get("receipt_account") ? Number(d.get("receipt_account")) : null,
        received_on: receiptMethod === "cash" ? null : String(d.get("received_on") || "") || null,
      },
      {
        // Cleared only once the receipt is saved: a refused receipt keeps what
        // was typed, so it can be corrected rather than entered again. The
        // picker state lives outside the form, so it is cleared here too.
        onSuccess: () => {
          form.reset();
          setReceiptStudentId(null);
          setReceiptInvoiceId("");
          setReceiptMethod("cash");
        },
      },
    );
  };
  const submitUnidentifiedReceipt = (event: FormEvent<HTMLFormElement>) => {
    const d = data(event);
    const form = event.currentTarget;
    createUnidentifiedReceipt.mutate({
      reference: String(d.get("reference")),
      payer_name: String(d.get("payer_name")),
      amount: String(d.get("amount")),
      payment_method: String(d.get("payment_method")),
      receipt_account: Number(d.get("receipt_account")),
      received_at: String(d.get("received_at")),
      notes: String(d.get("notes")),
    }, { onSuccess: () => { form.reset(); setBankingForm(""); } });
  };
  const submitUnidentifiedResolution = (event: FormEvent<HTMLFormElement>, id: number) => {
    const d = data(event);
    const invoice = String(d.get("invoice") || "");
    const fee = String(d.get("fee") || "");
    resolveUnidentifiedReceipt.mutate({
      id,
      student: Number(d.get("student")),
      invoice: invoice ? Number(invoice) : undefined,
      fee: fee ? Number(fee) : undefined,
      notes: String(d.get("notes")),
    });
  };
  const submitInvoice = (event: FormEvent<HTMLFormElement>) => {
    const d = data(event);
    const form = event.currentTarget;
    createInvoice.mutate(
      {
        student: Number(d.get("student")),
        due_date: String(d.get("due")),
        total_amount: String(d.get("amount")),
        notes: String(d.get("notes")),
      },
      {
        // Only once it is saved: a refused invoice keeps what was typed.
        onSuccess: () => {
          form.reset();
          setInvoiceFormKey((key) => key + 1);
          setShowInvoiceForm(false);
        },
      },
    );
  };
  const submitFeeStructure = (event: FormEvent<HTMLFormElement>) => {
    const d = data(event);
    const items = structureItems
      .filter((item) => item.fee && item.amount)
      .map((item) => ({ fee: Number(item.fee), amount: item.amount }));
    if (!items.length) {
      setError("Add at least one fee item to the structure.");
      return;
    }
    setGenerationResult("");
    const form = event.currentTarget;
    createFeeStructure.mutate(
      {
        name: String(d.get("name")),
        term: Number(d.get("term")),
        level: String(d.get("level")),
        notes: String(d.get("notes")),
        receivable_account: d.get("receivable_account") ? Number(d.get("receivable_account")) : null,
        items,
      },
      {
        onSuccess: () => {
          form.reset();
          setStructureItems([{ fee: "", amount: "" }]);
          setShowStructureForm(false);
        },
      },
    );
  };
  const submitCreditNote = (event: FormEvent<HTMLFormElement>) => {
    const d = data(event);
    if (!String(d.get("reason") ?? "").trim()) {
      setError("Write the reason for the credit note.");
      return;
    }
    const payload = {
      invoice: Number(d.get("invoice")),
      amount: String(d.get("amount")),
      reason: String(d.get("reason")),
    };
    if (editingCreditNoteId !== null) {
      updateCreditNote.mutate(
        { id: editingCreditNoteId, ...payload },
        {
          onSuccess: () => {
            setEditingCreditNoteId(null);
            setShowCreditForm(false);
          },
        },
      );
      return;
    }
    const form = event.currentTarget;
    createCreditNote.mutate(payload, {
      onSuccess: () => {
        form.reset();
        setShowCreditForm(false);
      },
    });
  };
  // Preview only -- the server assigns the real code on save, so a code taken
  // by someone else in the meantime shifts this account to the next one.
  const nextAccountCode = useMemo(() => {
    const highest = (accounts.data ?? []).reduce((max, account) => {
      const match = /^A(\d+)$/.exec(account.code);
      return match ? Math.max(max, Number(match[1])) : max;
    }, 0);
    return `A${String(highest + 1).padStart(3, "0")}`;
  }, [accounts.data]);
  const resetAccountForm = () => {
    setAccountFormOpen(false);
    setEditingAccountId(null);
    setAccountName("");
    setAccountDescription("");
    setAccountParent("");
    setNewAccountType("asset");
    setNewAccountStructure("main");
    setNewAccountCategory("");
  };
  const editAccountRequest = (account: Account) => {
    setEditingAccountId(account.id);
    setAccountName(account.name);
    setAccountDescription(account.description ?? "");
    setAccountParent(account.parent ? String(account.parent) : "");
    setNewAccountType(account.account_type);
    setNewAccountStructure(account.parent ? "sub" : "main");
    setNewAccountCategory(account.statement_category ? String(account.statement_category) : "");
  };
  // A blank form, already on the type asked for -- "Create one" beside a role
  // that has no account of its type opens straight onto that type.
  const openNewAccount = (type?: Account["account_type"]) => {
    resetAccountForm();
    if (type) setNewAccountType(type);
    setAccountFormOpen(true);
  };
  const submitAccount = (event: FormEvent<HTMLFormElement>) => {
    const d = data(event);
    // Every account created here is a plain posting account: the form asks
    // only where it sits. Summary-only and control accounts still exist in
    // the ledger, but they are no longer something this form can produce.
    const payload: Partial<Account> = {
      name: accountName,
      account_type: String(d.get("account_type")) as
        "asset" | "liability" | "equity" | "income" | "expense",
      description: accountDescription,
      structure: newAccountStructure,
      parent: newAccountStructure === "sub" && accountParent ? Number(accountParent) : null,
      // Only a main account is placed on the statements; a subaccount reports
      // through the account it sits under.
      statement_category:
        canConfigureAccounting && newAccountStructure === "main" && newAccountCategory ? Number(newAccountCategory) : undefined,
      is_active: true,
    };
    if (editingAccountId !== null) {
      updateAccount.mutate({ id: editingAccountId, ...payload }, { onSuccess: resetAccountForm });
    } else {
      createAccount.mutate(payload, { onSuccess: resetAccountForm });
    }
  };
  const submitJournal = (event: FormEvent<HTMLFormElement>) => {
    const d = data(event);
    if (journalLines.length < 2 || journalLines.some((line) => !line.account || !line.amount)) {
      setError("Add at least two complete affected-account lines.");
      return;
    }
    const payload = {
      entry_date: String(d.get("entry_date")),
      description: journalDescription,
      reference: journalReference,
      transaction_type: journalTemplate,
      lines: journalLines.map((line) => ({
        account: Number(line.account),
        effect: line.effect,
        amount: line.amount,
        description: line.description,
      })),
    };
    if (editingJournalId !== null) {
      updateJournal.mutate({ id: editingJournalId, ...payload }, { onSuccess: resetJournalForm });
    } else {
      createJournal.mutate(payload, { onSuccess: resetJournalForm });
    }
  };

  const selectJournalTemplate = (template: JournalTemplate) => {
    setJournalTemplate(template);
    if (template === "internal_transfer") {
      setJournalDescription("Transfer between school cash/bank accounts");
      setJournalLines([
        { ...emptyGuidedJournalLine(), effect: "increase", description: "Money received into destination account" },
        { ...emptyGuidedJournalLine(), effect: "decrease", description: "Money withdrawn from source account" },
      ]);
    } else if (template === "immediate_expense") {
      setJournalDescription("Expense paid immediately");
      setJournalLines([
        { ...emptyGuidedJournalLine(), effect: "increase", description: "Expense incurred" },
        { ...emptyGuidedJournalLine(), effect: "decrease", description: "Paid from cash/bank" },
      ]);
    } else {
      setJournalDescription(template === "correction" ? "Accounting correction or adjustment" : "");
      setJournalLines([emptyGuidedJournalLine(), emptyGuidedJournalLine()]);
    }
  };
  const submitSupplier = (event: FormEvent<HTMLFormElement>) => {
    const d = data(event);
    const form = event.currentTarget;
    createSupplier.mutate(
      {
        name: String(d.get("name")),
        contact_person: String(d.get("contact_person") || ""),
        phone: String(d.get("phone") || ""),
        email: String(d.get("email") || ""),
        payment_terms: String(d.get("terms") || ""),
      },
      { onSuccess: () => { form.reset(); setShowSupplierForm(false); } },
    );
  };
  const editBill = (bill: VendorBill) => {
    setEditingBillId(bill.id);
    setBillOrderId(bill.purchase_order ? String(bill.purchase_order) : "");
    scrollPageToTop();
  };
  const resetBillForm = () => {
    setEditingBillId(null);
    setBillOrderId("");
    setShowBillForm(false);
  };
  const submitBill = (event: FormEvent<HTMLFormElement>) => {
    const d = data(event);
    const orderId = d.get("purchase_order") ? Number(d.get("purchase_order")) : null;
    const payload = {
      supplier: Number(d.get("supplier")),
      purchase_order: orderId,
      bill_number: String(d.get("number")),
      bill_date: String(d.get("date")),
      due_date: String(d.get("due")),
      description: String(d.get("description")),
      total_amount: String(d.get("amount")),
      expense_account: Number(d.get("account")),
      payable_account: d.get("payable_account") ? Number(d.get("payable_account")) : null,
      // What the supplier is charging for, line by line. Only worth sending
      // when there is an order to check it against.
      lines: orderId
        ? (d.getAll("line_item") as string[])
            .map((item, index) => ({
              item: Number(item),
              description: String((d.getAll("line_description") as string[])[index] || ""),
              quantity: String((d.getAll("line_quantity") as string[])[index] || "0"),
              unit_amount: String((d.getAll("line_unit_amount") as string[])[index] || "0"),
            }))
            .filter((line) => line.item && Number(line.quantity) > 0)
        : undefined,
    };
    if (editingBillId !== null) {
      updateBill.mutate(
        // An edit that drops the order has to say so with an empty list --
        // leaving `lines` out would keep the old ones against a total they
        // no longer add up to.
        { id: editingBillId, ...payload, lines: payload.lines ?? [] },
        { onSuccess: resetBillForm },
      );
      return;
    }
    if (!payload.description.trim()) {
      setError("Say what the bill is for.");
      return;
    }
    createBill.mutate(payload, { onSuccess: resetBillForm });
  };
  const submitBillPayment = (event: FormEvent<HTMLFormElement>, bill: VendorBill) => {
    const d = data(event);
    payBill.mutate(
      {
        id: bill.id,
        amount: String(d.get("amount")),
        payment_account: Number(d.get("payment_account")),
        reference: String(d.get("reference")),
        payment_date: String(d.get("payment_date")),
      },
      { onSuccess: () => setViewingBillId(null) },
    );
  };
  const submitBank = (event: FormEvent<HTMLFormElement>) => {
    const d = data(event);
    const form = event.currentTarget;
    createBank.mutate({
      name: String(d.get("name")),
      bank_name: String(d.get("bank")),
      account_number: String(d.get("number")),
      ledger_account: Number(d.get("account")),
      opening_balance: String(d.get("opening") || "0"),
      opening_balance_date: String(d.get("opening_date") || "") || null,
      is_active: true,
    }, { onSuccess: () => { form.reset(); setBankingForm(""); } });
  };
  const submitBankLine = (event: FormEvent<HTMLFormElement>) => {
    const d = data(event);
    const form = event.currentTarget;
    createBankLine.mutate({
      bank_account: Number(d.get("bank")),
      transaction_date: String(d.get("date")),
      description: String(d.get("description")),
      reference: String(d.get("reference")),
      amount: String(d.get("amount")),
    }, { onSuccess: () => { form.reset(); setBankingForm(""); } });
  };
  const submitBankImport = (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    const d = data(event);
    const file = d.get("file");
    if (!(file instanceof File) || !file.name) {
      setError("Choose a CSV statement file.");
      return;
    }
    setBankImportResult(null);
    importBankStatement.mutate({
      bank_account: Number(d.get("bank")),
      file,
    }, {
      onSuccess: (result) => {
        form.reset();
        setBankImportResult(result as BankStatementImport);
      },
    });
  };
  const submitProviderImport = (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    const d = data(event);
    const file = d.get("file");
    if (!(file instanceof File) || !file.name) {
      setError("Choose a provider CSV payment report.");
      return;
    }
    importProviderPayments.mutate({
      provider: String(d.get("provider")),
      receipt_account: Number(d.get("receipt_account")),
      settlement_total: String(d.get("settlement_total") || ""),
      period_start: String(d.get("period_start") || ""),
      period_end: String(d.get("period_end") || ""),
      column_mapping: Object.fromEntries(
        ["transaction_id", "transaction_date", "student_identifier", "invoice_reference", "payer_name", "amount"]
          .map((field) => [field, String(d.get(`column_${field}`) || "").trim()])
          .filter(([, value]) => value),
      ),
      file,
    }, {
      onSuccess: (result) => {
        form.reset();
        // A gap does not stop the import -- it names the dates still needing a
        // report run, which would otherwise go unnoticed.
        setProviderImportWarnings((result as { period_warnings?: string[] })?.period_warnings ?? []);
      },
    });
  };
  const resetBudgetForm = () => {
    setEditingBudgetId(null);
    setBudgetLines([{ account: "", amount: "", notes: "" }]);
    setShowBudgetForm(false);
  };
  const editBudget = (budget: Budget) => {
    setEditingBudgetId(budget.id);
    setBudgetLines(budget.lines.map((line) => ({
      account: String(line.account), amount: String(line.amount), notes: line.notes ?? "",
    })));
    scrollPageToTop();
  };
  const submitBudget = (event: FormEvent<HTMLFormElement>) => {
    const d = data(event);
    const lines = budgetLines
      .filter((line) => line.account && line.amount)
      .map((line) => ({ account: Number(line.account), amount: line.amount, notes: line.notes }));
    if (!lines.length) {
      setError("Give the budget at least one account and amount.");
      return;
    }
    const payload = {
      name: String(d.get("name")),
      start_date: String(d.get("start")),
      end_date: String(d.get("end")),
      lines,
    };
    if (editingBudgetId !== null) {
      updateBudget.mutate({ id: editingBudgetId, ...payload }, { onSuccess: resetBudgetForm });
      return;
    }
    const form = event.currentTarget;
    createBudget.mutate(payload, { onSuccess: () => { form.reset(); resetBudgetForm(); } });
  };
  const submitGrant = (event: FormEvent<HTMLFormElement>) => {
    const d = data(event);
    const payload = {
      name: String(d.get("name")),
      donor: String(d.get("donor")),
      project: String(d.get("project") || ""),
      restriction: String(d.get("restriction")) as "unrestricted" | "temporarily_restricted" | "restricted",
      funding_period_start: String(d.get("start")),
      funding_period_end: String(d.get("end")),
      total_amount: String(d.get("amount")),
      notes: String(d.get("notes") || ""),
    };
    if (editingGrantId !== null) {
      updateGrant.mutate({ id: editingGrantId, ...payload }, { onSuccess: () => { setEditingGrantId(null); setShowGrantForm(false); } });
      return;
    }
    const form = event.currentTarget;
    createGrant.mutate(payload, { onSuccess: () => { form.reset(); setShowGrantForm(false); } });
  };
  const submitGrantReceipt = (event: FormEvent<HTMLFormElement>, id: number) => {
    const d = data(event);
    const form = event.currentTarget;
    recordGrantReceipt.mutate({
      id,
      amount: String(d.get("amount")),
      receipt_account: Number(d.get("receipt_account")),
      description: String(d.get("description") || ""),
    }, { onSuccess: () => form.reset() });
  };
  const submitGrantExpense = (event: FormEvent<HTMLFormElement>, id: number) => {
    const d = data(event);
    const form = event.currentTarget;
    recordGrantExpense.mutate({
      id,
      amount: String(d.get("amount")),
      expense_account: Number(d.get("expense_account")),
      payment_account: Number(d.get("payment_account")),
      description: String(d.get("description") || ""),
    }, { onSuccess: () => form.reset() });
  };
  const submitPayroll = (event: FormEvent<HTMLFormElement>) => {
    const d = data(event);
    const form = event.currentTarget;
    generatePayroll.mutate({
      name: String(d.get('name')),
      period_start: String(d.get('start')),
      period_end: String(d.get('end')),
      notes: String(d.get('notes') || ''),
    }, { onSuccess: () => { form.reset(); setShowPayrollForm(false); } });
  };
  /**
   * Figures can only move while the run is still being prepared: once it has
   * gone in for review somebody is reading them, and once approved they are
   * what the ledger says.
   */
  const canEditDeductions = (period: PayrollPeriod) =>
    canInitiate &&
    period.status === "draft" &&
    ["draft", "returned"].includes(period.workflow_status || "draft");
  const submitDeductionEdit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingDeduction) return;
    const d = new FormData(event.currentTarget);
    editDeductions.mutate(
      {
        id: editingDeduction.id,
        other_deductions: String(d.get("other_deductions") || "0"),
        other_deductions_notes: String(d.get("notes") || "").trim(),
      },
      { onSuccess: () => setEditingDeduction(null) },
    );
  };
  /**
   * One handler for both shapes of payment: `part` null settles everything the
   * run still owes in a single transfer, otherwise it pays just that part.
   */
  const submitPayrollPayment = (
    event: FormEvent<HTMLFormElement>,
    id: number,
    part: { role: string } | null,
  ) => {
    const d = data(event);
    const common = {
      id,
      payment_account: Number(d.get('account')),
      reference: String(d.get('reference')),
      payment_date: String(d.get('date') || '') || undefined,
    };
    if (part) {
      payPayrollComponent.mutate({ ...common, role: part.role, amount: String(d.get('amount')) });
    } else {
      settlePayroll.mutate(common);
    }
    event.currentTarget.reset();
  };
  const submitTransportCharge = (event: FormEvent<HTMLFormElement>) => {
    const d = data(event);
    const form = event.currentTarget;
    createTransportCharge.mutate(
      {
        student_transport: Number(d.get('allocation')),
        amount: String(d.get('amount')),
        due_date: String(d.get('due')),
        notes: String(d.get('notes')),
      },
      { onSuccess: () => { form.reset(); setShowTransportForm(false); } },
    );
  };
  const submitTransportPayment = (event: FormEvent<HTMLFormElement>, id: number) => {
    const d = data(event);
    processTransportPayment.mutate({
      id,
      payment_method: String(d.get('method')),
      reference_number: String(d.get('reference')) || undefined,
      receipt_number: String(d.get('receipt')) || undefined,
    });
  };
  const submitTransportCancellation = (event: FormEvent<HTMLFormElement>, id: number) => {
    const d = data(event);
    cancelTransportCharge.mutate({ id, reason: String(d.get('reason')) });
  };
  const submitHostelCharge = (event: FormEvent<HTMLFormElement>) => {
    const d = data(event);
    const form = event.currentTarget;
    createHostelCharge.mutate(
      {
        allocation: Number(d.get('allocation')),
        fee: Number(d.get('fee')),
        amount: String(d.get('amount')),
        due_date: String(d.get('due')),
        notes: String(d.get('notes')),
      },
      { onSuccess: () => { form.reset(); setShowHostelForm(false); } },
    );
  };
  const submitHostelPayment = (event: FormEvent<HTMLFormElement>, id: number) => {
    const d = data(event);
    processHostelPayment.mutate({
      id,
      payment_method: String(d.get('method')),
      reference_number: String(d.get('reference')) || undefined,
      receipt_number: String(d.get('receipt')) || undefined,
    });
  };
  const submitHostelCancellation = (event: FormEvent<HTMLFormElement>, id: number) => {
    const d = data(event);
    cancelHostelCharge.mutate({ id, reason: String(d.get('reason')) });
  };
  const submitCafeteriaPayment = (event: FormEvent<HTMLFormElement>, id: number) => {
    const d = data(event);
    processCafeteriaPayment.mutate({
      id,
      payment_method: String(d.get('method')),
      reference_number: String(d.get('reference')) || undefined,
    });
  };
  const submitCafeteriaRefund = (event: FormEvent<HTMLFormElement>, id: number) => {
    const d = data(event);
    refundCafeteriaPayment.mutate({ id, reason: String(d.get('reason')) });
  };
  const submitCafeteriaSubscriptionPayment = (event: FormEvent<HTMLFormElement>, id: number) => {
    const d = data(event);
    processCafeteriaSubscriptionPayment.mutate({
      id,
      payment_method: String(d.get('method')),
      reference_number: String(d.get('reference')) || undefined,
    });
  };
  const submitCafeteriaSubscriptionRefund = (event: FormEvent<HTMLFormElement>, id: number) => {
    const d = data(event);
    refundCafeteriaSubscriptionPayment.mutate({ id, reason: String(d.get('reason')) });
  };
  const submitUniformPayment = (event: FormEvent<HTMLFormElement>, order: number) => {
    const d = data(event);
    processUniformPayment.mutate({
      order,
      amount: String(d.get('amount')),
      payment_method: String(d.get('method')),
      transaction_id: String(d.get('reference')),
    });
  };
  const submitUniformRefund = (event: FormEvent<HTMLFormElement>, id: number) => {
    const d = data(event);
    refundUniformPayment.mutate({ id, reason: String(d.get('reason')) });
  };
  const myUnsubmitted = (document: { workflow_status?: string }, initiator?: number | null) =>
    canInitiate && isEditableByInitiator(document, initiator, user?.id);
  // An account request this bursar raised and has not yet sent for approval,
  // or that a reviewer or the approver has sent back to them: still theirs to
  // correct, resubmit or delete.
  const myAccountRequest = (account: Account) =>
    canCreateAccountRequests && isEditableByInitiator(account, account.initiated_by, user?.id);
  const editingAccount = accounts.data?.find((account) => account.id === editingAccountId);
  const editingJournal = journals.data?.results.find((entry) => entry.id === editingJournalId);
  const editingBill = bills.data?.find((bill) => bill.id === editingBillId);
  const editingBudget = budgets.data?.find((budget) => budget.id === editingBudgetId);
  const editingCreditNote = creditNotes.data?.find((note) => note.id === editingCreditNoteId);
  const editingGrant = grants.data?.find((grant) => grant.id === editingGrantId);
  const resetJournalForm = () => {
    setEditingJournalId(null);
    setJournalLines([emptyGuidedJournalLine(), emptyGuidedJournalLine()]);
    setJournalTemplate("general");
    setJournalDescription("");
    setJournalReference("");
  };
  const editJournal = (entry: JournalEntry) => {
    setEditingJournalId(entry.id);
    // Set directly rather than through selectJournalTemplate, which resets the
    // lines to a blank template -- here the saved lines are what we want back.
    setJournalTemplate(entry.transaction_type);
    setJournalDescription(entry.description);
    setJournalReference(entry.reference ?? "");
    setJournalLines(entry.lines.map((line) => ({
      account: String(line.account),
      effect: journalLineEffect(line, accounts.data?.find((account) => account.id === line.account)),
      amount: String(Number(line.debit) > 0 ? line.debit : line.credit),
      description: line.description ?? "",
    })));
    scrollPageToTop();
  };
  const journalLineSide = (line: GuidedJournalLineState) => {
    const account = accounts.data?.find((candidate) => candidate.id === Number(line.account));
    if (!account) return "";
    const debitNormal = account.is_contra_account
      ? account.normal_balance !== "debit"
      : account.normal_balance === "debit";
    return debitNormal === (line.effect === "increase") ? "Debit" : "Credit";
  };
  const journalTotals = journalLines.reduce((totals, line) => {
    const amount = Number(line.amount || 0);
    const side = journalLineSide(line);
    if (side === "Debit") totals.debit += amount;
    if (side === "Credit") totals.credit += amount;
    return totals;
  }, { debit: 0, credit: 0 });
  const formatCurrency = (value: string | number | null | undefined) =>
    `UGX ${Number(value ?? 0).toLocaleString()}`;
  const formatStudentIdentifier = (value: string) => value.trim().toUpperCase();
  const actionItems = [
    {
      label: "Overdue invoices",
      count: stats.data?.overdue_invoices ?? 0,
      detail: formatCurrency(stats.data?.overdue_amount),
      link: "/financial/invoices",
      tone: "text-red-700 bg-red-50 border-red-200",
    },
    {
      label: "Pending refunds",
      count: refundRequests.data?.filter((request) => request.status === "pending").length ?? 0,
      detail: "Awaiting review",
      link: "/financial/refunds",
      tone: "text-amber-700 bg-amber-50 border-amber-200",
    },
    {
      label: "Draft journals",
      count: draftJournals.data?.count ?? 0,
      detail: "Not posted to the ledger",
      link: "/financial/journals",
      tone: "text-slate-700 bg-slate-50 border-slate-200",
    },
    {
      label: "Unreconciled bank lines",
      count: bankLines.data?.filter((line) => !line.matched_entry).length ?? 0,
      detail: "Require matching or review",
      link: "/financial/banking",
      tone: "text-violet-700 bg-violet-50 border-violet-200",
    },
  ].filter((item) => item.count > 0);
  const sumAmounts = (records: Array<{ amount?: string | number; total_amount?: string | number; amount_paid?: string | number }> | undefined) =>
    (records ?? []).reduce((total, record) => total + Number(record.amount ?? record.total_amount ?? record.amount_paid ?? 0), 0);
  const operationalRevenue = [
    { label: "Transport", value: sumAmounts(transportCharges.data), count: transportCharges.data?.length ?? 0, link: "/financial/transport" },
    { label: "Boarding", value: sumAmounts(hostelCharges.data), count: hostelCharges.data?.length ?? 0, link: "/financial/boarding" },
    { label: "Cafeteria", value: sumAmounts(cafeteriaOrders.data) + sumAmounts(cafeteriaSubscriptions.data), count: (cafeteriaOrders.data?.length ?? 0) + (cafeteriaSubscriptions.data?.length ?? 0), link: "/financial/cafeteria" },
    { label: "Uniforms", value: sumAmounts(uniformOrders.data), count: uniformOrders.data?.length ?? 0, link: "/financial/uniforms" },
  ];
  // Classes in school order (Baby, Middle, Top, P1 ...), then by name.
  const studentDirectoryClasses = Array.from(
    new Map(
      (studentDirectoryFilters.data ?? [])
        .filter((student) => student.current_class && student.class_name)
        .map((student) => [
          student.current_class!,
          { name: student.class_name!, order: student.class_level_order ?? 100 },
        ] as const),
    ).entries(),
  )
    .sort(([, first], [, second]) => first.order - second.order || first.name.localeCompare(second.name))
    .map(([id, { name }]) => [id, name] as const);
  // The catalogue is one small list, so it is filtered, sorted and paged here.
  const filteredFees = filterFees(fees.data ?? [], feeFilters);
  const feeStatus = feeStatusCounts(fees.data ?? [], feeFilters);
  const activeFeeFilters = countFeeFilters(feeFilters);
  const feePageRows = paginate(filteredFees, feePage, feePageSize);
  const feeIncomeAccount = (fee: Fee) => {
    const account = accounts.data?.find((entry) => entry.id === fee.income_account);
    return account ? `${account.code} · ${account.name}` : "Fee income (automatic)";
  };
  const updateFeeFilters = (changes: Partial<FeeFilters>) => {
    setFeeFilters((current) => ({ ...current, ...changes }));
    setFeePage(1);
  };
  const exportFees = (format: ExportFormat) => {
    const columns = feeExportColumns(feeIncomeAccount);
    if (format === "csv") exportAsCsv("fee-catalogue.csv", columns, filteredFees);
    if (format === "excel") exportAsExcel("fee-catalogue.xls", "Fee Catalogue", columns, filteredFees);
    if (format === "pdf") exportAsPdf("Fee Catalogue", columns, filteredFees);
  };
  // Looked up from the query each render so the details stay current after an edit.
  const viewingFee = fees.data?.find((fee) => fee.id === viewingFeeId) ?? null;
  const confirmDeleteFee = (fee: Fee) => {
    if (window.confirm(`Delete "${fee.name}"? This only works if the fee has never been used.`)) {
      deleteFee.mutate(fee.id, { onSuccess: () => setViewingFeeId(null) });
    }
  };
  // Fee structures: one list, filtered, sorted and paged here like the catalogue.
  const structureContext: FeeStructureContext = {
    termOf: (termId) => academicTerms.data?.find((term) => term.id === termId),
    levels: classLevels.data ?? [],
  };
  const structureTermName = (structure: FeeStructure) => {
    const term = academicTerms.data?.find((entry) => entry.id === structure.term);
    return term ? `${term.academic_year_name} · ${termLabel(term.term)}` : structure.term_name ?? "";
  };
  const filteredStructures = filterFeeStructures(feeStructures.data ?? [], structureFilters, structureContext);
  const structureInvoicing = feeStructureInvoicingCounts(feeStructures.data ?? [], structureFilters, structureContext);
  const activeStructureFilters = countFeeStructureFilters(structureFilters);
  const structurePageRows = paginate(filteredStructures, structurePage, structurePageSize);
  const viewingStructure = feeStructures.data?.find((structure) => structure.id === viewingStructureId) ?? null;
  const updateStructureFilters = (changes: Partial<FeeStructureFilters>) => {
    setStructureFilters((current) => ({ ...current, ...changes }));
    setStructurePage(1);
  };
  const openStructure = (id: number) => {
    setGenerationResult("");
    setViewingStructureId(id);
  };
  const closeStructure = () => {
    setViewingStructureId(null);
    setDiscountsOpenFor(null);
  };
  const exportStructures = (format: ExportFormat) => {
    const columns = feeStructureExportColumns(structureTermName);
    if (format === "csv") exportAsCsv("fee-structures.csv", columns, filteredStructures);
    if (format === "excel") exportAsExcel("fee-structures.xls", "Fee Structures", columns, filteredStructures);
    if (format === "pdf") exportAsPdf("Fee Structures", columns, filteredStructures);
  };
  const feeCategoryOptions = FEE_CATEGORIES.map(([value, label]) => <option key={value} value={value}>{label}</option>);
  const exportStudentDirectory = async (format: ExportFormat) => {
    setIsExportingStudentDirectory(true);
    try {
      const rows = await financialService.getAllStudents(studentDirectoryQuery);
      const columns = [
        { header: "Student", accessor: (student: StudentOption) => student.full_name },
        { header: "Student ID", accessor: (student: StudentOption) => formatStudentIdentifier(student.student_id) },
        { header: "Admission No.", accessor: (student: StudentOption) => student.admission_number || "" },
        { header: "Class", accessor: (student: StudentOption) => student.class_name || "Unassigned" },
        { header: "Gender", accessor: (student: StudentOption) => student.gender || "" },
        { header: "Status", accessor: (student: StudentOption) => student.status || "" },
      ];
      if (format === "csv") exportAsCsv("finance-student-directory.csv", columns, rows);
      if (format === "excel") exportAsExcel("finance-student-directory.xls", "Student Directory", columns, rows);
      if (format === "pdf") exportAsPdf("Finance Student Directory", columns, rows);
    } finally {
      setIsExportingStudentDirectory(false);
    }
  };
  // Which invoices a receipt could be moved to is a question for the server about
  // that learner, not a search through every invoice the school has issued.
  const moveAllocation = async (
    payment: LedgerPayment,
    allocation: NonNullable<LedgerPayment["allocations"]>[number],
  ) => {
    const { results } = await financialService.getInvoicePage({ student: payment.student, outstanding: true, page_size: 100 });
    const eligible = results.filter(
      (invoice) => invoice.id !== allocation.invoice && Number(invoice.balance_due) >= Number(allocation.active_amount),
    );
    const invoiceNumber = window.prompt(
      `Move this allocation to which invoice number?\n${eligible.map((invoice) => `${invoice.invoice_number} · UGX ${Number(invoice.balance_due).toLocaleString()} due`).join("\n")}`,
    )?.trim();
    const target = eligible.find((invoice) => invoice.invoice_number.toLowerCase() === invoiceNumber?.toLowerCase());
    if (!target) {
      if (invoiceNumber) setError("Enter one of the eligible invoice numbers shown.");
      return;
    }
    const reason = window.prompt("Reason for reallocating this payment")?.trim();
    if (reason) reallocatePayment.mutate({ id: allocation.id, invoice: target.id, reason });
  };
  const paymentPageSize = paymentRegisterPageSize;
  const visiblePayments = payments.data?.results ?? [];
  const paymentCount = payments.data?.count ?? 0;
  const paymentPageCount = Math.max(1, Math.ceil(paymentCount / paymentPageSize));
  const activePaymentFilters = countPaymentFilters(paymentRegisterFilters);
  const paymentFiltersActive = activePaymentFilters > 0;
  const paymentPeriod = activePeriod(paymentRegisterFilters);
  const viewingPayment = visiblePayments.find((payment) => payment.id === viewingPaymentId) ?? null;
  const updatePaymentFilters = (changes: Partial<PaymentRegisterFilters>) => {
    setPaymentRegisterFilters((current) => ({ ...current, ...changes }));
    setPaymentPage(1);
  };
  const canDownloadReceipt = (payment: LedgerPayment) => ["completed", "part_refunded"].includes(payment.payment_status);
  const refundPendingFor = (payment: LedgerPayment) =>
    Boolean(refundRequests.data?.some((item) => item.receipt === payment.id && item.status === "pending"));
  const canRequestRefund = (payment: LedgerPayment) =>
    canInitiate && canDownloadReceipt(payment) && Number(payment.refundable_amount) > 0 && !refundPendingFor(payment);
  const requestPaymentRefund = (payment: LedgerPayment) => setRefundFormFor(payment.id);
  const exportPayments = (format: ExportFormat) => {
    setIsExportingPayments(true);
    const columns = paymentExportColumns();
    // The register is read a page at a time, so an export asks the server for
    // every row that matches the filters on screen.
    financialService
      .getPayments(paymentFilters)
      .then((rows) => {
        if (format === "csv") exportAsCsv("payment-register.csv", columns, rows);
        if (format === "excel") exportAsExcel("payment-register.xls", "Payment Register", columns, rows);
        if (format === "pdf") exportAsPdf("Payment Register", columns, rows);
      })
      .catch(() => setError("The payment register could not be exported. Try again."))
      .finally(() => setIsExportingPayments(false));
  };
  // Refund requests arrive as one list, so they are filtered and paged here.
  const allRefunds = refundRequests.data ?? [];
  const filteredRefunds = filterRefunds(allRefunds, refundFilters, user?.id);
  const refundCounts = refundStatusCounts(allRefunds, refundFilters, user?.id);
  const activeRefundFilters = countRefundFilters(refundFilters);
  const refundPageRows = paginate(filteredRefunds, refundPage, refundPageSize);
  const viewingRefund = allRefunds.find((refund) => refund.id === viewingRefundId) ?? null;
  const awaitingMyDecision = allRefunds.filter((refund) => refund.status === "pending" && refund.requested_by !== user?.id).length;
  const updateRefundFilters = (changes: Partial<RefundFilters>) => {
    setRefundFilters((current) => ({ ...current, ...changes }));
    setRefundPage(1);
  };
  const openRefund = (id: number) => {
    setRefundDecisionNotes("");
    setViewingRefundId(id);
  };
  const closeRefund = () => {
    setViewingRefundId(null);
    setRefundDecisionNotes("");
  };
  const exportRefunds = (format: ExportFormat) => {
    const columns = refundExportColumns();
    if (format === "csv") exportAsCsv("refund-register.csv", columns, filteredRefunds);
    if (format === "excel") exportAsExcel("refund-register.xls", "Refund Register", columns, filteredRefunds);
    if (format === "pdf") exportAsPdf("Refund Register", columns, filteredRefunds);
  };
  const invoicePageSize = invoiceRegisterPageSize;
  const visibleInvoices = invoices.data?.results ?? [];
  const invoiceCount = invoices.data?.count ?? 0;
  const invoicePageCount = Math.max(1, Math.ceil(invoiceCount / invoicePageSize));
  const activeInvoiceFilters = countInvoiceFilters(invoiceRegisterFilters);
  const invoiceFiltersActive = activeInvoiceFilters > 0;
  const invoiceView = activeInvoiceView(invoiceRegisterFilters);
  const viewingInvoice = visibleInvoices.find((invoice) => invoice.id === viewingInvoiceId) ?? null;
  const updateInvoiceFilters = (changes: Partial<InvoiceRegisterFilters>) => {
    setInvoiceRegisterFilters((current) => ({ ...current, ...changes }));
    setInvoicePage(1);
  };
  const closeInvoice = () => {
    setViewingInvoiceId(null);
    setInvoiceDiscount(null);
  };
  // What may be done with an invoice, shared by its row and its details panel.
  const receiptsFor = (invoice: Invoice) =>
    (unallocatedReceipts.data?.results ?? []).filter((payment) =>
      payment.student === invoice.student &&
      (
        Number(payment.unallocated_amount || 0) > 0 ||
        (!payment.invoice && !payment.allocations?.length && Number(payment.amount) <= Number(invoice.balance_due))
      ),
    );
  const canApplyReceipt = (invoice: Invoice) => canInitiate && Number(invoice.balance_due) > 0;
  const canDiscountInvoice = (invoice: Invoice) =>
    canGenerateFeeInvoices && Boolean(invoice.fee_structure) && Number(invoice.paid_amount) === 0 &&
    Number(invoice.credited_amount || 0) === 0 && ["sent", "overdue"].includes(invoice.status) &&
    !invoice.lines?.some((line) => Number(line.line_total) < 0);
  const canCancelInvoice = (invoice: Invoice) =>
    canInitiate && Number(invoice.paid_amount) === 0 && ["sent", "overdue"].includes(invoice.status);
  const confirmCancelInvoice = (invoice: Invoice) => {
    const reason = window.prompt(`Reason for cancelling ${invoice.invoice_number}`)?.trim();
    if (reason) cancelInvoice.mutate({ id: invoice.id, reason });
  };
  const visibleJournals = journals.data?.results ?? [];
  const journalCount = journals.data?.count ?? 0;
  const journalPageCount = Math.max(1, Math.ceil(journalCount / journalPageSize));
  const journalFiltersActive = Boolean(journalSearch) || journalStatusFilter !== "all";
  const firstJournalRow = (journalPage - 1) * journalPageSize + 1;
  const journalBalanced = journalTotals.debit > 0 && journalTotals.debit === journalTotals.credit;
  // Transfer and pay-now templates fix the rows and their effects; only the
  // accounts and amounts are the bursar's to choose.
  const journalLinesFixed = ["internal_transfer", "immediate_expense"].includes(journalTemplate);
  const exportJournals = (format: "csv" | "excel" | "pdf") => {
    setIsExportingJournals(true);
    const columns = [
      { header: "Entry no.", accessor: (entry: JournalEntry) => entry.entry_number },
      { header: "Date", accessor: (entry: JournalEntry) => entry.entry_date },
      { header: "Description", accessor: (entry: JournalEntry) => entry.description },
      { header: "Reference", accessor: (entry: JournalEntry) => entry.reference || "" },
      { header: "Type", accessor: (entry: JournalEntry) => entry.transaction_type.replace(/_/g, " ") },
      {
        header: "Accounts",
        accessor: (entry: JournalEntry) => entry.lines
          .map((line) => `${line.account_code} ${Number(line.debit) ? `Dr ${Number(line.debit).toLocaleString()}` : `Cr ${Number(line.credit).toLocaleString()}`}`)
          .join("; "),
      },
      { header: "Amount (UGX)", accessor: (entry: JournalEntry) => entry.lines.reduce((total, line) => total + Number(line.debit), 0).toLocaleString() },
      { header: "Stage", accessor: (entry: JournalEntry) => entry.workflow_status || entry.status },
      { header: "Prepared by", accessor: (entry: JournalEntry) => entry.created_by_name || "" },
    ];
    financialService
      .getJournalEntries(journalFilters)
      .then((rows) => {
        if (format === "csv") exportAsCsv("journal-register.csv", columns, rows);
        if (format === "excel") exportAsExcel("journal-register.xls", "Journal Register", columns, rows);
        if (format === "pdf") exportAsPdf("Journal Register", columns, rows);
      })
      .catch(() => setError("The journal register could not be exported. Try again."))
      .finally(() => setIsExportingJournals(false));
  };
  // A register can shrink under the reader -- a receipt is allocated, the last draft
  // journal is posted -- and the server has no page 5 to give any more.
  useEffect(() => { if (invoicePage > invoicePageCount) setInvoicePage(invoicePageCount); }, [invoicePage, invoicePageCount]);
  useEffect(() => { if (paymentPage > paymentPageCount) setPaymentPage(paymentPageCount); }, [paymentPage, paymentPageCount]);
  useEffect(() => { if (journalPage > journalPageCount) setJournalPage(journalPageCount); }, [journalPage, journalPageCount]);
  // Credit notes arrive as one list, so they are filtered and paged here.
  const allCreditNotes = creditNotes.data ?? [];
  const creditActor = { userId: user?.id, canReview, canApprove };
  const filteredCreditNotes = filterCreditNotes(allCreditNotes, creditFilters, creditActor);
  const creditCounts = creditViewCounts(allCreditNotes, creditFilters, creditActor);
  const activeCreditFilters = countCreditFilters(creditFilters);
  const creditPageRows = paginate(filteredCreditNotes, creditPage, creditPageSize);
  const viewingCredit = allCreditNotes.find((note) => note.id === viewingCreditId) ?? null;
  const creditsNeedingMe = allCreditNotes.filter((note) => creditNeedsActor(note, creditActor)).length;
  const updateCreditFilters = (changes: Partial<CreditNoteFilters>) => {
    setCreditFilters((current) => ({ ...current, ...changes }));
    setCreditPage(1);
  };
  const openCredit = (id: number) => {
    setCreditDecisionNotes("");
    setViewingCreditId(id);
  };
  const closeCredit = () => {
    setViewingCreditId(null);
    setCreditDecisionNotes("");
  };
  const exportCreditNotes = (format: ExportFormat) => {
    const columns = creditNoteExportColumns();
    if (format === "csv") exportAsCsv("credit-notes.csv", columns, filteredCreditNotes);
    if (format === "excel") exportAsExcel("credit-notes.xls", "Credit Notes", columns, filteredCreditNotes);
    if (format === "pdf") exportAsPdf("Credit Notes", columns, filteredCreditNotes);
  };
  const exportInvoices = (format: ExportFormat) => {
    setIsExportingInvoices(true);
    const columns = invoiceExportColumns();
    // The register is read a page at a time, so an export asks the server for
    // every row that matches the filters on screen.
    financialService
      .getInvoices(invoiceFilters)
      .then((rows) => {
        if (format === "csv") exportAsCsv("invoice-register.csv", columns, rows);
        if (format === "excel") exportAsExcel("invoice-register.xls", "Invoice Register", columns, rows);
        if (format === "pdf") exportAsPdf("Invoice Register", columns, rows);
      })
      .catch(() => setError("The invoice register could not be exported. Try again."))
      .finally(() => setIsExportingInvoices(false));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Financial Management
        </h1>
        <p className="text-gray-600">
          Manage the fee ledger, invoices, receipts, and collection performance
        </p>
      </div>
      {!showOverviewSummary && <FinancialSectionNav />}
      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {showOverviewSummary && (
        <FinanceOverview
          reportStart={reportStart}
          reportEnd={reportEnd}
          asOfDate={asOfDate}
          onPeriodChange={(start, end) => {
            setReportStart(start);
            setReportEnd(end);
          }}
          onAsOfChange={setAsOfDate}
          canExport={canExportReports}
          canViewAuditLogs={canViewAuditLogs}
          actionItems={actionItems}
          services={operationalRevenue}
          unreconciledLines={bankLines.data?.filter((line) => !line.matched_entry).length ?? 0}
          suggestedMatches={bankSuggestions.data?.reduce((total, item) => total + item.suggestions.length, 0) ?? 0}
          grantBalance={grants.data?.filter((grant) => grant.status === "active").reduce((total, grant) => total + Number(grant.balance), 0) ?? 0}
          activeGrants={grants.data?.filter((grant) => grant.status === "active").length ?? 0}
          latestNetPay={payrollPeriods.data?.[0]?.total_net_pay}
          onOpenStudent={setViewStudent}
        />
      )}
      {activeTab === "students" && (
        <div className="card">
          <div className="card-header pb-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <Users className="h-5 w-5" /> Student financial directory
                </h2>
                <p className="text-sm text-gray-500">Open a student profile to review invoices, payments, credits, and current balance.</p>
              </div>
              <ExportButtons
                onExport={exportStudentDirectory}
                disabled={!studentDirectory.data?.count}
                busy={isExportingStudentDirectory}
                what="the filtered directory"
              />
            </div>
          </div>
          <div className="card-content space-y-4">
            <div className="filter-panel">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div role="group" aria-label="Enrolment" className="segmented">
                  {(Object.keys(FINANCE_ENROLMENT) as FinanceStudentFilters["enrolment"][]).map((value) => (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={studentFilters.enrolment === value}
                      onClick={() => updateStudentFilters({ enrolment: value })}
                      className="segmented-option"
                    >
                      {FINANCE_ENROLMENT[value][0]}
                      {studentFilters.enrolment === value && studentDirectory.data && (
                        <span className="ml-1.5 tabular-nums opacity-70">{studentDirectory.data.count}</span>
                      )}
                    </button>
                  ))}
                </div>
                {activeStudentFilters > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setStudentDirectorySearch("");
                      setStudentFilters(EMPTY_FINANCE_STUDENT_FILTERS);
                      setStudentDirectoryPage(1);
                    }}
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                  >
                    <X className="h-3.5 w-3.5" /> Clear {activeStudentFilters}{" "}
                    {activeStudentFilters === 1 ? "filter" : "filters"}
                  </button>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-[minmax(16rem,2fr)_repeat(5,minmax(0,1fr))]">
                <label className="filter-field sm:col-span-2 lg:col-span-3 2xl:col-span-1">
                  Search
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                    <input
                      type="search"
                      value={studentDirectorySearch}
                      onChange={(event) => setStudentDirectorySearch(event.target.value)}
                      className="input-compact pl-8"
                      placeholder="Name, student ID or admission number"
                    />
                  </div>
                </label>
                <label className="filter-field">
                  Class
                  <select
                    value={studentFilters.current_class}
                    onChange={(event) => updateStudentFilters({ current_class: event.target.value })}
                    className="input-compact"
                  >
                    <option value="">All classes</option>
                    {studentDirectoryClasses.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                  </select>
                </label>
                <label className="filter-field">
                  Gender
                  <select
                    value={studentFilters.gender}
                    onChange={(event) => updateStudentFilters({ gender: event.target.value as FinanceStudentFilters["gender"] })}
                    className="input-compact"
                  >
                    <option value="">All genders</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </label>
                <label className="filter-field">
                  Residence
                  <select
                    value={studentFilters.residence}
                    onChange={(event) => updateStudentFilters({ residence: event.target.value as FinanceStudentFilters["residence"] })}
                    className="input-compact"
                  >
                    <option value="">Day and boarding</option>
                    <option value="day">Day scholars</option>
                    <option value="boarding">Boarders</option>
                  </select>
                </label>
                <label className="filter-field">
                  Transport
                  <select
                    value={studentFilters.uses_transport}
                    onChange={(event) => updateStudentFilters({ uses_transport: event.target.value as FinanceStudentFilters["uses_transport"] })}
                    className="input-compact"
                  >
                    <option value="">Everyone</option>
                    <option value="true">Uses school transport</option>
                    <option value="false">Own transport</option>
                  </select>
                </label>
                <label className="filter-field">
                  Sort by
                  <select
                    value={studentFilters.ordering}
                    onChange={(event) => updateStudentFilters({ ordering: event.target.value as FinanceStudentFilters["ordering"] })}
                    className="input-compact"
                  >
                    {Object.entries(FINANCE_STUDENT_ORDERINGS).map(([value, [label]]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            {studentDirectory.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 8 }).map((_, index) => (
                  <div key={index} className="h-7 animate-pulse rounded bg-gray-100" />
                ))}
              </div>
            ) : studentDirectory.isError ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                The student directory could not be loaded.{" "}
                <button type="button" className="font-medium underline" onClick={() => studentDirectory.refetch()}>Try again</button>
              </div>
            ) : !studentDirectory.data?.results.length ? (
              <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
                {activeStudentFilters ? "No students match these filters." : "No students are enrolled yet."}
              </div>
            ) : (
              <div className="space-y-3">
                <div className={`sheet-wrap ${studentDirectory.isFetching ? "opacity-60" : ""}`}>
                  <table className="sheet">
                    <thead>
                      <tr>
                        <th className="sheet-rownum">#</th>
                        <th>Student</th>
                        <th>Student ID</th>
                        <th>Admission no.</th>
                        <th>Class</th>
                        <th>Gender</th>
                        <th>Status</th>
                        <th className="text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {studentDirectory.data.results.map((student, index) => (
                        <tr key={student.id} className="cursor-pointer" onClick={() => setViewStudent(student)}>
                          <td className="sheet-rownum">{firstStudentRow + index}</td>
                          <td>
                            <span className="inline-flex items-center gap-2">
                              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-[10px] font-semibold text-primary-700">
                                {initials(student.full_name)}
                              </span>
                              <span className="font-medium text-gray-900">{student.full_name}</span>
                            </span>
                          </td>
                          <td className="font-mono text-xs text-gray-700">{formatStudentIdentifier(student.student_id)}</td>
                          <td className="font-mono text-xs text-gray-600">
                            {student.admission_number || <span className="font-sans text-gray-300">—</span>}
                          </td>
                          <td className="text-gray-700">{student.class_name || <span className="text-gray-400">Unassigned</span>}</td>
                          <td className="capitalize text-gray-600">{student.gender || <span className="text-gray-300">—</span>}</td>
                          <td>
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium capitalize text-gray-700">
                              <span className={`h-1.5 w-1.5 rounded-full ${STUDENT_ENROLMENT_DOTS[student.status ?? ""] || "bg-gray-400"}`} />
                              {student.status || "active"}
                            </span>
                          </td>
                          <td className="text-right">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                setViewStudent(student);
                              }}
                              className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-primary-700 hover:bg-primary-50"
                            >
                              <Eye className="h-3.5 w-3.5" /> Open profile
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pager
                  first={firstStudentRow}
                  shown={studentDirectory.data.results.length}
                  total={studentDirectory.data.count}
                  noun={studentDirectory.data.count === 1 ? "student" : "students"}
                  page={studentDirectoryPage}
                  pageCount={studentDirectoryPageCount}
                  onPage={setStudentDirectoryPage}
                  pageSize={studentDirectoryPageSize}
                  pageSizes={FINANCE_STUDENT_PAGE_SIZES}
                  onPageSize={(size) => {
                    setStudentDirectoryPageSize(size);
                    setStudentDirectoryPage(1);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "fees" && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-primary-100 bg-primary-50 p-5">
            <div>
              <h2 className="font-semibold text-primary-950">Fee catalogue</h2>
              <p className="mt-1 max-w-2xl text-sm text-primary-800">Maintain the prices available for fee structures. Creating a catalogue item does not bill students.</p>
            </div>
            <div className="flex items-center gap-3 text-sm"><span className="rounded-md bg-white px-3 py-2 font-medium text-gray-700">{fees.data?.length ?? 0} fee items</span><span className="rounded-md bg-white px-3 py-2 font-medium text-emerald-700">{fees.data?.filter((fee) => fee.is_active).length ?? 0} active</span></div>
          </div>
          {canConfigureFees && (showFeeForm || fees.data?.length === 0) && <form onSubmit={submitFee} className="card">
            <div className="card-titlebar">
              <div>
                <h3 className="font-semibold text-gray-900">Add catalogue item</h3>
                <p className="mt-1 text-sm text-gray-500">Define the price, who pays it, and how it may be paid.</p>
              </div>
              {!!fees.data?.length && (
                <button type="button" onClick={() => setShowFeeForm(false)} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close the new fee form">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="card-content space-y-5 pt-5">
              <FormSection title="The fee" divided={false} columns={3}>
                <Field label="Fee name">
                  <input required name="name" className="input mt-1" placeholder="e.g. Term 3 tuition" />
                </Field>
                <Field label="Category">
                  <select name="type" className="input mt-1">{feeCategoryOptions}</select>
                </Field>
                <Field label="Amount (UGX)">
                  <input required min="1" type="number" name="amount" className="input mt-1 tabular-nums" placeholder="0" />
                </Field>
                <Field label="Description" optional className="sm:col-span-2 lg:col-span-3" hint="What the fee covers, when it is charged, any conditions. Shown in full when the fee is opened.">
                  <textarea name="description" rows={3} className="input mt-1 h-auto min-h-[4.5rem] resize-y py-2 leading-5" />
                </Field>
              </FormSection>
              <FormSection title="Billing rules" columns={3}>
                <Field label="Who pays">
                  <select name="applies_to" className="input mt-1" defaultValue="all">
                    <option value="all">Day and boarding students</option>
                    <option value="day">Day students only</option>
                    <option value="boarding">Boarding students only</option>
                  </select>
                </Field>
                <Field as="div" label="Income account" optional className="lg:col-span-2" hint="Left alone, the school fee income account is used.">
                  <AccountSelect
                    name="income_account"
                    placeholder="Fee income (automatic)"
                    accounts={accounts.data}
                    isSelectable={(account) => canReceivePostings(account) && account.account_type === "income"}
                  />
                </Field>
                <div className="space-y-2 sm:col-span-2 lg:col-span-3">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" name="allows_partial_payment" defaultChecked />
                    Parents may pay this fee in installments
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" name="transport_only" />
                    Transport fee: bill only students who use school transport
                  </label>
                </div>
              </FormSection>
            </div>
            <div className="card-footer">
              <p className="mr-auto text-xs text-gray-500">Adding a fee to the catalogue does not bill anyone.</p>
              <button disabled={createFee.isPending} className="btn btn-primary">
                <Plus className="mr-2 h-4 w-4" />
                Create fee
              </button>
            </div>
          </form>}
          <div className="card">
            <div className="card-header pb-4">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-gray-900">Catalogue items</h3>
                  <p className="mt-1 text-sm text-gray-500">Only active fees can be included in new fee structures.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <ExportButtons onExport={exportFees} disabled={!filteredFees.length} what="the filtered catalogue" />
                  {canConfigureFees && !showFeeForm && !!fees.data?.length && (
                    <button type="button" onClick={() => setShowFeeForm(true)} className="btn-primary h-8 min-h-8 px-3 text-xs">
                      <Plus className="mr-1.5 h-3.5 w-3.5" /> New fee
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="card-content space-y-4">
              <div className="filter-panel">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div role="group" aria-label="Status" className="segmented">
                    {([["all", "All"], ["active", "Active"], ["inactive", "Inactive"]] as const).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={feeFilters.status === value}
                        onClick={() => updateFeeFilters({ status: value })}
                        className="segmented-option"
                      >
                        {label}
                        <span className="ml-1.5 tabular-nums opacity-70">{feeStatus[value]}</span>
                      </button>
                    ))}
                  </div>
                  {activeFeeFilters > 0 && (
                    <button
                      type="button"
                      onClick={() => updateFeeFilters({ ...EMPTY_FEE_FILTERS, status: feeFilters.status, ordering: feeFilters.ordering })}
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                    >
                      <X className="h-3.5 w-3.5" /> Clear {activeFeeFilters} {activeFeeFilters === 1 ? "filter" : "filters"}
                    </button>
                  )}
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-[minmax(16rem,2fr)_repeat(4,minmax(0,1fr))]">
                  <label className="filter-field sm:col-span-2 lg:col-span-3 2xl:col-span-1">
                    Search
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                      <input
                        type="search"
                        value={feeFilters.search}
                        onChange={(event) => updateFeeFilters({ search: event.target.value })}
                        className="input-compact pl-8"
                        placeholder="Fee name or description"
                      />
                    </div>
                  </label>
                  <label className="filter-field">
                    Category
                    <select value={feeFilters.category} onChange={(event) => updateFeeFilters({ category: event.target.value })} className="input-compact">
                      <option value="">All categories</option>
                      {feeCategoryOptions}
                    </select>
                  </label>
                  <label className="filter-field">
                    Who pays
                    <select
                      value={feeFilters.appliesTo}
                      onChange={(event) => updateFeeFilters({ appliesTo: event.target.value as FeeFilters["appliesTo"] })}
                      className="input-compact"
                    >
                      <option value="">Anyone</option>
                      <option value="all">Day and boarding</option>
                      <option value="day">Day students only</option>
                      <option value="boarding">Boarders only</option>
                    </select>
                  </label>
                  <label className="filter-field">
                    Payment terms
                    <select
                      value={feeFilters.terms}
                      onChange={(event) => updateFeeFilters({ terms: event.target.value as FeeFilters["terms"] })}
                      className="input-compact"
                    >
                      <option value="">Any terms</option>
                      <option value="installments">Installments allowed</option>
                      <option value="full">Pay in full</option>
                    </select>
                  </label>
                  <label className="filter-field">
                    Sort by
                    <select
                      value={feeFilters.ordering}
                      onChange={(event) => updateFeeFilters({ ordering: event.target.value as FeeFilters["ordering"] })}
                      className="input-compact"
                    >
                      {Object.entries(FEE_ORDERINGS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </label>
                </div>
              </div>

              {fees.isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 6 }).map((_, index) => <div key={index} className="h-7 animate-pulse rounded bg-gray-100" />)}
                </div>
              ) : fees.isError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                  The fee catalogue could not be loaded.{" "}
                  <button type="button" className="font-medium underline" onClick={() => fees.refetch()}>Try again</button>
                </div>
              ) : !filteredFees.length ? (
                <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
                  {fees.data?.length ? "No fees match these filters." : "No fee catalogue items have been created yet."}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="sheet-wrap">
                    <table className="sheet">
                      <thead>
                        <tr>
                          <th className="sheet-rownum">#</th>
                          <th>Fee</th>
                          <th>Category</th>
                          <th>Who pays</th>
                          <th>Payment terms</th>
                          <th>Income account</th>
                          <th className="text-right">Amount (UGX)</th>
                          <th>Status</th>
                          {canConfigureFees && <th className="text-right">Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {feePageRows.rows.map((fee, index) =>
                          canConfigureFees && editingFeeId === fee.id ? (
                            <tr key={fee.id}>
                              <td colSpan={9} className="whitespace-normal bg-amber-50/50 p-0">
                  <form onSubmit={(event) => submitFeeEdit(event, fee.id)} className="bg-amber-50/50">
                    <div className="grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-4">
                      <p className="text-sm font-semibold text-gray-900 sm:col-span-2 lg:col-span-4">Editing {fee.name}</p>
                      <Field label="Fee name">
                        <input required name="name" defaultValue={fee.name} className="input mt-1" />
                      </Field>
                      <Field label="Category">
                        <select name="type" defaultValue={fee.fee_type} className="input mt-1">{feeCategoryOptions}</select>
                      </Field>
                      <Field label="Amount (UGX)">
                        <input required min="1" type="number" name="amount" defaultValue={fee.amount} className="input mt-1 tabular-nums" />
                      </Field>
                      <Field label="Who pays">
                        <select name="applies_to" defaultValue={fee.applies_to || "all"} className="input mt-1">
                          <option value="all">Day and boarding students</option>
                          <option value="day">Day students only</option>
                          <option value="boarding">Boarding students only</option>
                        </select>
                      </Field>
                      <Field label="Description" optional className="sm:col-span-2">
                        <textarea name="description" rows={3} defaultValue={fee.description} className="input mt-1 h-auto min-h-[4.5rem] resize-y py-2 leading-5" />
                      </Field>
                      <Field as="div" label="Income account" optional className="sm:col-span-2">
                        <AccountSelect
                          name="income_account"
                          defaultValue={fee.income_account ? String(fee.income_account) : ""}
                          placeholder="Fee income (automatic)"
                          accounts={accounts.data}
                          isSelectable={(account) => canReceivePostings(account) && account.account_type === "income"}
                        />
                      </Field>
                      <div className="flex flex-wrap gap-x-6 gap-y-2 sm:col-span-2 lg:col-span-4">
                        <label className="flex items-center gap-2 text-sm text-gray-700">
                          <input type="checkbox" name="allows_partial_payment" defaultChecked={fee.allows_partial_payment} />
                          Installments allowed
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700">
                          <input type="checkbox" name="transport_only" defaultChecked={fee.transport_only} />
                          School transport users only
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700">
                          <input type="checkbox" name="is_active" defaultChecked={fee.is_active} />
                          Active: available for new fee structures
                        </label>
                      </div>
                      <div className="flex justify-end gap-2 sm:col-span-2 lg:col-span-4">
                        <button type="button" onClick={() => setEditingFeeId(null)} className="btn-outline">Cancel</button>
                        <button disabled={updateFee.isPending} className="btn btn-primary">Save changes</button>
                      </div>
                    </div>
                  </form>
                              </td>
                            </tr>
                          ) : (
                            <tr key={fee.id} className="cursor-pointer" onClick={() => setViewingFeeId(fee.id)}>
                              <td className="sheet-rownum">{feePageRows.first + index}</td>
                              <td className="max-w-[18rem]">
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setViewingFeeId(fee.id);
                                  }}
                                  className="max-w-full truncate text-left font-medium text-gray-900 hover:text-primary-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                                  title="View fee details"
                                >
                                  {fee.name}
                                </button>
                                {fee.description && <span className="block truncate text-xs text-gray-500">{fee.description}</span>}
                              </td>
                              <td className="text-gray-700">{feeCategoryLabel(fee.fee_type)}</td>
                              <td className="text-gray-700">{feeAudienceLabel(fee)}</td>
                              <td className="text-gray-700">{feeTermsLabel(fee)}</td>
                              <td className="text-gray-600">{feeIncomeAccount(fee)}</td>
                              <td className="text-right font-semibold tabular-nums text-gray-900">{Number(fee.amount).toLocaleString()}</td>
                              <td>
                                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700">
                                  <span className={`h-1.5 w-1.5 rounded-full ${fee.is_active ? "bg-emerald-500" : "bg-gray-400"}`} />
                                  {fee.is_active ? "Active" : "Inactive"}
                                </span>
                              </td>
                              {canConfigureFees && (
                                <td className="text-right">
                                  <span className="inline-flex gap-1">
                                    <button
                                      type="button"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setEditingFeeId(fee.id);
                                      }}
                                      className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-primary-700 hover:bg-primary-50"
                                    >
                                      <Edit className="h-3.5 w-3.5" /> Edit
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        confirmDeleteFee(fee);
                                      }}
                                      className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-red-600 hover:bg-red-50"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" /> Delete
                                    </button>
                                  </span>
                                </td>
                              )}
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  </div>
                  <FeeDetailsModal
                    fee={viewingFee}
                    incomeAccount={feeIncomeAccount}
                    onClose={() => setViewingFeeId(null)}
                    onEdit={canConfigureFees ? (fee) => {
                      setViewingFeeId(null);
                      setEditingFeeId(fee.id);
                    } : undefined}
                    onDelete={canConfigureFees ? confirmDeleteFee : undefined}
                  />
                  <Pager
                    first={feePageRows.first}
                    shown={feePageRows.rows.length}
                    total={filteredFees.length}
                    noun={filteredFees.length === 1 ? "fee" : "fees"}
                    page={feePageRows.page}
                    pageCount={feePageRows.pageCount}
                    onPage={setFeePage}
                    pageSize={feePageSize}
                    pageSizes={FEE_PAGE_SIZES}
                    onPageSize={(size) => {
                      setFeePageSize(size);
                      setFeePage(1);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "payments" && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-primary-100 bg-primary-50 p-5">
            <div><h2 className="font-semibold text-primary-950">Payment collection</h2><p className="mt-1 max-w-2xl text-sm text-primary-800">Record verified receipts, allocate them to invoices, and manage student credit or refund requests.</p></div>
            <div className="flex items-center gap-3 text-sm"><span className="rounded-md bg-white px-3 py-2 font-medium text-gray-700">{paymentCount} receipts</span><span className="rounded-md bg-white px-3 py-2 font-medium text-amber-700">{unallocatedReceipts.data?.count ?? 0} credits to allocate</span></div>
          </div>
          {canInitiate && <form onSubmit={submitPayment} className="card">
            <div className="card-titlebar">
              <div>
                <h3 className="font-semibold text-gray-900">Record receipt</h3>
                <p className="mt-1 text-sm text-gray-500">For money you have seen: cash handed over, or a transfer showing on the statement.</p>
              </div>
              <span className="rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">Verified collection</span>
            </div>
            <div className="card-content space-y-5 pt-5">
              <FormSection title="Who paid" divided={false} columns={3}>
                <Field as="div" label="Student">
                  <StudentSearchField onSelect={(student) => setReceiptStudentId(student?.id ?? null)} />
                </Field>
                <Field
                  label="Apply to invoice"
                  className="lg:col-span-2"
                  hint={
                    !receiptStudentId
                      ? "Choose the student first and their unpaid invoices appear here."
                      : !receiptStudentInvoices.length
                        ? "This student owes nothing, so the money is held for them and applied to their next invoice."
                        : !receiptInvoiceId && receiptStudentInvoices.length > 1
                          ? `They have ${receiptStudentInvoices.length} unpaid invoices totalling UGX ${receiptStudentOwes.toLocaleString()}. Left as it is, the money goes to the oldest first and any remainder is held for them.`
                          : undefined
                  }
                >
                  <select
                    name="invoice"
                    className="input mt-1"
                    value={receiptInvoiceId}
                    onChange={(event) => setReceiptInvoiceId(event.target.value)}
                  >
                    <option value="">Their oldest unpaid invoice first</option>
                    {receiptStudentInvoices.map((invoice) => (
                      <option key={invoice.id} value={invoice.id}>
                        {invoice.invoice_number} · UGX {Number(invoice.balance_due).toLocaleString()} due
                        {Number(invoice.minimum_payment ?? 0) >= Number(invoice.balance_due)
                          ? " · pay in full"
                          : Number(invoice.minimum_payment ?? 0) > 0
                            ? ` · at least UGX ${Number(invoice.minimum_payment).toLocaleString()} first`
                            : ""}
                      </option>
                    ))}
                  </select>
                </Field>
              </FormSection>

              <FormSection title="The payment" columns={3}>
                <Field
                  label="Paid for"
                  hint={
                    receiptStudentId && receiptFeeChoices.length
                      ? `Charges on ${receiptInvoiceId ? "this invoice" : "their unpaid invoices"}, at the amounts this student was billed.`
                      : receiptStudentId
                        ? "Nothing is billed to them yet, so this is the fee catalogue at its standard prices."
                        : undefined
                  }
                >
                  <select required name="fee" className="input mt-1" disabled={!receiptStudentId}>
                    <option value="">{receiptStudentId ? "Choose a fee" : "Choose the student first"}</option>
                    {(receiptFeeChoices.length ? receiptFeeChoices : receiptFeeFallback).map((fee) => (
                      <option key={fee.id} value={fee.id}>
                        {fee.name} · UGX {Number(fee.amount).toLocaleString()}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  label="Amount received (UGX)"
                  hint={receiptStudentId && receiptStudentOwes > 0 ? `They owe UGX ${receiptStudentOwes.toLocaleString()} in total.` : undefined}
                >
                  <input required min="1" type="number" name="amount" className="input mt-1 tabular-nums" placeholder="0" />
                </Field>
                <Field label="Method" hint={receiptMethod === "cash" ? "Cash is dated today, the day it is handed over." : undefined}>
                  <select name="method" className="input mt-1" value={receiptMethod} onChange={(event) => setReceiptMethod(event.target.value)}>
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank transfer</option>
                    <option value="mobile_money">Mobile money</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </Field>
                {receiptMethod !== "cash" && (
                  <Field label="Date the money arrived" hint="The day it reached the account, which can be before today.">
                    <input name="received_on" type="date" max={today} defaultValue={today} className="input mt-1" />
                  </Field>
                )}
                <Field label="Payer's reference" optional hint="Their cheque, bank slip or SurePay number. The receipt number is issued by the system.">
                  <input name="reference" className="input mt-1" placeholder="e.g. slip or transaction number" />
                </Field>
                <Field
                  as="div"
                  label="Received into"
                  optional
                  hint="Left alone, the method decides: cash to the cash account, a transfer to the fees bank. Set it only when the money went somewhere else."
                >
                  <AccountSelect
                    name="receipt_account"
                    placeholder="Automatic from the method"
                    accounts={accounts.data}
                    isSelectable={moneyLandsHere}
                  />
                </Field>
              </FormSection>
            </div>
            <div className="card-footer">
              <p className="mr-auto text-xs text-gray-500">A receipt number is issued and the money is applied as soon as you record it.</p>
              <button disabled={recordPayment.isPending} className="btn btn-primary">
                {recordPayment.isPending ? "Recording…" : "Record verified payment"}
              </button>
            </div>
          </form>}
          {canInitiate && (
            <form onSubmit={submitProviderImport} className="card">
              <div className="card-titlebar">
                <div>
                  <h3 className="font-semibold text-gray-900">Import payment-provider collections</h3>
                  <p className="mt-1 max-w-3xl text-sm text-gray-500">
                    Preview a SurePay or other provider report, catch duplicates, match student IDs and invoices, then post the accepted rows once.
                  </p>
                </div>
                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">Preview before posting</span>
              </div>
              <div className="card-content space-y-5 pt-5">
                <FormSection title="Provider and account" divided={false} columns={3}>
                  <Field label="Provider" hint="As it appears on the report, e.g. SurePay or PesaPal.">
                    <input required name="provider" defaultValue="surepay" list="payment-providers" className="input mt-1" />
                    <datalist id="payment-providers">
                      <option value="surepay">SurePay</option>
                      <option value="pesapal">PesaPal</option>
                      <option value="mtn_mobile_money">MTN Mobile Money</option>
                      <option value="airtel_money">Airtel Money</option>
                    </datalist>
                  </Field>
                  <Field as="div" label="Received into" hint="The bank or clearing account the provider pays out to.">
                    <AccountSelect
                      required
                      name="receipt_account"
                      placeholder="Choose the receiving account"
                      accounts={accounts.data}
                      isSelectable={moneyLandsHere}
                    />
                  </Field>
                  <Field label="Report total (UGX)" optional hint="From the provider's settlement, to check the file adds up.">
                    <input name="settlement_total" min="0" step="0.01" type="number" className="input mt-1 tabular-nums" placeholder="0" />
                  </Field>
                </FormSection>

                <FormSection
                  title="Dates the report covers"
                  description="Re-importing dates already loaded is refused, and a gap since the last report is flagged so it can be gone back for."
                  columns={3}
                >
                  <Field label="From">
                    <input required name="period_start" type="date" className="input mt-1" />
                  </Field>
                  <Field label="To">
                    <input required name="period_end" type="date" className="input mt-1" />
                  </Field>
                </FormSection>

                <FormSection title="Report file" columns={1}>
                  <Field
                    label="CSV file"
                    hint="Needs a transaction ID, payment date, SurePay or student ID, and amount. Invoice number and payer name are optional. Common header names are recognised automatically."
                  >
                    <input required name="file" type="file" accept=".csv,text/csv" className="input-file mt-1" />
                  </Field>
                  <details className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
                    <summary className="cursor-pointer text-sm font-medium text-gray-700">
                      Column names <span className="font-normal text-gray-500">(only if the headers aren't recognised)</span>
                    </summary>
                    <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      <Field label="Transaction ID column"><input name="column_transaction_id" className="input mt-1" placeholder="Exact header text" /></Field>
                      <Field label="Payment date column"><input name="column_transaction_date" className="input mt-1" placeholder="Exact header text" /></Field>
                      <Field label="Student ID column"><input name="column_student_identifier" className="input mt-1" placeholder="Exact header text" /></Field>
                      <Field label="Amount column"><input name="column_amount" className="input mt-1" placeholder="Exact header text" /></Field>
                      <Field label="Invoice column" optional><input name="column_invoice_reference" className="input mt-1" placeholder="Exact header text" /></Field>
                      <Field label="Payer name column" optional><input name="column_payer_name" className="input mt-1" placeholder="Exact header text" /></Field>
                    </div>
                  </details>
                </FormSection>
                {!!providerImportWarnings.length && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    <strong>Dates with no report</strong>
                    <ul className="mt-1 list-disc pl-5">
                      {providerImportWarnings.map((warning) => <li key={warning}>{warning}</li>)}
                    </ul>
                  </div>
                )}
              </div>
              <div className="card-footer">
                <p className="mr-auto text-xs text-gray-500">Nothing is posted yet: you review the matched rows first.</p>
                <button disabled={importProviderPayments.isPending} className="btn btn-primary">
                  {importProviderPayments.isPending ? "Uploading…" : "Upload & preview"}
                </button>
              </div>
            </form>
          )}

          {!!providerImports.data?.length && (
            <div className="card">
              <div className="card-content">
                <h3 className="font-semibold">Provider import reconciliation</h3>
                <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {providerImports.data.slice(0, 12).map((batch) => (
                    <button
                      type="button"
                      key={batch.id}
                      onClick={() => setSelectedProviderImport(batch.id)}
                      className={`rounded border p-3 text-left ${selectedProviderImport === batch.id ? "border-primary-500 bg-primary-50" : "border-gray-200"}`}
                    >
                      <div className="flex justify-between gap-2">
                        <span className="font-medium">{batch.provider} · {batch.filename}</span>
                        <span className="text-xs capitalize">{batch.status}</span>
                      </div>
                      <div className="mt-1 text-xs text-gray-600">
                        {batch.matched_rows} matched · {batch.unmatched_rows} unmatched · {batch.invalid_rows} invalid · {batch.duplicate_rows} duplicate
                      </div>
                      <div className="mt-2 text-sm">
                        Provider UGX {Number(batch.provider_total).toLocaleString()} · posted UGX {Number(batch.posted_total).toLocaleString()}
                      </div>
                      <div className={`text-xs ${Number(batch.reconciliation.provider_to_system_difference) === 0 ? "text-green-700" : "text-amber-700"}`}>
                        System difference: UGX {Number(batch.reconciliation.provider_to_system_difference).toLocaleString()}
                      </div>
                      {batch.settlement_total != null && (
                        <div className={`text-xs ${Number(batch.reconciliation.provider_to_settlement_difference) === 0 ? "text-green-700" : "text-red-700"}`}>
                          Bank-settlement difference: UGX {Number(batch.reconciliation.provider_to_settlement_difference).toLocaleString()}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {selectedProviderImport && (
            <div className="card">
              <div className="card-content flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="font-semibold">Provider payment preview</h3>
                  <p className="text-sm text-gray-500">Resolve unmatched rows before posting. Invalid and duplicate rows are never posted.</p>
                </div>
                {canInitiate && providerRows.data?.some((row) => row.match_status === "matched" && row.posting_status !== "posted") && (
                  <button
                    disabled={postProviderPaymentImport.isPending}
                    className="btn btn-primary"
                    onClick={() => {
                      if (window.confirm("Post every currently matched provider payment? This creates receipts and ledger entries.")) {
                        postProviderPaymentImport.mutate(selectedProviderImport);
                      }
                    }}
                  >Post all matched rows</button>
                )}
              </div>
              <div className="divide-y">
                {providerRows.data?.map((row) => (
                  <div key={row.id} className="flex flex-wrap items-start justify-between gap-3 p-4 text-sm">
                    <div>
                      <div className="font-medium">Row {row.row_number} · {row.provider_transaction_id || "No transaction ID"}</div>
                      <div className="text-gray-600">
                        {row.transaction_date || "Invalid date"} · {row.student_identifier || "No student ID"} · UGX {Number(row.amount || 0).toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">{row.match_message}</div>
                    </div>
                    <div className="min-w-72 text-right">
                      <div className="mb-2 flex justify-end gap-2">
                        <span className="rounded bg-gray-100 px-2 py-1 text-xs capitalize">{row.match_status}</span>
                        <span className="rounded bg-gray-100 px-2 py-1 text-xs capitalize">{row.posting_status}</span>
                      </div>
                      {canInitiate && row.match_status === "unmatched" && (
                        <div className="flex items-center justify-end gap-2">
                          <select
                            className="input py-1 text-xs"
                            value={providerInvoiceMatches[row.id] || ""}
                            onChange={(event) => setProviderInvoiceMatches((current) => ({ ...current, [row.id]: event.target.value }))}
                          >
                            <option value="">Choose outstanding invoice</option>
                            {(outstandingInvoices.data?.results ?? []).filter((invoice) => Number(invoice.balance_due) >= Number(row.amount || 0)).map((invoice) => (
                              <option key={invoice.id} value={invoice.id}>{invoice.invoice_number} · {invoice.student_name} · UGX {Number(invoice.balance_due).toLocaleString()}</option>
                            ))}
                          </select>
                          <button
                            disabled={!providerInvoiceMatches[row.id]}
                            className="text-primary-700 disabled:text-gray-400"
                            onClick={() => {
                              const invoice = outstandingInvoices.data?.results.find((item) => item.id === Number(providerInvoiceMatches[row.id]));
                              if (invoice) resolveProviderPaymentRow.mutate({ id: row.id, student: invoice.student, invoice: invoice.id });
                            }}
                          >Resolve</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {!providerRows.data?.length && <div className="p-6 text-center text-sm text-gray-500">No rows in this import.</div>}
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-header pb-4">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-gray-900">Payment register</h3>
                  <p className="mt-1 text-sm text-gray-500">Open a receipt to see where the money went, download it, or request a refund.</p>
                </div>
                <ExportButtons onExport={exportPayments} disabled={!paymentCount} busy={isExportingPayments} what="every receipt matching these filters" />
              </div>
            </div>
            <div className="card-content space-y-4">
              <div className="filter-panel">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div role="group" aria-label="Period" className="segmented">
                    {(Object.keys(PAYMENT_PERIODS) as PaymentPeriod[]).map((period) => (
                      <button
                        key={period}
                        type="button"
                        aria-pressed={paymentPeriod === period}
                        onClick={() => updatePaymentFilters(periodRange(period))}
                        className="segmented-option"
                      >
                        {PAYMENT_PERIODS[period]}
                      </button>
                    ))}
                  </div>
                  {paymentFiltersActive && (
                    <button
                      type="button"
                      onClick={() => updatePaymentFilters({ ...EMPTY_PAYMENT_FILTERS, ordering: paymentRegisterFilters.ordering })}
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                    >
                      <X className="h-3.5 w-3.5" /> Clear {activePaymentFilters} {activePaymentFilters === 1 ? "filter" : "filters"}
                    </button>
                  )}
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <label className="filter-field sm:col-span-2">
                    Search
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                      <input
                        type="search"
                        value={paymentRegisterFilters.search}
                        onChange={(event) => updatePaymentFilters({ search: event.target.value })}
                        className="input-compact pl-8"
                        placeholder="Student, admission no., receipt, reference or invoice"
                      />
                    </div>
                  </label>
                  <label className="filter-field">
                    From
                    <input
                      type="date"
                      value={paymentRegisterFilters.dateFrom}
                      max={paymentRegisterFilters.dateTo || undefined}
                      onChange={(event) => updatePaymentFilters({ dateFrom: event.target.value })}
                      className="input-compact"
                    />
                  </label>
                  <label className="filter-field">
                    To
                    <input
                      type="date"
                      value={paymentRegisterFilters.dateTo}
                      min={paymentRegisterFilters.dateFrom || undefined}
                      onChange={(event) => updatePaymentFilters({ dateTo: event.target.value })}
                      className="input-compact"
                    />
                  </label>
                  <label className="filter-field">
                    Method
                    <select value={paymentRegisterFilters.method} onChange={(event) => updatePaymentFilters({ method: event.target.value })} className="input-compact">
                      <option value="">All methods</option>
                      {PAYMENT_METHODS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </label>
                  <label className="filter-field">
                    Status
                    <select value={paymentRegisterFilters.status} onChange={(event) => updatePaymentFilters({ status: event.target.value })} className="input-compact">
                      <option value="">All statuses</option>
                      {PAYMENT_STATUSES.map((status) => <option key={status} value={status}>{PAYMENT_STATUS_LABELS[status] ?? status}</option>)}
                    </select>
                  </label>
                  <label className="filter-field">
                    Paid for
                    <select value={paymentRegisterFilters.fee} onChange={(event) => updatePaymentFilters({ fee: event.target.value })} className="input-compact">
                      <option value="">Any fee</option>
                      {fees.data?.map((fee) => <option key={fee.id} value={fee.id}>{fee.name}</option>)}
                    </select>
                  </label>
                  <label className="filter-field">
                    Sort by
                    <select
                      value={paymentRegisterFilters.ordering}
                      onChange={(event) => updatePaymentFilters({ ordering: event.target.value as PaymentRegisterFilters["ordering"] })}
                      className="input-compact"
                    >
                      {Object.entries(PAYMENT_ORDERINGS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </label>
                </div>
                <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={paymentRegisterFilters.allocation === "credit"}
                    onChange={(event) => updatePaymentFilters({ allocation: event.target.checked ? "credit" : "" })}
                  />
                  Only receipts still holding student credit
                </label>
              </div>

              {payments.isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 8 }).map((_, index) => <div key={index} className="h-7 animate-pulse rounded bg-gray-100" />)}
                </div>
              ) : payments.isError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                  The payment register could not be loaded.{" "}
                  <button type="button" className="font-medium underline" onClick={() => payments.refetch()}>Try again</button>
                </div>
              ) : !visiblePayments.length ? (
                <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
                  {paymentFiltersActive ? "No payments match these filters." : "No payments have been recorded yet."}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className={`sheet-wrap ${payments.isFetching ? "opacity-60" : ""}`}>
                    <table className="sheet">
                      <thead>
                        <tr>
                          <th className="sheet-rownum">#</th>
                          <th>Date</th>
                          <th>Receipt no.</th>
                          <th>Student</th>
                          <th>Paid for</th>
                          <th>Method</th>
                          <th>Invoices</th>
                          <th className="text-right">Amount (UGX)</th>
                          <th className="text-right">Credit held</th>
                          <th>Status</th>
                          <th className="text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visiblePayments.map((payment, index) => (
                          <tr key={payment.id} className="cursor-pointer" onClick={() => setViewingPaymentId(payment.id)}>
                            <td className="sheet-rownum">{(paymentPage - 1) * paymentPageSize + index + 1}</td>
                            <td className="tabular-nums text-gray-700">{paymentDay(payment) || "—"}</td>
                            <td className="font-mono text-xs text-gray-700">{payment.receipt_number || <span className="font-sans text-gray-300">—</span>}</td>
                            <td className="max-w-[14rem] truncate font-medium text-gray-900">{payment.student_name}</td>
                            <td className="max-w-[12rem] truncate text-gray-700">{payment.fee_name || "PRN receipt"}</td>
                            <td className="text-gray-700">{paymentMethodLabel(payment.payment_method)}</td>
                            <td className="max-w-[10rem] truncate font-mono text-xs text-gray-600">
                              {paymentInvoices(payment) || <span className="font-sans text-amber-700">Held as credit</span>}
                            </td>
                            <td className="text-right font-semibold tabular-nums text-gray-900">{Number(payment.amount).toLocaleString()}</td>
                            <td className={`text-right tabular-nums ${Number(payment.unallocated_amount) > 0 ? "font-medium text-amber-700" : "text-gray-300"}`}>
                              {Number(payment.unallocated_amount) > 0 ? Number(payment.unallocated_amount).toLocaleString() : "—"}
                            </td>
                            <td>
                              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700">
                                <span className={`h-1.5 w-1.5 rounded-full ${PAYMENT_STATUS_DOTS[payment.payment_status] ?? "bg-gray-400"}`} />
                                {paymentStatusLabel(payment.payment_status)}
                              </span>
                            </td>
                            <td className="text-right">
                              <span className="inline-flex gap-1">
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setViewingPaymentId(payment.id);
                                  }}
                                  className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-primary-700 hover:bg-primary-50"
                                >
                                  <Eye className="h-3.5 w-3.5" /> Open
                                </button>
                                {canDownloadReceipt(payment) && (
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      financialService.downloadPaymentReceipt(payment.id);
                                    }}
                                    className="inline-flex items-center rounded px-1.5 py-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                                    title="Download receipt"
                                    aria-label={`Download receipt ${payment.receipt_number ?? ""}`.trim()}
                                  >
                                    <Download className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pager
                    first={(paymentPage - 1) * paymentPageSize + 1}
                    shown={visiblePayments.length}
                    total={paymentCount}
                    noun={paymentCount === 1 ? "receipt" : "receipts"}
                    page={paymentPage}
                    pageCount={paymentPageCount}
                    onPage={setPaymentPage}
                    pageSize={paymentRegisterPageSize}
                    pageSizes={PAYMENT_PAGE_SIZES}
                    onPageSize={(size) => {
                      setPaymentRegisterPageSize(size);
                      setPaymentPage(1);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <PaymentDetailsModal
            payment={viewingPayment}
            onClose={() => {
              setViewingPaymentId(null);
              setRefundFormFor(null);
            }}
            refundPending={viewingPayment ? refundPendingFor(viewingPayment) : false}
            onDownloadReceipt={viewingPayment && canDownloadReceipt(viewingPayment) ? () => financialService.downloadPaymentReceipt(viewingPayment.id) : undefined}
            onRequestRefund={viewingPayment && canRequestRefund(viewingPayment) && refundFormFor !== viewingPayment.id ? () => requestPaymentRefund(viewingPayment) : undefined}
            onReallocate={viewingPayment && canApprove ? (allocation) => { void moveAllocation(viewingPayment, allocation); } : undefined}
          >
            {viewingPayment && refundFormFor === viewingPayment.id && canRequestRefund(viewingPayment) && (
              <RefundRequestForm
                key={viewingPayment.id}
                payment={viewingPayment}
                pending={requestRefund.isPending}
                onCancel={() => setRefundFormFor(null)}
                onSubmit={({ amount, reason }) =>
                  requestRefund.mutate({ id: viewingPayment.id, amount, reason }, { onSuccess: () => setRefundFormFor(null) })
                }
              />
            )}
          </PaymentDetailsModal>
        </div>
      )}

      {activeTab === "refunds" && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-primary-100 bg-primary-50 p-5"><div><h2 className="font-semibold text-primary-950">Refund governance</h2><p className="mt-1 max-w-2xl text-sm text-primary-800">Refunds are requested from Payments or service tabs, then reviewed by a different authorised leader before money and ledger balances change.</p></div><div className="flex items-center gap-3 text-sm"><span className="rounded-md bg-white px-3 py-2 font-medium text-gray-700">{allRefunds.length} requests</span><span className="rounded-md bg-white px-3 py-2 font-medium text-amber-700">{allRefunds.filter((refund) => refund.status === "pending").length} awaiting decision</span></div></div>
          <div className="card">
            <div className="card-header pb-4">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-gray-900">Refund register</h3>
                  <p className="mt-1 text-sm text-gray-500">Open a request to read it in full and decide it. New requests start from a receipt on the Payments tab.</p>
                </div>
                <ExportButtons onExport={exportRefunds} disabled={!filteredRefunds.length} what="the filtered refund requests" />
              </div>
            </div>
            <div className="card-content space-y-4">
              <div className="filter-panel">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div role="group" aria-label="Status" className="segmented">
                    {(["", ...REFUND_STATUSES] as const).map((status) => (
                      <button
                        key={status || "all"}
                        type="button"
                        aria-pressed={refundFilters.status === status}
                        onClick={() => updateRefundFilters({ status })}
                        className="segmented-option"
                      >
                        {status ? REFUND_STATUS_LABELS[status] : "All"}
                        <span className="ml-1.5 tabular-nums opacity-70">{refundCounts[status || "all"]}</span>
                      </button>
                    ))}
                  </div>
                  {activeRefundFilters > 0 && (
                    <button
                      type="button"
                      onClick={() => updateRefundFilters({ ...EMPTY_REFUND_FILTERS, status: refundFilters.status, ordering: refundFilters.ordering })}
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                    >
                      <X className="h-3.5 w-3.5" /> Clear {activeRefundFilters} {activeRefundFilters === 1 ? "filter" : "filters"}
                    </button>
                  )}
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <label className="filter-field sm:col-span-2 lg:col-span-1">
                    Search
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                      <input
                        type="search"
                        value={refundFilters.search}
                        onChange={(event) => updateRefundFilters({ search: event.target.value })}
                        className="input-compact pl-8"
                        placeholder="Student, request, receipt or reason"
                      />
                    </div>
                  </label>
                  <label className="filter-field">
                    Requested from
                    <input type="date" value={refundFilters.requestedFrom} max={refundFilters.requestedTo || undefined} onChange={(event) => updateRefundFilters({ requestedFrom: event.target.value })} className="input-compact" />
                  </label>
                  <label className="filter-field">
                    Requested to
                    <input type="date" value={refundFilters.requestedTo} min={refundFilters.requestedFrom || undefined} onChange={(event) => updateRefundFilters({ requestedTo: event.target.value })} className="input-compact" />
                  </label>
                  <label className="filter-field">
                    Sort by
                    <select
                      value={refundFilters.ordering}
                      onChange={(event) => updateRefundFilters({ ordering: event.target.value as RefundFilters["ordering"] })}
                      className="input-compact"
                    >
                      {Object.entries(REFUND_ORDERINGS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </label>
                </div>
                {canApprove && (
                  <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={refundFilters.awaitingMe}
                      onChange={(event) => updateRefundFilters({ awaitingMe: event.target.checked })}
                    />
                    Only requests waiting for my decision
                    <span className="rounded-full bg-amber-50 px-1.5 text-[11px] font-semibold text-amber-700">{awaitingMyDecision}</span>
                  </label>
                )}
              </div>

              {refundRequests.isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 6 }).map((_, index) => <div key={index} className="h-7 animate-pulse rounded bg-gray-100" />)}
                </div>
              ) : refundRequests.isError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                  The refund requests could not be loaded.{" "}
                  <button type="button" className="font-medium underline" onClick={() => refundRequests.refetch()}>Try again</button>
                </div>
              ) : !filteredRefunds.length ? (
                <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
                  {allRefunds.length ? "No refund requests match these filters." : "No refund requests have been submitted."}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="sheet-wrap">
                    <table className="sheet">
                      <thead>
                        <tr>
                          <th className="sheet-rownum">#</th>
                          <th>Request no.</th>
                          <th>Requested</th>
                          <th>Student</th>
                          <th>Receipt</th>
                          <th>Reason</th>
                          <th className="text-right">Amount (UGX)</th>
                          <th>Requested by</th>
                          <th>Status</th>
                          <th className="text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {refundPageRows.rows.map((item, index) => {
                          const mine = item.requested_by === user?.id;
                          return (
                            <tr key={item.id} className="cursor-pointer" onClick={() => openRefund(item.id)}>
                              <td className="sheet-rownum">{refundPageRows.first + index}</td>
                              <td className="font-mono text-xs text-gray-800">{item.request_number}</td>
                              <td className="tabular-nums text-gray-600">{refundRequestedOn(item) || "—"}</td>
                              <td className="max-w-[12rem] truncate font-medium text-gray-900">{item.student_name}</td>
                              <td className="font-mono text-xs text-gray-600">{item.receipt_reference || `#${item.receipt}`}</td>
                              <td className="max-w-[16rem] truncate text-gray-600">{item.reason}</td>
                              <td className="text-right font-semibold tabular-nums text-gray-900">{Number(item.amount).toLocaleString()}</td>
                              <td className="text-gray-600">{mine ? "You" : item.requested_by_name}</td>
                              <td>
                                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700">
                                  <span className={`h-1.5 w-1.5 rounded-full ${REFUND_STATUS_DOTS[item.status] ?? "bg-gray-400"}`} />
                                  {refundStatusLabel(item.status)}
                                </span>
                              </td>
                              <td className="text-right">
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openRefund(item.id);
                                  }}
                                  className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-primary-700 hover:bg-primary-50"
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                  {item.status === "pending" && canApprove && !mine ? "Review" : "Open"}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <Pager
                    first={refundPageRows.first}
                    shown={refundPageRows.rows.length}
                    total={filteredRefunds.length}
                    noun={filteredRefunds.length === 1 ? "request" : "requests"}
                    page={refundPageRows.page}
                    pageCount={refundPageRows.pageCount}
                    onPage={setRefundPage}
                    pageSize={refundPageSize}
                    pageSizes={REFUND_PAGE_SIZES}
                    onPageSize={(size) => {
                      setRefundPageSize(size);
                      setRefundPage(1);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          {viewingRefund && (() => {
            const item = viewingRefund;
            const pending = item.status === "pending";
            const mine = item.requested_by === user?.id;
            const canDecide = pending && canApprove && !mine;
            const deciding = approveRefund.isPending || rejectRefund.isPending;
            return (
              <RefundDetailsModal
                refund={item}
                onClose={closeRefund}
                footer={
                  <>
                    {pending && mine && (
                      <button
                        type="button"
                        disabled={cancelRefund.isPending}
                        onClick={() => {
                          if (window.confirm(`Withdraw ${item.request_number}? The receipt stays as it is.`)) {
                            cancelRefund.mutate(item.id, { onSuccess: closeRefund });
                          }
                        }}
                        className="mr-auto inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                      >
                        <X className="h-4 w-4" /> Withdraw request
                      </button>
                    )}
                    <button type="button" onClick={closeRefund} className="btn-outline">Close</button>
                  </>
                }
              >
                {pending && canApprove && mine && (
                  <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">
                    You raised this request, so another approver has to decide it.
                  </p>
                )}
                {canDecide && (
                  <section aria-labelledby="refund-decide-heading" className="space-y-3 rounded-lg border border-gray-200 p-4">
                    <div>
                      <h4 id="refund-decide-heading" className="text-sm font-semibold text-gray-900">Your decision</h4>
                      <p className="mt-0.5 text-xs text-gray-500">
                        Approving returns UGX {Number(item.amount).toLocaleString()} to {item.student_name} and posts it to the ledger. It cannot be undone.
                      </p>
                    </div>
                    <Field as="div" label="Decision notes" hint="Kept on the request. Needed to reject, so the requester knows why.">
                      <AIAssistField
                        rows={2}
                        value={refundDecisionNotes}
                        onChange={(next) => setRefundDecisionNotes(next)}
                        placeholder="e.g. No proof of the second payment attached."
                        context={{
                          key: "financial.approval_note",
                          params: { document_type: "financial.refundrequest", id: item.id, purpose: "reject" },
                        }}
                        draftLabel="Draft a reason"
                        draftHint="Reads this request. Jot rough notes first for a better draft."
                      />
                    </Field>
                    <div className="flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        disabled={deciding || !refundDecisionNotes.trim()}
                        title={refundDecisionNotes.trim() ? undefined : "Write why before rejecting"}
                        onClick={() => rejectRefund.mutate({ id: item.id, notes: refundDecisionNotes.trim() }, { onSuccess: closeRefund })}
                        className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        Reject
                      </button>
                      <button
                        type="button"
                        disabled={deciding}
                        onClick={() => {
                          if (window.confirm(`Approve ${item.request_number} and refund UGX ${Number(item.amount).toLocaleString()} to ${item.student_name}?`)) {
                            approveRefund.mutate({ id: item.id, notes: refundDecisionNotes.trim() }, { onSuccess: closeRefund });
                          }
                        }}
                        className="btn btn-primary"
                      >
                        Approve refund
                      </button>
                    </div>
                  </section>
                )}
              </RefundDetailsModal>
            );
          })()}
        </div>
      )}

      {activeTab === "credits" && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-primary-100 bg-primary-50 p-5">
            <div><h2 className="font-semibold text-primary-950">Credit note governance</h2><p className="mt-1 max-w-2xl text-sm text-primary-800">Prepare credit notes against outstanding invoices. A separate authorised reviewer must approve before the student ledger is adjusted.</p></div>
            <div className="flex items-center gap-3 text-sm"><span className="rounded-md bg-white px-3 py-2 font-medium text-gray-700">{allCreditNotes.length} notes</span><span className="rounded-md bg-white px-3 py-2 font-medium text-amber-700">{allCreditNotes.filter((note) => ["draft", "submitted", "verified", "returned"].includes(creditNoteStage(note))).length} not yet approved</span></div>
          </div>
          {canInitiate && (showCreditForm || editingCreditNote) && <form key={editingCreditNoteId ?? "new"} onSubmit={submitCreditNote} className="card">
            <div className="card-titlebar">
              <div>
                <h3 className="font-semibold text-gray-900">{editingCreditNote ? `Edit ${editingCreditNote.credit_number}` : "Prepare credit note"}</h3>
                <p className="mt-1 text-sm text-gray-500">Reduce what a student owes on one invoice. It changes nothing until another person reviews and approves it.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setEditingCreditNoteId(null);
                  setShowCreditForm(false);
                }}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-label="Close the credit note form"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="card-content space-y-5 pt-5">
              {editingCreditNote && (
                <EditingNotice
                  label={editingCreditNote.credit_number}
                  reason={editingCreditNote.workflow_status === "returned" ? editingCreditNote.return_reason : undefined}
                  onCancel={() => setEditingCreditNoteId(null)}
                />
              )}
              <FormSection title="Credit" divided={false} columns={3}>
                <CreditNoteFields editing={editingCreditNote} invoices={outstandingInvoices.data?.results ?? []} />
              </FormSection>
            </div>
            <div className="card-footer">
              <p className="mr-auto text-xs text-gray-500">Saved as a draft. Submit it for review from the register.</p>
              <button disabled={createCreditNote.isPending || updateCreditNote.isPending} className="btn btn-primary">
                {editingCreditNoteId === null ? "Prepare credit note" : "Save changes"}
              </button>
            </div>
          </form>}
          <div className="card">
            <div className="card-header pb-4">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-gray-900">Credit note register</h3>
                  <p className="mt-1 text-sm text-gray-500">Open a note to follow its approval and act on it.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <ExportButtons onExport={exportCreditNotes} disabled={!filteredCreditNotes.length} what="the filtered credit notes" />
                  {canInitiate && !showCreditForm && !editingCreditNote && (
                    <button type="button" onClick={() => setShowCreditForm(true)} className="btn-primary h-8 min-h-8 px-3 text-xs">
                      <Plus className="mr-1.5 h-3.5 w-3.5" /> Prepare credit note
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="card-content space-y-4">
              <div className="filter-panel">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div role="group" aria-label="Stage" className="segmented">
                    {(Object.keys(CREDIT_VIEWS) as CreditView[]).map((view) => (
                      <button
                        key={view}
                        type="button"
                        aria-pressed={creditFilters.view === view}
                        onClick={() => updateCreditFilters({ view })}
                        className="segmented-option"
                      >
                        {CREDIT_VIEWS[view].label}
                        <span className="ml-1.5 tabular-nums opacity-70">{creditCounts[view]}</span>
                      </button>
                    ))}
                  </div>
                  {activeCreditFilters > 0 && (
                    <button
                      type="button"
                      onClick={() => updateCreditFilters({ ...EMPTY_CREDIT_FILTERS, view: creditFilters.view, ordering: creditFilters.ordering })}
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                    >
                      <X className="h-3.5 w-3.5" /> Clear {activeCreditFilters} {activeCreditFilters === 1 ? "filter" : "filters"}
                    </button>
                  )}
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <label className="filter-field sm:col-span-2 lg:col-span-1">
                    Search
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                      <input
                        type="search"
                        value={creditFilters.search}
                        onChange={(event) => updateCreditFilters({ search: event.target.value })}
                        className="input-compact pl-8"
                        placeholder="Credit note, invoice, student or reason"
                      />
                    </div>
                  </label>
                  <label className="filter-field">
                    Prepared from
                    <input type="date" value={creditFilters.preparedFrom} max={creditFilters.preparedTo || undefined} onChange={(event) => updateCreditFilters({ preparedFrom: event.target.value })} className="input-compact" />
                  </label>
                  <label className="filter-field">
                    Prepared to
                    <input type="date" value={creditFilters.preparedTo} min={creditFilters.preparedFrom || undefined} onChange={(event) => updateCreditFilters({ preparedTo: event.target.value })} className="input-compact" />
                  </label>
                  <label className="filter-field">
                    Sort by
                    <select
                      value={creditFilters.ordering}
                      onChange={(event) => updateCreditFilters({ ordering: event.target.value as CreditNoteFilters["ordering"] })}
                      className="input-compact"
                    >
                      {Object.entries(CREDIT_ORDERINGS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </label>
                </div>
                {(canReview || canApprove) && (
                  <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={creditFilters.needsMe}
                      onChange={(event) => updateCreditFilters({ needsMe: event.target.checked })}
                    />
                    Only notes waiting for me to review or approve
                    <span className="rounded-full bg-amber-50 px-1.5 text-[11px] font-semibold text-amber-700">{creditsNeedingMe}</span>
                  </label>
                )}
              </div>

              {creditNotes.isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 6 }).map((_, index) => <div key={index} className="h-7 animate-pulse rounded bg-gray-100" />)}
                </div>
              ) : creditNotes.isError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                  The credit notes could not be loaded.{" "}
                  <button type="button" className="font-medium underline" onClick={() => creditNotes.refetch()}>Try again</button>
                </div>
              ) : !filteredCreditNotes.length ? (
                <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
                  {allCreditNotes.length ? "No credit notes match these filters." : "No credit notes have been prepared yet."}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="sheet-wrap">
                    <table className="sheet">
                      <thead>
                        <tr>
                          <th className="sheet-rownum">#</th>
                          <th>Credit note</th>
                          <th>Prepared</th>
                          <th>Student</th>
                          <th>Invoice</th>
                          <th>Reason</th>
                          <th className="text-right">Amount (UGX)</th>
                          <th>Prepared by</th>
                          <th>Stage</th>
                          <th className="text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {creditPageRows.rows.map((item, index) => (
                          <tr key={item.id} className="cursor-pointer" onClick={() => openCredit(item.id)}>
                            <td className="sheet-rownum">{creditPageRows.first + index}</td>
                            <td className="font-mono text-xs text-gray-800">{item.credit_number}</td>
                            <td className="tabular-nums text-gray-600">{creditPreparedOn(item) || "—"}</td>
                            <td className="max-w-[12rem] truncate font-medium text-gray-900">{item.student_name}</td>
                            <td className="font-mono text-xs text-gray-600">{item.invoice_number}</td>
                            <td className="max-w-[16rem] truncate text-gray-600">{item.reason}</td>
                            <td className="text-right font-semibold tabular-nums text-gray-900">{Number(item.amount).toLocaleString()}</td>
                            <td className="text-gray-600">{item.prepared_by === user?.id ? "You" : item.prepared_by_name}</td>
                            <td>
                              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700">
                                <span className={`h-1.5 w-1.5 rounded-full ${CREDIT_STAGE_DOTS[creditNoteStage(item)] ?? "bg-gray-400"}`} />
                                {creditStageLabel(item)}
                              </span>
                            </td>
                            <td className="text-right">
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openCredit(item.id);
                                }}
                                className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-primary-700 hover:bg-primary-50"
                              >
                                <Eye className="h-3.5 w-3.5" />
                                {creditNeedsActor(item, creditActor) ? "Review" : "Open"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pager
                    first={creditPageRows.first}
                    shown={creditPageRows.rows.length}
                    total={filteredCreditNotes.length}
                    noun={filteredCreditNotes.length === 1 ? "credit note" : "credit notes"}
                    page={creditPageRows.page}
                    pageCount={creditPageRows.pageCount}
                    onPage={setCreditPage}
                    pageSize={creditPageSize}
                    pageSizes={CREDIT_PAGE_SIZES}
                    onPageSize={(size) => {
                      setCreditPageSize(size);
                      setCreditPage(1);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          {viewingCredit && (() => {
            const item = viewingCredit;
            const stage = creditNoteStage(item);
            const mine = item.prepared_by === user?.id;
            const inReview = ["submitted", "verified"].includes(stage);
            const canEdit = myUnsubmitted(item, item.prepared_by);
            const canSubmit = canInitiate && ["draft", "returned"].includes(stage);
            const canVerify = canReview && stage === "submitted" && !mine;
            const canApproveThis = canApprove && stage === "verified" && !mine;
            const canSendBack = canReview && inReview && !mine;
            const canRejectThis = inReview && !mine && (canReview || canApprove);
            const busy = verifyCreditNoteRequest.isPending || approveCreditNote.isPending || returnCreditNote.isPending || rejectCreditNote.isPending;
            const notes = creditDecisionNotes.trim();
            return (
              <CreditNoteDetailsModal
                note={item}
                onClose={closeCredit}
                footer={
                  <>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => {
                          closeCredit();
                          setEditingCreditNoteId(item.id);
                          scrollPageToTop();
                        }}
                        className="mr-auto inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50"
                      >
                        <Edit className="h-4 w-4" /> Edit
                      </button>
                    )}
                    <button type="button" onClick={closeCredit} className="btn-outline">Close</button>
                    {canSubmit && (
                      <button
                        type="button"
                        disabled={submitCreditNoteForApproval.isPending}
                        onClick={() => submitCreditNoteForApproval.mutate(item.id)}
                        className="btn btn-primary"
                      >
                        Submit for review
                      </button>
                    )}
                  </>
                }
              >
                {inReview && mine && (
                  <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">
                    You prepared this note, so someone else reviews and approves it.
                  </p>
                )}
                {(canVerify || canApproveThis || canSendBack || canRejectThis) && (
                  <section aria-labelledby="credit-decide-heading" className="space-y-3 rounded-lg border border-gray-200 p-4">
                    <div>
                      <h4 id="credit-decide-heading" className="text-sm font-semibold text-gray-900">
                        {canApproveThis ? "Approve this credit" : "Review this credit"}
                      </h4>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {canApproveThis
                          ? `Approving takes UGX ${Number(item.amount).toLocaleString()} off ${item.invoice_number} and posts it to the ledger.`
                          : "Mark it reviewed to pass it to an approver, or send it back if something needs to change."}
                      </p>
                    </div>
                    <Field as="div" label="Comments" hint="Needed to send it back or reject it, so the preparer knows why.">
                      <AIAssistField
                        rows={2}
                        value={creditDecisionNotes}
                        onChange={(next) => setCreditDecisionNotes(next)}
                        placeholder={canApproveThis ? "e.g. Scholarship letter not attached." : "e.g. Amount should be 50,000, not 55,000."}
                        context={{
                          key: "financial.approval_note",
                          // A reviewer mostly sends a note back; at approval it is a reason to reject.
                          params: { document_type: "financial.creditnote", id: item.id, purpose: canApproveThis ? "reject" : "return" },
                        }}
                        draftLabel={canApproveThis ? "Draft a reason" : "Draft what to change"}
                        draftHint="Reads this credit note. Jot rough notes first for a better draft."
                      />
                    </Field>
                    <div className="flex flex-wrap justify-end gap-2">
                      {canRejectThis && (
                        <button
                          type="button"
                          disabled={busy || !notes}
                          title={notes ? undefined : "Write why before rejecting"}
                          onClick={() => rejectCreditNote.mutate({ id: item.id, reason: notes }, { onSuccess: closeCredit })}
                          className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                        >
                          Reject
                        </button>
                      )}
                      {canSendBack && (
                        <button
                          type="button"
                          disabled={busy || !notes}
                          title={notes ? undefined : "Write what needs to change"}
                          onClick={() => returnCreditNote.mutate({ id: item.id, reason: notes }, { onSuccess: closeCredit })}
                          className="rounded-md border border-amber-200 px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-50 disabled:opacity-50"
                        >
                          Send back
                        </button>
                      )}
                      {canVerify && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => verifyCreditNoteRequest.mutate({ id: item.id }, { onSuccess: closeCredit })}
                          className="btn btn-primary"
                        >
                          Mark reviewed
                        </button>
                      )}
                      {canApproveThis && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => {
                            if (window.confirm(`Approve ${item.credit_number} and take UGX ${Number(item.amount).toLocaleString()} off ${item.invoice_number}?`)) {
                              approveCreditNote.mutate(item.id, { onSuccess: closeCredit });
                            }
                          }}
                          className="btn btn-primary"
                        >
                          Approve credit
                        </button>
                      )}
                    </div>
                  </section>
                )}
              </CreditNoteDetailsModal>
            );
          })()}
        </div>
      )}

      {activeTab === "transport" && (() => {
        const charge = transportCharges.data?.find((row) => row.id === viewingTransportId) ?? null;
        const charges = transportCharges.data ?? [];
        return (
        <div className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-primary-100 bg-primary-50 p-5">
            <div><h2 className="font-semibold text-primary-950">Transport charges</h2><p className="mt-1 max-w-2xl text-sm text-primary-800">Bill students on school transport from their route allocation, then record their payments.</p></div>
            <div className="flex items-center gap-3 text-sm"><span className="rounded-md bg-white px-3 py-2 font-medium text-gray-700">{charges.length} charges</span><span className="rounded-md bg-white px-3 py-2 font-medium text-amber-700">{charges.filter(chargeIsOpen).length} awaiting payment</span></div>
          </div>
          {canInitiate && showTransportForm && <form onSubmit={submitTransportCharge} className="card">
            <div className="card-titlebar">
              <div>
                <h3 className="font-semibold text-gray-900">New transport charge</h3>
                <p className="mt-1 text-sm text-gray-500">Issues an invoice for a student's transport allocation.</p>
              </div>
              <button type="button" onClick={() => setShowTransportForm(false)} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close the new transport charge form">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="card-content space-y-5 pt-5">
              <FormSection title="Charge" divided={false} columns={4}>
                <Field label="Student and route" className="sm:col-span-2" hint="Active allocations with something still to bill.">
                  <select
                    required
                    name="allocation"
                    className="input mt-1"
                    onChange={(event) => {
                      const form = event.currentTarget.form!;
                      const allocation = transportAllocations.data?.find((a) => a.id === Number(event.currentTarget.value));
                      (form.elements.namedItem("amount") as HTMLInputElement).value = allocation?.unbilled_amount ?? "";
                    }}
                  >
                    <option value="">Choose an allocation</option>
                    {transportAllocations.data
                      ?.filter((allocation) => allocation.status === 'active' && Number(allocation.unbilled_amount) > 0)
                      .map((allocation) => (
                        <option key={allocation.id} value={allocation.id}>
                          {allocation.student_name} · {allocation.route_name} · UGX {Number(allocation.unbilled_amount).toLocaleString()} unbilled
                        </option>
                      ))}
                  </select>
                </Field>
                <Field label="Amount (UGX)" hint="Filled with the unbilled balance. Lower it for a part charge.">
                  <input required min="1" type="number" name="amount" className="input mt-1 tabular-nums" />
                </Field>
                <Field label="Due date">
                  <input required type="date" name="due" className="input mt-1" />
                </Field>
                <Field label="Billing period or description" optional className="sm:col-span-2 lg:col-span-4">
                  <textarea name="notes" rows={2} className="input mt-1 h-auto min-h-[3.5rem] resize-y py-2 leading-5" placeholder="e.g. Term 3 transport, Route A" />
                </Field>
              </FormSection>
            </div>
            <div className="card-footer">
              <p className="mr-auto text-xs text-gray-500">The invoice is issued at once and appears on the student's account.</p>
              <button disabled={createTransportCharge.isPending} className="btn btn-primary">Issue transport invoice</button>
            </div>
          </form>}
          <ClientRegister
            title="Transport charge register"
            description="Open a charge to record its payment or cancel it."
            actions={canInitiate && !showTransportForm && (
              <button type="button" onClick={() => setShowTransportForm(true)} className="btn-primary h-8 min-h-8 px-3 text-xs">
                <Plus className="mr-1.5 h-3.5 w-3.5" /> New charge
              </button>
            )}
            rows={transportCharges.data}
            config={transportConfig}
            columns={transportColumns}
            rowKey={(row) => row.id}
            onOpen={(row) => setViewingTransportId(row.id)}
            openLabel={(row) => (canInitiate && chargeIsOpen(row) ? "Record payment" : "Open")}
            noun={["charge", "charges"]}
            exportName={{ file: "transport-charges", title: "Transport Charges" }}
            isLoading={transportCharges.isLoading}
            isError={transportCharges.isError}
            onRetry={() => transportCharges.refetch()}
            emptyText="No transport charges have been issued yet."
          />
          {charge && (
            <DetailsModal
              title={`Transport charge · ${charge.student_name}`}
              headline={{ label: "Amount", value: `UGX ${Number(charge.amount).toLocaleString()}`, note: charge.route_name }}
              status={chargeStatus(charge)}
              facts={[
                ["Student", `${charge.student_name}${charge.student_id ? ` · ${charge.student_id}` : ""}`],
                ["Route", charge.route_name],
                ["Invoice", charge.invoice_number],
                ["Due", <>{charge.due_date}{chargeIsOverdue(charge) && <span className="ml-1.5 text-red-600">· overdue</span>}</>],
                ["Paid on", charge.paid_date?.slice(0, 10)],
                ["Method", charge.payment_method ? humanise(charge.payment_method) : ""],
                ["Reference", charge.reference_number],
                ["Receipt no.", charge.receipt_number],
                ["Issued by", charge.created_by_name],
                ["Processed by", charge.processed_by_name],
              ]}
              sections={[
                { title: "Billing period or description", body: charge.notes },
                { title: "Cancelled", body: charge.cancellation_reason, tone: "danger" },
              ]}
              onClose={() => setViewingTransportId(null)}
            >
              {canInitiate && chargeIsOpen(charge) && (
                <>
                  <ActionPanel title="Record payment" description={`Records the full UGX ${Number(charge.amount).toLocaleString()} against this charge's invoice.`}>
                    <form onSubmit={(event) => submitTransportPayment(event, charge.id)} className="grid gap-4 sm:grid-cols-3">
                      <PaymentMethodField />
                      <Field label="Reference" optional hint="The bank slip, mobile money or cheque number.">
                          <input name="reference" className="input mt-1" />
                        </Field>
                      <Field label="Receipt no." optional hint="Leave blank for the system to number it.">
                        <input name="receipt" className="input mt-1" />
                      </Field>
                      <div className="flex justify-end sm:col-span-3">
                        <button disabled={processTransportPayment.isPending} className="btn btn-primary">Record full payment</button>
                      </div>
                    </form>
                  </ActionPanel>
                  <ActionPanel title="Cancel this charge" description="For a charge issued in error. Its invoice is cancelled with it.">
                    <form onSubmit={(event) => submitTransportCancellation(event, charge.id)} className="space-y-3">
                      <Field label="Reason">
                        <textarea required name="reason" rows={2} className="input mt-1 h-auto min-h-[3.5rem] resize-y py-2 leading-5" />
                      </Field>
                      <div className="flex justify-end">
                        <button disabled={cancelTransportCharge.isPending} className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50">Cancel charge</button>
                      </div>
                    </form>
                  </ActionPanel>
                </>
              )}
            </DetailsModal>
          )}
        </div>
        );
      })()}

      {activeTab === "boarding" && (() => {
        const charge = hostelCharges.data?.find((row) => row.id === viewingHostelId) ?? null;
        const charges = hostelCharges.data ?? [];
        return (
        <div className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-primary-100 bg-primary-50 p-5">
            <div><h2 className="font-semibold text-primary-950">Boarding charges</h2><p className="mt-1 max-w-2xl text-sm text-primary-800">Bill boarders the configured hostel fees, then record their payments.</p></div>
            <div className="flex items-center gap-3 text-sm"><span className="rounded-md bg-white px-3 py-2 font-medium text-gray-700">{charges.length} charges</span><span className="rounded-md bg-white px-3 py-2 font-medium text-amber-700">{charges.filter(chargeIsOpen).length} awaiting payment</span></div>
          </div>
          {canInitiate && showHostelForm && <form onSubmit={submitHostelCharge} className="card">
            <div className="card-titlebar">
              <div>
                <h3 className="font-semibold text-gray-900">New boarding charge</h3>
                <p className="mt-1 text-sm text-gray-500">Issues an invoice for a boarder at a configured hostel fee.</p>
              </div>
              <button type="button" onClick={() => setShowHostelForm(false)} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close the new boarding charge form">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="card-content space-y-5 pt-5">
              <FormSection title="Charge" divided={false} columns={4}>
                <Field label="Boarder" className="sm:col-span-2">
                  <select required name="allocation" className="input mt-1">
                    <option value="">Choose an active allocation</option>
                    {hostelAllocations.data
                      ?.filter((allocation) => allocation.status === 'active')
                      .map((allocation) => (
                        <option key={allocation.id} value={allocation.id}>
                          {allocation.student_name} · {allocation.hostel_name} · Room {allocation.room_number}
                        </option>
                      ))}
                  </select>
                </Field>
                <Field label="Boarding fee" className="sm:col-span-2" hint="Sets the amount below.">
                  <select
                    required
                    name="fee"
                    className="input mt-1"
                    onChange={(event) => {
                      const form = event.currentTarget.form!;
                      const fee = hostelFees.data?.find((entry) => entry.id === Number(event.currentTarget.value));
                      (form.elements.namedItem("amount") as HTMLInputElement).value = fee?.amount ?? "";
                    }}
                  >
                    <option value="">Choose a configured fee</option>
                    {hostelFees.data?.filter((fee) => fee.is_active).map((fee) => (
                      <option key={fee.id} value={fee.id}>
                        {fee.hostel_name} · {fee.name} · UGX {Number(fee.amount).toLocaleString()}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Amount (UGX)">
                  <input required min="1" type="number" name="amount" className="input mt-1 tabular-nums" />
                </Field>
                <Field label="Due date">
                  <input required type="date" name="due" className="input mt-1" />
                </Field>
                <Field label="Billing period or notes" optional className="sm:col-span-2">
                  <input name="notes" className="input mt-1" placeholder="e.g. Term 3 boarding" />
                </Field>
              </FormSection>
            </div>
            <div className="card-footer">
              <p className="mr-auto text-xs text-gray-500">The invoice is issued at once and appears on the student's account.</p>
              <button disabled={createHostelCharge.isPending} className="btn btn-primary">Issue boarding invoice</button>
            </div>
          </form>}
          <ClientRegister
            title="Boarding charge register"
            description="Open a charge to record its payment or cancel it."
            actions={canInitiate && !showHostelForm && (
              <button type="button" onClick={() => setShowHostelForm(true)} className="btn-primary h-8 min-h-8 px-3 text-xs">
                <Plus className="mr-1.5 h-3.5 w-3.5" /> New charge
              </button>
            )}
            rows={hostelCharges.data}
            config={boardingConfig}
            columns={boardingColumns}
            rowKey={(row) => row.id}
            onOpen={(row) => setViewingHostelId(row.id)}
            openLabel={(row) => (canInitiate && chargeIsOpen(row) ? "Record payment" : "Open")}
            noun={["charge", "charges"]}
            exportName={{ file: "boarding-charges", title: "Boarding Charges" }}
            isLoading={hostelCharges.isLoading}
            isError={hostelCharges.isError}
            onRetry={() => hostelCharges.refetch()}
            emptyText="No boarding charges have been issued yet."
          />
          {charge && (
            <DetailsModal
              title={`Boarding charge · ${charge.student_name}`}
              headline={{ label: "Amount", value: `UGX ${Number(charge.amount).toLocaleString()}`, note: `${charge.fee_name} · ${charge.hostel_name}` }}
              status={chargeStatus(charge)}
              facts={[
                ["Student", `${charge.student_name}${charge.student_id ? ` · ${charge.student_id}` : ""}`],
                ["Hostel", charge.hostel_name],
                ["Fee", charge.fee_name],
                ["Invoice", charge.invoice_number],
                ["Due", <>{charge.due_date}{chargeIsOverdue(charge) && <span className="ml-1.5 text-red-600">· overdue</span>}</>],
                ["Paid on", charge.paid_date?.slice(0, 10)],
                ["Method", charge.payment_method ? humanise(charge.payment_method) : ""],
                ["Reference", charge.reference_number],
                ["Receipt no.", charge.receipt_number],
                ["Issued by", charge.created_by_name],
              ]}
              sections={[
                { title: "Billing period or notes", body: charge.notes },
                { title: "Cancelled", body: charge.cancellation_reason, tone: "danger" },
              ]}
              onClose={() => setViewingHostelId(null)}
            >
              {canInitiate && chargeIsOpen(charge) && (
                <>
                  <ActionPanel title="Record payment" description={`Records the full UGX ${Number(charge.amount).toLocaleString()} against this charge's invoice.`}>
                    <form onSubmit={(event) => submitHostelPayment(event, charge.id)} className="grid gap-4 sm:grid-cols-3">
                      <PaymentMethodField />
                      <Field label="Reference" optional hint="The bank slip, mobile money or cheque number.">
                          <input name="reference" className="input mt-1" />
                        </Field>
                      <Field label="Receipt no." optional hint="Leave blank for the system to number it.">
                        <input name="receipt" className="input mt-1" />
                      </Field>
                      <div className="flex justify-end sm:col-span-3">
                        <button disabled={processHostelPayment.isPending} className="btn btn-primary">Record full payment</button>
                      </div>
                    </form>
                  </ActionPanel>
                  <ActionPanel title="Cancel this charge" description="For a charge issued in error. Its invoice is cancelled with it.">
                    <form onSubmit={(event) => submitHostelCancellation(event, charge.id)} className="space-y-3">
                      <Field label="Reason">
                        <textarea required name="reason" rows={2} className="input mt-1 h-auto min-h-[3.5rem] resize-y py-2 leading-5" />
                      </Field>
                      <div className="flex justify-end">
                        <button disabled={cancelHostelCharge.isPending} className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50">Cancel charge</button>
                      </div>
                    </form>
                  </ActionPanel>
                </>
              )}
            </DetailsModal>
          )}
        </div>
        );
      })()}

      {activeTab === "cafeteria" && (() => {
        const order = cafeteriaOrders.data?.find((row) => row.id === viewingMealOrderId) ?? null;
        const plan = cafeteriaSubscriptions.data?.find((row) => row.id === viewingMealPlanId) ?? null;
        return (
        <div className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-primary-100 bg-primary-50 p-5">
            <div><h2 className="font-semibold text-primary-950">Cafeteria payments</h2><p className="mt-1 max-w-2xl text-sm text-primary-800">Receipt meal orders and meal plans, and refund what was paid for but not served.</p></div>
            <div className="flex items-center gap-3 text-sm"><span className="rounded-md bg-white px-3 py-2 font-medium text-amber-700">{(cafeteriaOrders.data ?? []).filter(mealOrderCanTakePayment).length} unpaid orders</span><span className="rounded-md bg-white px-3 py-2 font-medium text-amber-700">{(cafeteriaSubscriptions.data ?? []).filter(mealPlanCanTakePayment).length} plans awaiting payment</span></div>
          </div>
          <ClientRegister
            title="Meal orders"
            description="Individual meals ordered by students."
            rows={cafeteriaOrders.data}
            config={mealOrderConfig}
            columns={mealOrderColumns}
            rowKey={(row) => row.id}
            onOpen={(row) => setViewingMealOrderId(row.id)}
            openLabel={(row) => (canInitiate && mealOrderCanTakePayment(row) ? "Record payment" : "Open")}
            noun={["meal order", "meal orders"]}
            exportName={{ file: "meal-orders", title: "Meal Orders" }}
            isLoading={cafeteriaOrders.isLoading}
            isError={cafeteriaOrders.isError}
            onRetry={() => cafeteriaOrders.refetch()}
            emptyText="No meal orders yet."
          />
          <ClientRegister
            title="Meal plans"
            description="Students' subscriptions to a meal plan."
            rows={cafeteriaSubscriptions.data}
            config={mealPlanConfig}
            columns={mealPlanColumns}
            rowKey={(row) => row.id}
            onOpen={(row) => setViewingMealPlanId(row.id)}
            openLabel={(row) => (canInitiate && mealPlanCanTakePayment(row) ? "Receipt plan" : "Open")}
            noun={["meal plan", "meal plans"]}
            exportName={{ file: "meal-plans", title: "Meal Plans" }}
            isLoading={cafeteriaSubscriptions.isLoading}
            isError={cafeteriaSubscriptions.isError}
            onRetry={() => cafeteriaSubscriptions.refetch()}
            emptyText="No meal-plan subscriptions yet."
          />
          {order && (
            <DetailsModal
              title={`Meal order · ${order.student_name}`}
              headline={{ label: "Amount", value: `UGX ${Number(order.amount).toLocaleString()}`, note: order.daily_menu_name }}
              status={{ label: `Payment ${humanise(order.payment_status).toLowerCase()}`, dot: statusDot(order.payment_status) }}
              facts={[
                ["Student", order.student_name],
                ["Menu", order.daily_menu_name],
                ["Ordered", order.order_date?.slice(0, 10)],
                ["Order status", humanise(order.status)],
                ["Served at", order.served_time],
              ]}
              sections={[{ title: "Cancelled", body: order.cancellation_reason, tone: "danger" }]}
              onClose={() => setViewingMealOrderId(null)}
            >
              {canInitiate && mealOrderCanTakePayment(order) && (
                <ActionPanel title="Record meal payment">
                  <form onSubmit={(event) => submitCafeteriaPayment(event, order.id)} className="grid gap-4 sm:grid-cols-2">
                    <PaymentMethodField />
                    <Field label="Reference" optional hint="The bank slip, mobile money or cheque number.">
                          <input name="reference" className="input mt-1" />
                        </Field>
                    <div className="flex justify-end sm:col-span-2">
                      <button disabled={processCafeteriaPayment.isPending} className="btn btn-primary">Record meal payment</button>
                    </div>
                  </form>
                </ActionPanel>
              )}
              {canInitiate && mealOrderCanRefund(order) && (
                <ActionPanel title="Refund before preparation" description="Only while the meal has not been prepared.">
                  <form onSubmit={(event) => submitCafeteriaRefund(event, order.id)} className="space-y-3">
                    <Field label="Reason">
                      <textarea required name="reason" rows={2} className="input mt-1 h-auto min-h-[3.5rem] resize-y py-2 leading-5" />
                    </Field>
                    <div className="flex justify-end">
                      <button disabled={refundCafeteriaPayment.isPending} className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50">Request refund</button>
                    </div>
                  </form>
                </ActionPanel>
              )}
            </DetailsModal>
          )}
          {plan && (
            <DetailsModal
              title={`Meal plan · ${plan.student_name}`}
              headline={{ label: "Paid", value: `UGX ${Number(plan.amount_paid).toLocaleString()}`, note: plan.meal_plan_name }}
              status={{ label: humanise(plan.status), dot: statusDot(plan.status) }}
              facts={[
                ["Student", plan.student_name],
                ["Plan", plan.meal_plan_name],
                ["Runs", `${plan.start_date} to ${plan.end_date}`],
                ["Payment", humanise(plan.payment_status)],
                ["Paid on", plan.payment_date?.slice(0, 10)],
                ["Meals used", String(plan.meals_used)],
                ["Meals left", plan.meals_remaining != null ? String(plan.meals_remaining) : ""],
              ]}
              sections={[{ title: "Cancelled", body: plan.cancellation_reason, tone: "danger" }]}
              onClose={() => setViewingMealPlanId(null)}
            >
              {canInitiate && mealPlanCanTakePayment(plan) && (
                <ActionPanel title="Receipt and activate plan" description="The plan starts once its payment is recorded.">
                  <form onSubmit={(event) => submitCafeteriaSubscriptionPayment(event, plan.id)} className="grid gap-4 sm:grid-cols-2">
                    <PaymentMethodField />
                    <Field label="Reference" optional hint="The bank slip, mobile money or cheque number.">
                          <input name="reference" className="input mt-1" />
                        </Field>
                    <div className="flex justify-end sm:col-span-2">
                      <button disabled={processCafeteriaSubscriptionPayment.isPending} className="btn btn-primary">Receipt and activate plan</button>
                    </div>
                  </form>
                </ActionPanel>
              )}
              {canInitiate && mealPlanCanRefund(plan) && (
                <ActionPanel title="Refund unused plan" description="Only while no meals have been used on it.">
                  <form onSubmit={(event) => submitCafeteriaSubscriptionRefund(event, plan.id)} className="space-y-3">
                    <Field label="Reason">
                      <textarea required name="reason" rows={2} className="input mt-1 h-auto min-h-[3.5rem] resize-y py-2 leading-5" />
                    </Field>
                    <div className="flex justify-end">
                      <button disabled={refundCafeteriaSubscriptionPayment.isPending} className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50">Request refund</button>
                    </div>
                  </form>
                </ActionPanel>
              )}
            </DetailsModal>
          )}
        </div>
        );
      })()}

      {activeTab === "uniforms" && (() => {
        const order = uniformOrders.data?.find((row) => row.id === viewingUniformId) ?? null;
        const orders = uniformOrders.data ?? [];
        return (
        <div className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-primary-100 bg-primary-50 p-5">
            <div><h2 className="font-semibold text-primary-950">Uniform order receipts</h2><p className="mt-1 max-w-2xl text-sm text-primary-800">Uniform staff reserve and prepare stock; accounting controls all money received and refunded.</p></div>
            <div className="flex items-center gap-3 text-sm"><span className="rounded-md bg-white px-3 py-2 font-medium text-gray-700">{orders.length} orders</span><span className="rounded-md bg-white px-3 py-2 font-medium text-amber-700">{orders.filter(uniformCanTakePayment).length} with a balance</span></div>
          </div>
          <ClientRegister
            title="Uniform orders"
            description="Open an order to see its items and payments, receipt money or refund a payment."
            rows={uniformOrders.data}
            config={uniformConfig}
            columns={uniformColumns}
            rowKey={(row) => row.id}
            onOpen={(row) => setViewingUniformId(row.id)}
            openLabel={(row) => (canInitiate && uniformCanTakePayment(row) ? "Record receipt" : "Open")}
            noun={["order", "orders"]}
            exportName={{ file: "uniform-orders", title: "Uniform Orders" }}
            isLoading={uniformOrders.isLoading}
            isError={uniformOrders.isError}
            onRetry={() => uniformOrders.refetch()}
            emptyText="No uniform orders yet."
          />
          {order && (() => {
            const balance = uniformBalance(order);
            return (
              <DetailsModal
                size="3xl"
                title={`Uniform order ${order.order_number}`}
                headline={{
                  label: "Balance due",
                  value: `UGX ${Math.max(balance, 0).toLocaleString()}`,
                  note: `of UGX ${Number(order.total_amount).toLocaleString()} · UGX ${Number(order.paid_amount).toLocaleString()} paid`,
                }}
                status={{ label: humanise(order.status), dot: statusDot(order.status) }}
                facts={[
                  ["Student", order.student_name],
                  ["Class", order.student_class],
                  ["Ordered", order.order_date?.slice(0, 10)],
                  ["Delivered", order.delivered_date?.slice(0, 10)],
                  ["Stock", order.stock_reserved ? "Reserved" : "Not reserved"],
                ]}
                sections={[
                  { title: "Notes", body: order.notes },
                  { title: "Cancelled", body: order.cancellation_reason, tone: "danger" },
                ]}
                onClose={() => setViewingUniformId(null)}
              >
                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Items</h4>
                  <div className="sheet-wrap mt-2 max-h-none">
                    <table className="sheet">
                      <thead><tr><th>Item</th><th>Size</th><th className="text-right">Qty</th><th className="text-right">Unit (UGX)</th><th className="text-right">Total (UGX)</th></tr></thead>
                      <tbody>
                        {order.items.map((item) => (
                          <tr key={item.id}>
                            <td className="text-gray-900">{item.uniform_item_name}{item.uniform_item_color ? <span className="text-gray-500"> · {item.uniform_item_color}</span> : null}</td>
                            <td className="text-gray-700">{item.uniform_item_size}</td>
                            <td className="text-right tabular-nums">{item.quantity}</td>
                            <td className="text-right tabular-nums text-gray-600">{Number(item.unit_price).toLocaleString()}</td>
                            <td className="text-right tabular-nums text-gray-900">{Number(item.total_price).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Payments</h4>
                  {order.payments.length ? (
                    <div className="mt-2 space-y-2">
                      {order.payments.map((payment) => (
                        <div key={payment.id} className="rounded-lg border border-gray-200 p-3">
                          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                            <span className="font-medium tabular-nums text-gray-900">UGX {Number(payment.amount).toLocaleString()}</span>
                            <span className="text-gray-600">{humanise(payment.payment_method)}{payment.transaction_id ? ` · ${payment.transaction_id}` : ""}{payment.payment_date ? ` · ${payment.payment_date.slice(0, 10)}` : ""}</span>
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700"><span className={`h-1.5 w-1.5 rounded-full ${statusDot(payment.payment_status)}`} />{humanise(payment.payment_status)}</span>
                          </div>
                          {payment.refund_reason && <p className="mt-1 text-xs text-red-700">Refund: {payment.refund_reason}</p>}
                          {canInitiate && payment.payment_status === 'completed' && order.status !== 'delivered' && (
                            <form onSubmit={(event) => submitUniformRefund(event, payment.id)} className="mt-2 flex flex-wrap items-end gap-2">
                              <Field label="Refund reason" className="min-w-[14rem] flex-1">
                                <input required name="reason" className="input mt-1" />
                              </Field>
                              <button disabled={refundUniformPayment.isPending} className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50">Request refund</button>
                            </form>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-1.5 text-sm text-gray-500">Nothing paid on this order yet.</p>
                  )}
                </section>
                {canInitiate && uniformCanTakePayment(order) && (
                  <ActionPanel title="Record receipt" description="A part payment is fine; the balance stays on the order.">
                    <form onSubmit={(event) => submitUniformPayment(event, order.id)} className="grid gap-4 sm:grid-cols-3">
                      <Field label="Amount (UGX)" hint={`Up to UGX ${balance.toLocaleString()}.`}>
                        <input required min="1" max={balance} type="number" name="amount" defaultValue={balance} className="input mt-1 tabular-nums" />
                      </Field>
                      <PaymentMethodField withCard />
                      <Field label="Reference" optional hint="The bank slip, mobile money or cheque number.">
                          <input name="reference" className="input mt-1" />
                        </Field>
                      <div className="flex justify-end sm:col-span-3">
                        <button disabled={processUniformPayment.isPending} className="btn btn-primary">Record receipt</button>
                      </div>
                    </form>
                  </ActionPanel>
                )}
              </DetailsModal>
            );
          })()}
        </div>
        );
      })()}

      {activeTab === "invoices" && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-primary-100 bg-primary-50 p-5"><div><h2 className="font-semibold text-primary-950">Invoice collection</h2><p className="mt-1 max-w-2xl text-sm text-primary-800">Issue invoices, apply available receipts, and monitor outstanding balances by invoice status.</p></div><div className="flex items-center gap-3 text-sm"><span className="rounded-md bg-white px-3 py-2 font-medium text-gray-700">{invoiceCount} invoices</span><span className="rounded-md bg-white px-3 py-2 font-medium text-amber-700">{outstandingInvoices.data?.count ?? 0} outstanding</span></div></div>
          {canInitiate && showInvoiceForm && <form key={invoiceFormKey} onSubmit={submitInvoice} className="card">
            <div className="card-titlebar">
              <div>
                <h3 className="font-semibold text-gray-900">Issue invoice</h3>
                <p className="mt-1 text-sm text-gray-500">A direct invoice for one student, for a charge no fee structure covers.</p>
              </div>
              <button type="button" onClick={() => setShowInvoiceForm(false)} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close the issue invoice form">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="card-content space-y-5 pt-5">
              <FormSection title="Invoice" divided={false} columns={3}>
                <Field as="div" label="Student">
                  <StudentSearchField required />
                </Field>
                <Field label="Amount (UGX)">
                  <input required min="1" type="number" name="amount" className="input mt-1 tabular-nums" placeholder="0" />
                </Field>
                <Field label="Due date">
                  <input required type="date" name="due" className="input mt-1" />
                </Field>
                <Field label="What it is for" className="sm:col-span-2 lg:col-span-3" hint="Shown on the invoice the parent receives.">
                  <textarea name="notes" rows={2} className="input mt-1 h-auto min-h-[3.5rem] resize-y py-2 leading-5" placeholder="e.g. Replacement library book, Science trip to Entebbe" />
                </Field>
              </FormSection>
            </div>
            <div className="card-footer">
              <p className="mr-auto text-xs text-gray-500">The invoice is issued at once and appears on the student's account.</p>
              <button disabled={createInvoice.isPending} className="btn btn-primary">
                {createInvoice.isPending ? "Issuing…" : "Issue invoice"}
              </button>
            </div>
          </form>}
          <div className="card">
            <div className="card-header pb-4">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-gray-900">Invoice register</h3>
                  <p className="mt-1 text-sm text-gray-500">Open an invoice to see what it bills, apply a receipt, discount or cancel it.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <ExportButtons onExport={exportInvoices} disabled={!invoiceCount} busy={isExportingInvoices} what="every invoice matching these filters" />
                  {canInitiate && !showInvoiceForm && (
                    <button type="button" onClick={() => setShowInvoiceForm(true)} className="btn-primary h-8 min-h-8 px-3 text-xs">
                      <Plus className="mr-1.5 h-3.5 w-3.5" /> Issue invoice
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="card-content space-y-4">
              <div className="filter-panel">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div role="group" aria-label="Invoice view" className="segmented">
                    {(Object.keys(INVOICE_VIEWS) as InvoiceView[]).map((view) => (
                      <button
                        key={view}
                        type="button"
                        aria-pressed={invoiceView === view}
                        onClick={() => updateInvoiceFilters(INVOICE_VIEWS[view].filters)}
                        className="segmented-option"
                      >
                        {INVOICE_VIEWS[view].label}
                        {invoiceView === view && invoices.data && <span className="ml-1.5 tabular-nums opacity-70">{invoiceCount}</span>}
                      </button>
                    ))}
                  </div>
                  {invoiceFiltersActive && (
                    <button
                      type="button"
                      onClick={() => updateInvoiceFilters({ ...EMPTY_INVOICE_FILTERS, ordering: invoiceRegisterFilters.ordering })}
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                    >
                      <X className="h-3.5 w-3.5" /> Clear {activeInvoiceFilters} {activeInvoiceFilters === 1 ? "filter" : "filters"}
                    </button>
                  )}
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <label className="filter-field sm:col-span-2">
                    Search
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                      <input
                        type="search"
                        value={invoiceRegisterFilters.search}
                        onChange={(event) => updateInvoiceFilters({ search: event.target.value })}
                        className="input-compact pl-8"
                        placeholder="Invoice no., student, student ID, admission no. or notes"
                      />
                    </div>
                  </label>
                  <label className="filter-field">
                    Status
                    <select
                      value={invoiceRegisterFilters.status}
                      onChange={(event) => updateInvoiceFilters({ status: event.target.value, outstanding: false })}
                      className="input-compact"
                    >
                      <option value="">Any status</option>
                      {INVOICE_STATUSES.map((status) => <option key={status} value={status}>{INVOICE_STATUS_LABELS[status] ?? status}</option>)}
                    </select>
                  </label>
                  <label className="filter-field">
                    Class
                    <select value={invoiceRegisterFilters.classId} onChange={(event) => updateInvoiceFilters({ classId: event.target.value })} className="input-compact">
                      <option value="">All classes</option>
                      {studentDirectoryClasses.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                    </select>
                  </label>
                  <label className="filter-field">
                    Due from
                    <input type="date" value={invoiceRegisterFilters.dueFrom} max={invoiceRegisterFilters.dueTo || undefined} onChange={(event) => updateInvoiceFilters({ dueFrom: event.target.value })} className="input-compact" />
                  </label>
                  <label className="filter-field">
                    Due to
                    <input type="date" value={invoiceRegisterFilters.dueTo} min={invoiceRegisterFilters.dueFrom || undefined} onChange={(event) => updateInvoiceFilters({ dueTo: event.target.value })} className="input-compact" />
                  </label>
                  <label className="filter-field">
                    Issued from
                    <input type="date" value={invoiceRegisterFilters.issuedFrom} max={invoiceRegisterFilters.issuedTo || undefined} onChange={(event) => updateInvoiceFilters({ issuedFrom: event.target.value })} className="input-compact" />
                  </label>
                  <label className="filter-field">
                    Issued to
                    <input type="date" value={invoiceRegisterFilters.issuedTo} min={invoiceRegisterFilters.issuedFrom || undefined} onChange={(event) => updateInvoiceFilters({ issuedTo: event.target.value })} className="input-compact" />
                  </label>
                  <label className="filter-field sm:col-span-2 lg:col-span-1">
                    Sort by
                    <select
                      value={invoiceRegisterFilters.ordering}
                      onChange={(event) => updateInvoiceFilters({ ordering: event.target.value as InvoiceRegisterFilters["ordering"] })}
                      className="input-compact"
                    >
                      {Object.entries(INVOICE_ORDERINGS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </label>
                </div>
              </div>

              {invoices.isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 8 }).map((_, index) => <div key={index} className="h-7 animate-pulse rounded bg-gray-100" />)}
                </div>
              ) : invoices.isError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                  The invoice register could not be loaded.{" "}
                  <button type="button" className="font-medium underline" onClick={() => invoices.refetch()}>Try again</button>
                </div>
              ) : !visibleInvoices.length ? (
                <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
                  {invoiceFiltersActive ? "No invoices match these filters." : "No invoices have been issued yet."}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className={`sheet-wrap ${invoices.isFetching ? "opacity-60" : ""}`}>
                    <table className="sheet">
                      <thead>
                        <tr>
                          <th className="sheet-rownum">#</th>
                          <th>Invoice no.</th>
                          <th>Issued</th>
                          <th>Student</th>
                          <th>Class</th>
                          <th>Billed from</th>
                          <th>Due</th>
                          <th className="text-right">Total (UGX)</th>
                          <th className="text-right">Balance (UGX)</th>
                          <th>Status</th>
                          <th className="text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleInvoices.map((invoice, index) => {
                          const late = daysOverdue(invoice);
                          return (
                            <tr key={invoice.id} className="cursor-pointer" onClick={() => setViewingInvoiceId(invoice.id)}>
                              <td className="sheet-rownum">{(invoicePage - 1) * invoicePageSize + index + 1}</td>
                              <td className="font-mono text-xs text-gray-800">{invoice.invoice_number}</td>
                              <td className="tabular-nums text-gray-600">{invoiceIssuedOn(invoice) || "—"}</td>
                              <td className="max-w-[14rem] truncate font-medium text-gray-900">{invoice.student_name}</td>
                              <td className="text-gray-700">{invoice.class_name || <span className="text-gray-300">—</span>}</td>
                              <td className="max-w-[12rem] truncate text-gray-600">{invoiceBilling(invoice)}</td>
                              <td className="tabular-nums text-gray-700">
                                {invoice.due_date}
                                {late > 0 && <span className="ml-1.5 text-xs font-medium text-red-600">+{late}d</span>}
                              </td>
                              <td className="text-right tabular-nums text-gray-700">{Number(invoice.total_amount).toLocaleString()}</td>
                              <td className={`text-right font-semibold tabular-nums ${Number(invoice.balance_due) > 0 ? "text-gray-900" : "text-gray-300"}`}>
                                {Number(invoice.balance_due) > 0 ? Number(invoice.balance_due).toLocaleString() : "—"}
                              </td>
                              <td>
                                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700">
                                  <span className={`h-1.5 w-1.5 rounded-full ${INVOICE_STATUS_DOTS[invoice.status] ?? "bg-gray-400"}`} />
                                  {invoiceStatusLabel(invoice.status)}
                                </span>
                              </td>
                              <td className="text-right">
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setViewingInvoiceId(invoice.id);
                                  }}
                                  className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-primary-700 hover:bg-primary-50"
                                >
                                  <Eye className="h-3.5 w-3.5" /> Open
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <Pager
                    first={(invoicePage - 1) * invoicePageSize + 1}
                    shown={visibleInvoices.length}
                    total={invoiceCount}
                    noun={invoiceCount === 1 ? "invoice" : "invoices"}
                    page={invoicePage}
                    pageCount={invoicePageCount}
                    onPage={setInvoicePage}
                    pageSize={invoiceRegisterPageSize}
                    pageSizes={INVOICE_PAGE_SIZES}
                    onPageSize={(size) => {
                      setInvoiceRegisterPageSize(size);
                      setInvoicePage(1);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          {viewingInvoice && (() => {
            const invoice = viewingInvoice;
            const receipts = receiptsFor(invoice);
            return (
              <InvoiceDetailsModal
                invoice={invoice}
                onClose={closeInvoice}
                footer={
                  <>
                    {canCancelInvoice(invoice) && (
                      <button
                        type="button"
                        onClick={() => confirmCancelInvoice(invoice)}
                        className="mr-auto inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                      >
                        <X className="h-4 w-4" /> Cancel invoice
                      </button>
                    )}
                    {canDiscountInvoice(invoice) && invoiceDiscount?.id !== invoice.id && (
                      <button
                        type="button"
                        onClick={() => setInvoiceDiscount({ id: invoice.id, kind: "amount", value: "", reason: "" })}
                        className="btn-outline"
                      >
                        Give discount
                      </button>
                    )}
                    <button type="button" onClick={closeInvoice} className="btn-outline">Close</button>
                  </>
                }
              >
                {canApplyReceipt(invoice) && (
                  <section aria-labelledby="apply-receipt-heading" className="space-y-2 rounded-lg border border-gray-200 p-4">
                    <div>
                      <h4 id="apply-receipt-heading" className="text-sm font-semibold text-gray-900">Apply a receipt</h4>
                      <p className="mt-0.5 text-xs text-gray-500">Money {invoice.student_name} has paid that isn't against an invoice yet.</p>
                    </div>
                    {receipts.length ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <select
                          aria-label={`Receipt for ${invoice.invoice_number}`}
                          className="input max-w-md flex-1"
                          value={invoiceReceipts[invoice.id] || ""}
                          onChange={(event) => setInvoiceReceipts((current) => ({ ...current, [invoice.id]: event.target.value }))}
                        >
                          <option value="">Choose a receipt</option>
                          {receipts.map((payment) => (
                            <option key={payment.id} value={payment.id}>
                              {payment.receipt_number || payment.reference_number || `Receipt #${payment.id}`} · UGX {Number(payment.unallocated_amount || payment.amount).toLocaleString()} available
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          disabled={!invoiceReceipts[invoice.id] || applyPayment.isPending}
                          onClick={() => applyPayment.mutate({ id: invoice.id, payment: Number(invoiceReceipts[invoice.id]) })}
                          className="btn btn-primary"
                        >
                          Apply receipt
                        </button>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No unapplied receipts for this student. Record the payment on the Payments tab.</p>
                    )}
                  </section>
                )}
                  {invoiceDiscount?.id === invoice.id && (() => {
                    const total = Number(invoice.total_amount);
                    const value = Number(invoiceDiscount.value);
                    const amount = !Number.isFinite(value) || value <= 0
                      ? 0
                      : invoiceDiscount.kind === "percent" ? Math.round((total * value) / 100) : value;
                    const tooMuch = amount > total || (invoiceDiscount.kind === "percent" && value > 100);
                    const ready = amount > 0 && !tooMuch && invoiceDiscount.reason.trim().length >= 3;
                    return (
                      <div className="rounded-lg border border-gray-200 bg-white p-4">
                        <div className="text-sm font-semibold text-gray-900">Discount on {invoice.invoice_number}</div>
                        <p className="mt-1 text-xs text-gray-500">
                          Taken off this invoice before anything is paid, with the reason kept on it. Once money has
                          come in, reduce the invoice with a credit note instead.
                        </p>
                        <div className="mt-3 grid gap-2 sm:grid-cols-[5rem_8rem_1fr_auto_auto] sm:items-center">
                          <select
                            aria-label="Discount type"
                            className="input"
                            value={invoiceDiscount.kind}
                            onChange={(event) =>
                              setInvoiceDiscount({ ...invoiceDiscount, kind: event.target.value as "amount" | "percent" })
                            }
                          >
                            <option value="amount">UGX</option>
                            <option value="percent">%</option>
                          </select>
                          <input
                            aria-label="Discount"
                            type="number"
                            min="0"
                            className="input"
                            value={invoiceDiscount.value}
                            onChange={(event) => setInvoiceDiscount({ ...invoiceDiscount, value: event.target.value })}
                          />
                          <input
                            aria-label="Reason for discount"
                            className="input"
                            placeholder="Reason, e.g. staff child"
                            value={invoiceDiscount.reason}
                            onChange={(event) => setInvoiceDiscount({ ...invoiceDiscount, reason: event.target.value })}
                          />
                          <button
                            type="button"
                            className="btn btn-primary px-3 py-1.5 text-sm"
                            disabled={!ready || giveInvoiceDiscount.isPending}
                            onClick={() =>
                              giveInvoiceDiscount.mutate(
                                {
                                  id: invoice.id,
                                  reason: invoiceDiscount.reason.trim(),
                                  ...(invoiceDiscount.kind === "percent"
                                    ? { percent: invoiceDiscount.value }
                                    : { amount: invoiceDiscount.value }),
                                },
                                { onSuccess: () => setInvoiceDiscount(null) },
                              )
                            }
                          >
                            Apply discount
                          </button>
                          <button type="button" className="text-sm text-gray-500" onClick={() => setInvoiceDiscount(null)}>
                            Close
                          </button>
                        </div>
                        <div className={`mt-2 text-xs ${tooMuch ? "text-red-600" : "text-gray-500"}`}>
                          {tooMuch
                            ? `A discount cannot be more than the invoice (UGX ${total.toLocaleString()}).`
                            : amount > 0
                              ? `Discount UGX ${amount.toLocaleString()} · invoice UGX ${(total - amount).toLocaleString()}`
                              : "Enter an amount or a percentage."}
                        </div>
                      </div>
                    );
                  })()}
              </InvoiceDetailsModal>
            );
          })()}
        </div>
      )}

      {activeTab === "fee-structures" && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-primary-100 bg-primary-50 p-5">
            <div><h2 className="font-semibold text-primary-950">Fee structures</h2><p className="mt-1 max-w-2xl text-sm text-primary-800">Bundle catalogue fees by class and term, then issue invoices from an approved billing plan.</p></div>
            <div className="flex items-center gap-3 text-sm"><span className="rounded-md bg-white px-3 py-2 font-medium text-gray-700">{feeStructures.data?.length ?? 0} structures</span><span className="rounded-md bg-white px-3 py-2 font-medium text-amber-700">{feeStructures.data?.reduce((total, structure) => total + structure.pending_invoice_count, 0) ?? 0} pending invoices</span></div>
          </div>
          {canConfigureFees && (showStructureForm || feeStructures.data?.length === 0) && <form onSubmit={submitFeeStructure} className="card">
            <div className="card-titlebar">
              <div>
                <h3 className="font-semibold text-gray-900">Create billing plan</h3>
                <p className="mt-1 text-sm text-gray-500">Choose the class and term, then the catalogue fees it bills.</p>
              </div>
              {!!feeStructures.data?.length && (
                <button type="button" onClick={() => setShowStructureForm(false)} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close the new fee structure form">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="card-content space-y-5 pt-5">
              <FormSection title="The plan" divided={false} columns={4}>
                <Field label="Plan name" className="sm:col-span-2">
                  <input required name="name" className="input mt-1" placeholder="e.g. P5 Term 1 fees" />
                </Field>
                {/* One field, not two: a term on the school calendar already
                    knows its academic year, so asking for both invites a pair
                    that disagree. */}
                <Field label="Term">
                  <select required name="term" className="input mt-1" defaultValue="">
                    <option value="" disabled>Choose a term</option>
                    {academicTerms.data?.map((term) => (
                      <option key={term.id} value={term.id}>
                        {term.academic_year_name} · {termLabel(term.term)}
                        {term.is_current ? " · Current" : ""}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Class level">
                  <select required name="level" className="input mt-1" defaultValue="">
                    <option value="" disabled>Choose a class</option>
                    {classLevels.data?.map((level) => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </Field>
                <Field as="div" label="Receivables account" optional className="sm:col-span-2" hint="Left alone, the receivables control account is used.">
                  <AccountSelect
                    name="receivable_account"
                    placeholder="Receivables control (automatic)"
                    accounts={accounts.data}
                    isSelectable={(account) =>
                      canReceivePostings(account) &&
                      account.account_type === "asset" &&
                      account.can_hold_student_receivables !== false
                    }
                  />
                </Field>
                <Field label="Notes" optional className="sm:col-span-2">
                  <textarea name="notes" rows={2} className="input mt-1 h-auto min-h-[3.5rem] resize-y py-2 leading-5" placeholder="Who this plan is for, anything parents should know" />
                </Field>
              </FormSection>

              <FormSection title="Fee items" description="Amounts come from the catalogue and can only be changed there." columns={1}>
                <div className="overflow-hidden rounded-md border border-gray-200">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                      <tr>
                        <th className="w-10 px-3 py-2">#</th>
                        <th className="px-3 py-2">Catalogue fee</th>
                        <th className="w-40 px-3 py-2 text-right">Amount (UGX)</th>
                        <th className="w-20 px-3 py-2" aria-label="Remove" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {structureItems.map((item, index) => (
                        <tr key={index}>
                          <td className="px-3 py-2 tabular-nums text-gray-400">{index + 1}</td>
                          <td className="px-3 py-2">
                            <select
                              aria-label={`Fee item ${index + 1}`}
                              className="input"
                              value={item.fee}
                              onChange={(event) => {
                                const feeId = event.target.value;
                                const catalogAmount = fees.data?.find((fee) => String(fee.id) === feeId)?.amount ?? "";
                                setStructureItems((current) => current.map((row, i) => (i === index ? { ...row, fee: feeId, amount: catalogAmount } : row)));
                              }}
                            >
                              <option value="">Choose a fee</option>
                              {fees.data?.filter((fee) => fee.is_active).map((fee) => (
                                <option key={fee.id} value={fee.id}>{fee.name}</option>
                              ))}
                            </select>
                          </td>
                          <td
                            className="px-3 py-2 text-right tabular-nums text-gray-700"
                            title="Set from the fee's catalogue price. Change the fee itself to change it."
                          >
                            {item.amount ? Number(item.amount).toLocaleString() : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {structureItems.length > 1 && (
                              <button
                                type="button"
                                className="rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                                onClick={() => setStructureItems((current) => current.filter((_, i) => i !== index))}
                              >
                                Remove
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t border-gray-200 bg-gray-50">
                      <tr>
                        <td colSpan={2} className="px-3 py-2">
                          <button
                            type="button"
                            className="inline-flex items-center text-sm font-medium text-primary-700 hover:underline"
                            onClick={() => setStructureItems((current) => [...current, { fee: "", amount: "" }])}
                          >
                            <Plus className="mr-1 h-4 w-4" />
                            Add another fee
                          </button>
                        </td>
                        <td className="px-3 py-2 text-right font-semibold tabular-nums text-gray-900">
                          {structureItems.reduce((total, item) => total + Number(item.amount || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500">total</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </FormSection>
            </div>
            <div className="card-footer">
              <p className="mr-auto text-xs text-gray-500">Creating the plan bills no one. You generate its invoices from the plan afterwards.</p>
              <button disabled={createFeeStructure.isPending} className="btn btn-primary">
                Create fee structure
              </button>
            </div>
          </form>}
          {generationResult && !viewingStructure && (
            <div className="rounded-md bg-green-50 p-3 text-sm text-green-700">{generationResult}</div>
          )}
          <div className="card">
            <div className="card-header pb-4">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-gray-900">Billing plans</h3>
                  <p className="mt-1 text-sm text-gray-500">Open a plan to see what each student is billed and to generate its invoices.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <ExportButtons onExport={exportStructures} disabled={!filteredStructures.length} what="the filtered billing plans" />
                  {canConfigureFees && !showStructureForm && !!feeStructures.data?.length && (
                    <button type="button" onClick={() => setShowStructureForm(true)} className="btn-primary h-8 min-h-8 px-3 text-xs">
                      <Plus className="mr-1.5 h-3.5 w-3.5" /> New fee structure
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="card-content space-y-4">
              <div className="filter-panel">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div role="group" aria-label="Invoicing" className="segmented">
                    {([["all", "All"], ["pending", "Needs invoicing"], ["done", "Fully invoiced"]] as const).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={structureFilters.invoicing === value}
                        onClick={() => updateStructureFilters({ invoicing: value })}
                        className="segmented-option"
                      >
                        {label}
                        <span className="ml-1.5 tabular-nums opacity-70">{structureInvoicing[value]}</span>
                      </button>
                    ))}
                  </div>
                  {activeStructureFilters > 0 && (
                    <button
                      type="button"
                      onClick={() => updateStructureFilters({ ...EMPTY_FEE_STRUCTURE_FILTERS, invoicing: structureFilters.invoicing, ordering: structureFilters.ordering })}
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                    >
                      <X className="h-3.5 w-3.5" /> Clear {activeStructureFilters} {activeStructureFilters === 1 ? "filter" : "filters"}
                    </button>
                  )}
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-[minmax(16rem,2fr)_repeat(4,minmax(0,1fr))]">
                  <label className="filter-field sm:col-span-2 lg:col-span-3 2xl:col-span-1">
                    Search
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                      <input
                        type="search"
                        value={structureFilters.search}
                        onChange={(event) => updateStructureFilters({ search: event.target.value })}
                        className="input-compact pl-8"
                        placeholder="Plan name, class, fee item or notes"
                      />
                    </div>
                  </label>
                  <label className="filter-field">
                    Academic year
                    <select value={structureFilters.academicYear} onChange={(event) => updateStructureFilters({ academicYear: event.target.value })} className="input-compact">
                      <option value="">All years</option>
                      {academicYears.data?.map((year) => <option key={year.id} value={year.id}>{year.name}{year.is_current ? " (current)" : ""}</option>)}
                    </select>
                  </label>
                  <label className="filter-field">
                    Term
                    <select
                      value={structureFilters.term}
                      onChange={(event) => updateStructureFilters({ term: event.target.value as FeeStructureFilters["term"] })}
                      className="input-compact"
                    >
                      <option value="">All terms</option>
                      <option value="term_1">Term 1</option>
                      <option value="term_2">Term 2</option>
                      <option value="term_3">Term 3</option>
                    </select>
                  </label>
                  <label className="filter-field">
                    Class level
                    <select value={structureFilters.level} onChange={(event) => updateStructureFilters({ level: event.target.value })} className="input-compact">
                      <option value="">All classes</option>
                      {classLevels.data?.map((level) => <option key={level} value={level}>{level}</option>)}
                    </select>
                  </label>
                  <label className="filter-field">
                    Sort by
                    <select
                      value={structureFilters.ordering}
                      onChange={(event) => updateStructureFilters({ ordering: event.target.value as FeeStructureFilters["ordering"] })}
                      className="input-compact"
                    >
                      {Object.entries(FEE_STRUCTURE_ORDERINGS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </label>
                </div>
              </div>

              {feeStructures.isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 6 }).map((_, index) => <div key={index} className="h-7 animate-pulse rounded bg-gray-100" />)}
                </div>
              ) : feeStructures.isError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                  The fee structures could not be loaded.{" "}
                  <button type="button" className="font-medium underline" onClick={() => feeStructures.refetch()}>Try again</button>
                </div>
              ) : !filteredStructures.length ? (
                <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
                  {feeStructures.data?.length
                    ? "No billing plans match these filters."
                    : "No fee structures yet. Create one above, then generate invoices for every active student in that class level at once."}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="sheet-wrap">
                    <table className="sheet">
                      <thead>
                        <tr>
                          <th className="sheet-rownum">#</th>
                          <th>Fee structure</th>
                          <th>Class level</th>
                          <th>Term</th>
                          <th className="text-right">Fee items</th>
                          <th className="text-right">Per student (UGX)</th>
                          <th>Invoicing</th>
                          <th className="text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {structurePageRows.rows.map((structure, index) => (
                          <tr key={structure.id} className="cursor-pointer" onClick={() => openStructure(structure.id)}>
                            <td className="sheet-rownum">{structurePageRows.first + index}</td>
                            <td className="max-w-[18rem]">
                              <span className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openStructure(structure.id);
                                  }}
                                  className="truncate text-left font-medium text-gray-900 hover:text-primary-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                                  title="Open this billing plan"
                                >
                                  {structure.name}
                                </button>
                                {!structure.is_active && <span className="shrink-0 rounded bg-gray-100 px-1.5 text-[10px] font-medium text-gray-500">Inactive</span>}
                              </span>
                              {structure.notes && <span className="block truncate text-xs text-gray-500">{structure.notes}</span>}
                            </td>
                            <td className="text-gray-700">{structure.level}</td>
                            <td className="text-gray-700">{structureTermName(structure)}</td>
                            <td className="text-right tabular-nums text-gray-700" title={structure.items.map((item) => item.fee_name).join(", ")}>
                              {structure.items.length}
                            </td>
                            <td className="text-right font-semibold tabular-nums text-gray-900">{perStudentLabel(structure).replace("UGX ", "")}</td>
                            <td>
                              {structure.pending_invoice_count > 0 ? (
                                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700">
                                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                                  {structure.pending_invoice_count} not invoiced
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700">
                                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                  All invoiced
                                </span>
                              )}
                            </td>
                            <td className="text-right">
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openStructure(structure.id);
                                }}
                                className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-primary-700 hover:bg-primary-50"
                              >
                                <Eye className="h-3.5 w-3.5" />
                                {canGenerateFeeInvoices && structure.pending_invoice_count > 0 ? "Open & invoice" : "Open"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pager
                    first={structurePageRows.first}
                    shown={structurePageRows.rows.length}
                    total={filteredStructures.length}
                    noun={filteredStructures.length === 1 ? "billing plan" : "billing plans"}
                    page={structurePageRows.page}
                    pageCount={structurePageRows.pageCount}
                    onPage={setStructurePage}
                    pageSize={structurePageSize}
                    pageSizes={FEE_STRUCTURE_PAGE_SIZES}
                    onPageSize={(size) => {
                      setStructurePageSize(size);
                      setStructurePage(1);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          {viewingStructure && (() => {
            const structure = viewingStructure;
            return (
              <Modal
                isOpen
                size="4xl"
                title={structure.name}
                onClose={closeStructure}
                footer={
                  <>
                    {canConfigureFees && (
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Delete "${structure.name}"? This only works if no invoices were generated from it yet.`)) {
                            deleteFeeStructure.mutate(structure.id, { onSuccess: closeStructure });
                          }
                        }}
                        className="mr-auto inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" /> Delete
                      </button>
                    )}
                    <button type="button" onClick={closeStructure} className="btn-outline">Close</button>
                  </>
                }
              >
                <div className="space-y-6">
                  <FeeStructureSummary structure={structure} termName={structureTermName(structure)} />
                  {canGenerateFeeInvoices && (
                    <section aria-labelledby="generate-invoices-heading" className="space-y-3 rounded-lg border border-gray-200 p-4">
                      <div>
                        <h4 id="generate-invoices-heading" className="text-sm font-semibold text-gray-900">Generate invoices</h4>
                        <p className="mt-1 text-xs text-gray-500">
                          Set a due date, add any discounts, then invoice every active {structure.level} student not yet billed from this plan.
                        </p>
                      </div>
                      {generationResult && (
                        <div className="rounded-md bg-green-50 p-3 text-sm text-green-700">{generationResult}</div>
                      )}
                      <div className="flex flex-wrap items-center gap-2">
                    {canGenerateFeeInvoices && <>
                    <input
                      type="date"
                      aria-label="Due date"
                      className="input max-w-[10rem]"
                      value={generateDueDates[structure.id] || ""}
                      onChange={(event) => setGenerateDueDates((current) => ({ ...current, [structure.id]: event.target.value }))}
                    />
                    {([
                      ["term_1", "End of Term 1"],
                      ["term_2", "End of Term 2"],
                      ["term_3", "End of Term 3"],
                    ] as const).map(([term, label]) => {
                      const date = academicTerms.data?.find((t) => t.academic_year === structure.academic_year && t.term === term)?.end_date;
                      return (
                        <button
                          key={term}
                          type="button"
                          disabled={!date}
                          title={date ? `Set due date to ${date}` : `${label.replace("End of ", "")} dates haven't been set up for this academic year yet`}
                          onClick={() => date && setGenerateDueDates((current) => ({ ...current, [structure.id]: date }))}
                          className="btn-outline px-2 py-1 text-xs disabled:opacity-40"
                        >
                          {label}
                        </button>
                      );
                    })}
                    {(() => {
                      const yearEndDate = academicYears.data?.find((y) => y.id === structure.academic_year)?.end_date;
                      return (
                        <button
                          type="button"
                          disabled={!yearEndDate}
                          title={yearEndDate ? `Set due date to ${yearEndDate}` : "This academic year has no end date on record"}
                          onClick={() => yearEndDate && setGenerateDueDates((current) => ({ ...current, [structure.id]: yearEndDate }))}
                          className="btn-outline px-2 py-1 text-xs disabled:opacity-40"
                        >
                          End of Academic Year
                        </button>
                      );
                    })()}
                    <button
                      type="button"
                      onClick={() => setDiscountsOpenFor((current) => (current === structure.id ? null : structure.id))}
                      className="btn-outline px-3 py-1.5 text-sm"
                    >
                      {(generateDiscounts[structure.id] || []).length
                        ? `Discounts (${generateDiscounts[structure.id].length})`
                        : "Give discounts"}
                    </button>
                    <button
                      disabled={
                        !generateDueDates[structure.id] ||
                        generateInvoices.isPending ||
                        (generateDiscounts[structure.id] || []).some(
                          (row) =>
                            !discountRowReady(
                              discountStudents.data?.find((learner) => String(learner.id) === row.student)?.invoice_total ??
                                structure.total_amount,
                              row,
                            ),
                        )
                      }
                      onClick={() =>
                        generateInvoices.mutate({
                          id: structure.id,
                          due_date: generateDueDates[structure.id],
                          discounts: (generateDiscounts[structure.id] || []).map((row) => ({
                            student: Number(row.student),
                            reason: row.reason.trim(),
                            ...(row.kind === "percent" ? { percent: row.value } : { amount: row.value }),
                          })),
                        })
                      }
                      className="btn btn-primary px-3 py-1.5 text-sm"
                    >
                      Generate invoices for this class level
                    </button>
                    </>}
                      </div>
                  {canGenerateFeeInvoices && discountsOpenFor === structure.id && (() => {
                    const rows = generateDiscounts[structure.id] || [];
                    const setRows = (next: typeof rows) =>
                      setGenerateDiscounts((current) => ({ ...current, [structure.id]: next }));
                    const updateRow = (index: number, patch: Partial<(typeof rows)[number]>) =>
                      setRows(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
                    const learners = discountStudents.data || [];
                    const chosen = new Set(rows.map((row) => row.student));
                    // Day, boarding and transport learners are billed different fees,
                    // so each discount is measured against that learner's own invoice.
                    const totalFor = (student: string) =>
                      learners.find((learner) => String(learner.id) === student)?.invoice_total ?? structure.total_amount;
                    return (
                      <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">Discounts for selected students</div>
                          <p className="mt-1 text-xs text-gray-500">
                            Applied to these students&apos; invoices when you generate. Each discount needs a reason, which is kept on the invoice.
                            A student already invoiced from this plan can only be reduced with a credit note.
                          </p>
                        </div>
                        {discountStudents.isLoading && <div className="text-sm text-gray-500">Loading students…</div>}
                        {!discountStudents.isLoading && learners.length === 0 && (
                          <div className="text-sm text-gray-500">
                            No active students are in {structure.level} classes for this academic year.
                          </div>
                        )}
                        {rows.map((row, index) => {
                          const rowTotal = totalFor(row.student);
                          const invoiceTotal = Number(rowTotal);
                          const amount = discountAmount(rowTotal, row);
                          const tooMuch = amount > invoiceTotal || (row.kind === "percent" && Number(row.value) > 100);
                          return (
                            <div
                              key={index}
                              className="grid gap-2 md:grid-cols-[minmax(12rem,2fr)_5rem_8rem_minmax(10rem,2fr)_auto] md:items-center"
                            >
                              <select
                                aria-label="Student"
                                className="input"
                                value={row.student}
                                onChange={(event) => updateRow(index, { student: event.target.value })}
                              >
                                <option value="">Select student</option>
                                {learners.map((learner) => (
                                  <option
                                    key={learner.id}
                                    value={String(learner.id)}
                                    disabled={
                                      learner.already_invoiced ||
                                      Number(learner.invoice_total) <= 0 ||
                                      (chosen.has(String(learner.id)) && row.student !== String(learner.id))
                                    }
                                  >
                                    {learner.name} · {learner.class_name} · UGX {Number(learner.invoice_total).toLocaleString()}
                                    {learner.already_invoiced ? " (already invoiced)" : ""}
                                    {Number(learner.invoice_total) <= 0 ? " (no fees apply)" : ""}
                                  </option>
                                ))}
                              </select>
                              <select
                                aria-label="Discount type"
                                className="input"
                                value={row.kind}
                                onChange={(event) => updateRow(index, { kind: event.target.value as "amount" | "percent" })}
                              >
                                <option value="amount">UGX</option>
                                <option value="percent">%</option>
                              </select>
                              <input
                                aria-label={row.kind === "percent" ? "Discount percentage" : "Discount amount"}
                                type="number"
                                min="0"
                                max={row.kind === "percent" ? 100 : invoiceTotal}
                                className="input"
                                value={row.value}
                                onChange={(event) => updateRow(index, { value: event.target.value })}
                              />
                              <input
                                aria-label="Reason for discount"
                                className="input"
                                placeholder="Reason, e.g. staff child"
                                value={row.reason}
                                onChange={(event) => updateRow(index, { reason: event.target.value })}
                              />
                              <button
                                type="button"
                                className="text-sm font-medium text-red-600"
                                onClick={() => setRows(rows.filter((_, i) => i !== index))}
                              >
                                Remove
                              </button>
                              <div className={`text-xs md:col-span-5 ${tooMuch ? "text-red-600" : "text-gray-500"}`}>
                                {tooMuch
                                  ? `A discount cannot be more than the invoice (UGX ${invoiceTotal.toLocaleString()}).`
                                  : amount > 0
                                    ? `Discount UGX ${amount.toLocaleString()} · invoice UGX ${(invoiceTotal - amount).toLocaleString()}`
                                    : "Enter an amount or a percentage."}
                              </div>
                            </div>
                          );
                        })}
                        <button
                          type="button"
                          className="text-sm font-medium text-primary-600 disabled:opacity-40"
                          disabled={!learners.length}
                          onClick={() => setRows([...rows, { student: "", kind: "amount", value: "", reason: "" }])}
                        >
                          <Plus className="mr-1 inline h-4 w-4" />
                          Add a discount
                        </button>
                      </div>
                    );
                  })()}
                    </section>
                  )}
                </div>
              </Modal>
            );
          })()}
        </div>
      )}

      {activeTab === "accounts" && (() => {
        const chart = accounts.data ?? [];
        const inUse = chart.filter((account) => account.workflow_status === "approved" && account.is_active).length;
        const awaitingAccounts = chart.filter((account) => ["submitted", "verified"].includes(account.workflow_status ?? "")).length;
        const needsWork = chart.filter((account) => ["draft", "returned"].includes(account.workflow_status ?? "") && myAccountRequest(account)).length;
        const roles = accountRoles.data?.roles ?? [];
        const rolesWorking = roles.filter((role) => role.account).length;
        const rolesAwaiting = roles.filter((role) => ["submitted", "verified"].includes(role.workflow_status)).length;
        const rolesPercent = roles.length ? Math.round((rolesWorking / roles.length) * 100) : 0;
        const tiles: [string, ReactNode, string, ReactNode][] = [
          ["Accounts in use", inUse, "bg-primary-50 text-primary-600", `${chart.length} in the chart`],
          ["Awaiting approval", awaitingAccounts + rolesAwaiting, "bg-blue-50 text-blue-600", `${awaitingAccounts} account${awaitingAccounts === 1 ? "" : "s"} · ${rolesAwaiting} role${rolesAwaiting === 1 ? "" : "s"}`],
          [
            "Roles working",
            <>{rolesWorking}<span className="text-base font-medium text-gray-400"> / {roles.length}</span></>,
            "bg-emerald-50 text-emerald-600",
            <span className="mt-1 block h-1.5 w-full overflow-hidden rounded-full bg-gray-100"><span className="block h-full rounded-full bg-emerald-500" style={{ width: `${rolesPercent}%` }} /></span>,
          ],
          ["Still yours to finish", needsWork + (accountRoles.data?.unassigned_count ?? 0), "bg-amber-50 text-amber-600", `${needsWork} draft or returned · ${accountRoles.data?.unassigned_count ?? 0} roles unset`],
        ];
        return (
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {tiles.map(([label, value, tone, note], index) => (
                <div key={label} className="card flex items-start gap-4 p-4">
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tone}`}>
                    {[<BookOpen key="b" className="h-5 w-5" />, <Clock key="c" className="h-5 w-5" />, <GitBranch key="r" className="h-5 w-5" />, <AlertTriangle key="a" className="h-5 w-5" />][index]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
                    <div className="text-2xl font-bold tabular-nums text-gray-900">{value}</div>
                    <div className="truncate text-xs text-gray-500">{note}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <div role="tablist" aria-label="Ledger setup" className="segmented">
                {([
                  ["chart", "Chart of accounts", chart.length],
                  ["roles", "Account roles", `${rolesWorking}/${roles.length}`],
                ] as const).map(([view, label, count]) => (
                  <button
                    key={view}
                    type="button"
                    role="tab"
                    aria-selected={ledgerView === view}
                    aria-pressed={ledgerView === view}
                    onClick={() => setLedgerView(view)}
                    className="segmented-option px-4 py-1.5 text-sm"
                  >
                    {label}
                    <span className="ml-2 tabular-nums opacity-70">{count}</span>
                  </button>
                ))}
              </div>
              {canCreateAccountRequests && ledgerView === "roles" && (
                <button type="button" className="btn btn-outline h-8 min-h-0 gap-1.5 px-3 py-1 text-xs" onClick={() => openNewAccount()}>
                  <Plus className="h-3.5 w-3.5" /> New account
                </button>
              )}
            </div>

            {ledgerView === "chart" ? (
              <ChartOfAccountsView
                accounts={accounts.data}
                isLoading={accounts.isLoading}
                canCreate={canCreateAccountRequests}
                onNew={openNewAccount}
                actions={(account) => (
                  <>
                    {myAccountRequest(account) && (
                      <>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-primary-700 hover:bg-primary-50"
                          onClick={() => editAccountRequest(account)}
                        >
                          <Edit className="h-3.5 w-3.5" /> Edit
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 rounded bg-primary-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                          disabled={submitAccountRequest.isLoading}
                          onClick={() => submitAccountRequest.mutate(account.id)}
                        >
                          Submit
                        </button>
                      </>
                    )}
                    {canDeleteAccount(account, user?.id, { canApprove, canCreateAccountRequests }) && (
                      <button
                        type="button"
                        title="Delete account"
                        aria-label={`Delete ${account.code} - ${account.name}`}
                        className="sheet-action text-red-600 hover:bg-red-50"
                        disabled={deleteAccount.isLoading}
                        onClick={() => setDialog({
                          title: `Delete ${account.code} - ${account.name}?`,
                          body: accountDeleteMessage(account),
                          confirmLabel: "Delete account",
                          tone: "danger",
                          onConfirm: () => deleteAccount.mutate(account.id, {
                            onSuccess: () => {
                              if (editingAccountId === account.id) resetAccountForm();
                            },
                          }),
                        })}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </>
                )}
              />
            ) : (
              <AccountRolesPanel
                listing={accountRoles.data}
                accounts={accounts.data}
                canPropose={canCreateAccountRequests}
                canReview={canReview}
                canApprove={canApprove}
                onPropose={(data) => proposeRole.mutate(data)}
                onSubmit={(role) => submitRole.mutate(role)}
                onSubmitAll={() => submitAllRoles.mutate(undefined)}
                onVerifyAll={() => verifyAllRoles.mutate(undefined)}
                onApproveAll={() => approveAllRoles.mutate(undefined)}
                onVerify={(data) => verifyRole.mutate(data)}
                onReturn={(data) => returnRole.mutate(data)}
                onApprove={(role) => approveRole.mutate(role)}
                onCreateAccount={canCreateAccountRequests ? openNewAccount : undefined}
              />
            )}

            <Modal
              isOpen={canCreateAccountRequests && (accountFormOpen || editingAccountId !== null)}
              size="2xl"
              title={editingAccountId === null ? "New account" : `Edit ${editingAccount?.code ?? "account"} request`}
              onClose={resetAccountForm}
              footer={
                <>
                  <button type="button" className="btn btn-outline" onClick={resetAccountForm}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="account-form"
                    className="btn btn-primary gap-2"
                    disabled={createAccount.isLoading || updateAccount.isLoading || !accountName.trim()}
                  >
                    {editingAccountId === null ? <Plus className="h-4 w-4" /> : <Edit className="h-4 w-4" />}
                    {editingAccountId === null ? "Create account" : "Save changes"}
                  </button>
                </>
              }
            >
              <form id="account-form" onSubmit={submitAccount} className="space-y-5">
                {error && (
                  <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
                  </div>
                )}
                {editingAccount && (
                  <EditingNotice
                    label={`${editingAccount.code} - ${editingAccount.name}`}
                    reason={editingAccount.workflow_status === "returned" ? editingAccount.return_reason : undefined}
                    onCancel={resetAccountForm}
                  />
                )}

                <fieldset>
                  <legend className="text-sm font-medium text-gray-900">What kind of account is it?</legend>
                  <div className="mt-2 grid gap-2 sm:grid-cols-5">
                    {ACCOUNT_TYPES.map((entry) => (
                      <label
                        key={entry.type}
                        className="flex cursor-pointer flex-col rounded-lg border border-secondary-200 p-2.5 text-xs transition-colors hover:border-primary-300 has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50 has-[:checked]:ring-1 has-[:checked]:ring-primary-500"
                        title={entry.hint}
                      >
                        <input
                          type="radio"
                          name="account_type"
                          value={entry.type}
                          className="sr-only"
                          checked={newAccountType === entry.type}
                          onChange={() => { setNewAccountType(entry.type); setAccountParent(""); setNewAccountCategory(""); }}
                        />
                        <span className="inline-flex items-center gap-1.5 font-semibold text-gray-900">
                          <span className={`h-2 w-2 rounded-full ${entry.dot}`} /> {entry.label}
                        </span>
                      </label>
                    ))}
                  </div>
                  <p className="mt-1.5 text-xs text-gray-500">
                    {ACCOUNT_TYPES.find((entry) => entry.type === newAccountType)?.hint}
                  </p>
                </fieldset>

                <div className="grid gap-4 sm:grid-cols-[8rem_minmax(0,1fr)]">
                  <label className="text-sm font-medium text-gray-700">
                    Code
                    <input
                      readOnly
                      disabled
                      className="input mt-1 bg-secondary-50 font-mono text-gray-500"
                      value={editingAccountId === null ? nextAccountCode : editingAccount?.code ?? ""}
                      aria-label="Account code (assigned automatically)"
                    />
                    <span className="mt-1 block text-xs font-normal text-gray-500">
                      {editingAccountId === null ? "Assigned on save." : "Cannot be changed."}
                    </span>
                  </label>
                  <label className="text-sm font-medium text-gray-700">
                    Account name
                    <input
                      required
                      autoFocus
                      name="name"
                      className="input mt-1"
                      placeholder={{ asset: "e.g. Stanbic Bank – Fees account", liability: "e.g. Amounts owed to suppliers", equity: "e.g. Accumulated fund", income: "e.g. Tuition fees", expense: "e.g. Transport expense" }[newAccountType]}
                      value={accountName}
                      onChange={(event) => setAccountName(event.target.value)}
                    />
                  </label>
                </div>
                <div className="text-sm font-medium text-gray-700">
                  Description <span className="font-normal text-gray-400">(optional)</span>
                  <div className="mt-1 font-normal">
                    <AIAssistField
                      name="description"
                      rows={2}
                      placeholder="What goes into this account, so the next person uses it the same way"
                      value={accountDescription}
                      onChange={(next) => setAccountDescription(next)}
                      context={{
                        key: "financial.account_description",
                        params: {
                          name: accountName,
                          account_type: newAccountType,
                          parent: newAccountStructure === "sub" ? accountParent || null : null,
                        },
                      }}
                      draftLabel="Draft description"
                      draftHint={accountName.trim() ? "Uses the name, type and the accounts beside it." : "Name the account first, then draft."}
                    />
                  </div>
                </div>

                <fieldset>
                  <legend className="text-sm font-medium text-gray-900">Where does it sit?</legend>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    {([
                      ["main", "Main account", "Stands on its own in the trial balance."],
                      ["sub", "Subaccount", "Sits under a main account and rolls up into it."],
                    ] as const).map(([value, label, help]) => (
                      <label
                        key={value}
                        className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-secondary-200 p-3 text-sm transition-colors hover:border-primary-300 has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50 has-[:checked]:ring-1 has-[:checked]:ring-primary-500"
                      >
                        <input
                          type="radio"
                          name="structure"
                          value={value}
                          className="mt-1 accent-primary-600"
                          checked={newAccountStructure === value}
                          onChange={() => { setNewAccountStructure(value); if (value === "main") setAccountParent(""); }}
                        />
                        <span>
                          <span className="font-semibold text-gray-900">{label}</span>
                          <span className="block text-xs text-gray-500">{help}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                  {newAccountStructure === "sub" && (
                    <div className="mt-3">
                      <AccountSelect
                        required
                        name="parent"
                        placeholder="Select the account this sits under…"
                        accounts={accounts.data}
                        value={accountParent}
                        onChange={setAccountParent}
                        isSelectable={(account) =>
                          account.account_type === newAccountType &&
                          account.is_active &&
                          !account.is_control_account &&
                          account.workflow_status === "approved"
                        }
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Only approved {newAccountType} accounts are listed — a subaccount must match its parent's type.
                      </p>
                    </div>
                  )}
                </fieldset>

                {canConfigureAccounting && newAccountStructure === "main" && (
                  <label className="block text-sm font-medium text-gray-900">
                    Where does it report in the financial statements?
                    <select
                      className="input mt-1.5 font-normal"
                      value={newAccountCategory}
                      onChange={(event) => setNewAccountCategory(event.target.value)}
                    >
                      <option value="">Decide later</option>
                      {(statementSections.data ?? [])
                        .filter((section) => section.permitted_account_types.includes(newAccountType))
                        .map((section) => (
                          <optgroup key={section.id} label={section.name}>
                            {section.categories.map((category) => (
                              <option key={category.id} value={category.id}>{category.name}</option>
                            ))}
                          </optgroup>
                        ))}
                    </select>
                    <span className="mt-1 block text-xs font-normal text-gray-500">
                      Left undecided, the account shows in an unplaced list on the statements rather than being left out.
                    </span>
                  </label>
                )}

                <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-2.5 text-xs text-gray-600">
                  <span className="font-mono font-semibold text-gray-900">
                    {editingAccountId === null ? nextAccountCode : editingAccount?.code}
                  </span>
                  {" · "}
                  <span className="font-medium text-gray-900">{accountName.trim() || "Unnamed account"}</span>
                  {" · "}
                  {ACCOUNT_TYPES.find((entry) => entry.type === newAccountType)?.label} posting account
                  {newAccountStructure === "sub" && accountParent && (
                    <> under {accounts.data?.find((account) => String(account.id) === accountParent)?.name}</>
                  )}
                  . It is saved as a request, and nothing can post to it until it is approved.
                </div>
              </form>
            </Modal>
          </div>
        );
      })()}

      {activeTab === "journals" && (
        <div className="space-y-5">
          {canInitiate && <form key={editingJournalId ?? "new"} onSubmit={submitJournal} className="card">
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 px-6 py-4">
              <div>
                <h3 className="font-semibold text-gray-900">{editingJournal ? `Edit ${editingJournal.entry_number}` : "New journal entry"}</h3>
                <p className="mt-1 text-sm text-gray-500">Describe what happened, then list each account it affects. The system works out debit and credit.</p>
              </div>
              <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">Saved as a draft</span>
            </div>
            <div className="card-content space-y-5 pt-5">
              {editingJournal && (
                <EditingNotice
                  label={editingJournal.entry_number}
                  reason={editingJournal.workflow_status === "returned" ? editingJournal.return_reason : undefined}
                  onCancel={resetJournalForm}
                />
              )}
              <div className="grid gap-4 md:grid-cols-3">
                <label className="text-sm font-medium text-gray-700">
                  Transaction template
                  <select
                    className="input mt-1"
                    value={journalTemplate}
                    onChange={(event) => selectJournalTemplate(event.target.value as JournalTemplate)}
                  >
                    <option value="general">General journal</option>
                    <option value="internal_transfer">Cash / bank transfer</option>
                    <option value="immediate_expense">Expense paid immediately</option>
                    <option value="correction">Correction / adjustment</option>
                  </select>
                </label>
                <label className="text-sm font-medium text-gray-700">
                  Entry date
                  <input
                    required
                    type="date"
                    name="entry_date"
                    className="input mt-1"
                    defaultValue={editingJournal?.entry_date ?? today}
                  />
                </label>
                <label className="text-sm font-medium text-gray-700">
                  Voucher / document reference
                  {journalTemplate === "general" && <span className="ml-1 font-normal text-gray-400">(optional)</span>}
                  <input
                    name="reference"
                    className="input mt-1"
                    required={journalTemplate !== "general"}
                    value={journalReference}
                    onChange={(event) => setJournalReference(event.target.value)}
                    placeholder="e.g. PV-0042"
                  />
                </label>
                <label className="text-sm font-medium text-gray-700 md:col-span-3">
                  What happened?
                  <input
                    required
                    name="description"
                    className="input mt-1"
                    placeholder="e.g. Transfer of term fees from Stanbic collections to petty cash"
                    value={journalDescription}
                    onChange={(event) => setJournalDescription(event.target.value)}
                  />
                </label>
              </div>
              {journalTemplate === "internal_transfer" && (
                <div className="rounded-lg bg-primary-50 p-3 text-sm text-primary-800">
                  Choose the destination account first and the source account second. Both must be cash/bank asset accounts; this transfer cannot affect income or expenditure.
                </div>
              )}
              {journalTemplate === "immediate_expense" && (
                <div className="rounded-lg bg-primary-50 p-3 text-sm text-primary-800">
                  Choose the expense account first and the cash/bank account used to pay second.
                </div>
              )}
              <section aria-labelledby="journal-lines-heading" className="space-y-2">
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <h4 id="journal-lines-heading" className="text-sm font-semibold text-gray-900">Affected accounts</h4>
                    <p className="text-xs text-gray-500">State whether each account increases or decreases. Debit and credit fill in on their own.</p>
                  </div>
                  <button
                    type="button"
                    className="pager-button h-8"
                    disabled={journalLinesFixed}
                    onClick={() => setJournalLines((current) => [...current, emptyGuidedJournalLine()])}
                  >
                    <Plus className="h-3.5 w-3.5" /> Add account row
                  </button>
                </div>
                <div className="sheet-wrap">
                  <table className="sheet sheet-entry">
                    <thead>
                      <tr>
                        <th className="sheet-rownum">#</th>
                        <th className="min-w-[18rem]">Account</th>
                        <th className="w-32">Effect</th>
                        <th className="w-40 text-right">Amount (UGX)</th>
                        <th className="w-32 text-right">Debit</th>
                        <th className="w-32 text-right">Credit</th>
                        <th className="min-w-[14rem]">Line note</th>
                        <th className="w-10"><span className="sr-only">Remove</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {journalLines.map((line, index) => {
                        const side = journalLineSide(line);
                        const amount = Number(line.amount || 0);
                        const updateLine = (patch: Partial<GuidedJournalLineState>) =>
                          setJournalLines((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)));
                        return (
                          <tr key={index}>
                            <td className="sheet-rownum">{index + 1}</td>
                            <td>
                              <AccountSelect
                                required
                                ariaLabel={`Affected account ${index + 1}`}
                                placeholder="Select account…"
                                accounts={accounts.data}
                                value={line.account}
                                onChange={(next) => updateLine({ account: next })}
                                isSelectable={(account) => {
                                  if (!canReceivePostings(account)) return false;
                                  if (journalTemplate === "internal_transfer") return account.account_type === "asset";
                                  if (journalTemplate === "immediate_expense") return index === 0
                                    ? account.account_type === "expense"
                                    : account.account_type === "asset";
                                  return true;
                                }}
                              />
                            </td>
                            <td>
                              <select
                                className="sheet-input"
                                aria-label={`Effect on account ${index + 1}`}
                                value={line.effect}
                                disabled={journalLinesFixed}
                                onChange={(event) => updateLine({ effect: event.target.value as GuidedJournalLineState["effect"] })}
                              >
                                <option value="increase">Increases</option>
                                <option value="decrease">Decreases</option>
                              </select>
                            </td>
                            <td>
                              <input
                                required
                                min="0.01"
                                step="0.01"
                                type="number"
                                inputMode="decimal"
                                className="sheet-input text-right tabular-nums"
                                placeholder="0"
                                aria-label={`Amount for account ${index + 1}`}
                                value={line.amount}
                                onChange={(event) => updateLine({ amount: event.target.value })}
                              />
                            </td>
                            <td className="text-right tabular-nums text-gray-700">
                              {side === "Debit" && amount > 0 ? amount.toLocaleString() : <span className="text-gray-300">—</span>}
                            </td>
                            <td className="text-right tabular-nums text-gray-700">
                              {side === "Credit" && amount > 0 ? amount.toLocaleString() : <span className="text-gray-300">—</span>}
                            </td>
                            <td>
                              <input
                                className="sheet-input"
                                placeholder="Optional"
                                aria-label={`Line note for account ${index + 1}`}
                                value={line.description}
                                onChange={(event) => updateLine({ description: event.target.value })}
                              />
                            </td>
                            <td className="text-center">
                              {journalLines.length > 2 && !journalLinesFixed && (
                                <button
                                  type="button"
                                  className="sheet-action text-red-600"
                                  aria-label={`Remove affected account ${index + 1}`}
                                  title="Remove this row"
                                  onClick={() => setJournalLines((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-100 font-semibold text-gray-900">
                        <td className="sheet-rownum" />
                        <td colSpan={3} className="text-xs uppercase tracking-wide text-gray-600">Totals</td>
                        <td className="text-right tabular-nums">{journalTotals.debit.toLocaleString()}</td>
                        <td className="text-right tabular-nums">{journalTotals.credit.toLocaleString()}</td>
                        <td colSpan={2}>
                          {journalBalanced ? (
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700">
                              <span className="h-1.5 w-1.5 rounded-full bg-green-500" /> Balanced
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700">
                              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                              {journalTotals.debit + journalTotals.credit > 0
                                ? `Out by UGX ${Math.abs(journalTotals.debit - journalTotals.credit).toLocaleString()}`
                                : "Enter amounts"}
                            </span>
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </section>
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4">
                <p className="text-sm text-gray-500">
                  {journalTemplate === "internal_transfer" ? "Transfers" : "Manual journals"} are saved as drafts for approval by a different authorised user.
                </p>
                <div className="flex items-center gap-2">
                  {editingJournalId !== null && (
                    <button type="button" className="btn btn-outline" onClick={resetJournalForm}>Cancel</button>
                  )}
                  <button
                    className="btn btn-primary"
                    disabled={!journalBalanced || createJournal.isPending || updateJournal.isPending}
                  >
                    {editingJournalId === null ? "Save draft journal" : "Save changes"}
                  </button>
                </div>
              </div>
            </div>
          </form>}

          <div className="card">
            <div className="card-header pb-4">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                    <BookOpen className="h-5 w-5" /> Journal register
                  </h2>
                  <p className="text-sm text-gray-500">Drafts waiting for approval, posted entries, and reversals. Click a row to see its account lines.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-gray-500">Export</span>
                  {(
                    [
                      ["csv", FileText, "CSV"],
                      ["excel", FileSpreadsheet, "Excel"],
                      ["pdf", Printer, "PDF"],
                    ] as const
                  ).map(([format, Icon, label]) => (
                    <button
                      key={format}
                      type="button"
                      className="pager-button h-8"
                      disabled={isExportingJournals || !journalCount}
                      onClick={() => exportJournals(format)}
                      title={`Download the filtered register as ${label}`}
                    >
                      <Icon className="h-3.5 w-3.5" /> {label}
                    </button>
                  ))}
                  {isExportingJournals && <span className="text-xs text-gray-500">Preparing export…</span>}
                </div>
              </div>
            </div>
            <div className="card-content space-y-4">
              <div className="filter-panel">
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div role="group" aria-label="Journal status" className="segmented">
                    {["all", ...JOURNAL_STATUSES].map((status) => (
                      <button
                        key={status}
                        type="button"
                        aria-pressed={journalStatusFilter === status}
                        onClick={() => { setJournalStatusFilter(status); setJournalPage(1); }}
                        className="segmented-option capitalize"
                      >
                        {status === "all" ? "All entries" : status === "draft" ? "Drafts" : status}
                        {status === "draft" && Boolean(draftJournals.data?.count) && (
                          <span className="ml-1.5 tabular-nums opacity-70">{draftJournals.data?.count}</span>
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-1 flex-wrap items-end justify-end gap-3">
                    <label className="filter-field w-full sm:w-80">
                      Search
                      <div className="relative">
                        <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                        <input
                          type="search"
                          className="input-compact pl-8"
                          placeholder="Entry number, description, or reference"
                          value={journalSearch}
                          onChange={(event) => { setJournalSearch(event.target.value); setJournalPage(1); }}
                        />
                      </div>
                    </label>
                    {journalFiltersActive && (
                      <button
                        type="button"
                        onClick={() => { setJournalSearch(""); setJournalStatusFilter("all"); setJournalPage(1); }}
                        className="inline-flex h-8 items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                      >
                        <X className="h-3.5 w-3.5" /> Clear filters
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {journals.isLoading ? (
                <div className="py-10 text-center text-sm text-gray-500">Loading journal entries…</div>
              ) : journals.isError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                  The journal register could not be loaded.{" "}
                  <button type="button" className="font-medium underline" onClick={() => journals.refetch()}>Try again</button>
                </div>
              ) : !visibleJournals.length ? (
                <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
                  {journalFiltersActive ? "No journal entries match the active filters." : "No journal entries have been made yet."}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className={`sheet-wrap ${journals.isFetching ? "opacity-60" : ""}`}>
                    <table className="sheet">
                      <thead>
                        <tr>
                          <th className="sheet-rownum">#</th>
                          <th>Entry no.</th>
                          <th>Date</th>
                          <th>Description</th>
                          <th>Reference</th>
                          <th>Type</th>
                          <th className="text-right">Amount (UGX)</th>
                          <th>Stage</th>
                          <th>Prepared by</th>
                          <th className="text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleJournals.map((entry, index) => {
                          const stage = entry.workflow_status || entry.status;
                          const expanded = expandedJournalId === entry.id;
                          const amount = entry.lines.reduce((total, line) => total + Number(line.debit), 0);
                          const awaitingDecision = ["submitted", "verified"].includes(entry.workflow_status || "") && entry.created_by !== user?.id;
                          return (
                            <Fragment key={entry.id}>
                              <tr
                                className="cursor-pointer"
                                aria-expanded={expanded}
                                onClick={() => setExpandedJournalId(expanded ? null : entry.id)}
                              >
                                <td className="sheet-rownum">
                                  <span className="inline-flex items-center gap-1">
                                    <ChevronRight className={`h-3 w-3 transition-transform ${expanded ? "rotate-90" : ""}`} />
                                    {firstJournalRow + index}
                                  </span>
                                </td>
                                <td className="font-mono text-xs text-gray-800">{entry.entry_number}</td>
                                <td className="tabular-nums text-gray-700">{entry.entry_date}</td>
                                <td className="max-w-[20rem] truncate font-medium text-gray-900" title={entry.description}>{entry.description}</td>
                                <td className="font-mono text-xs text-gray-600">{entry.reference || <span className="font-sans text-gray-300">—</span>}</td>
                                <td className="capitalize text-gray-600">{entry.transaction_type.replace(/_/g, " ")}</td>
                                <td className="text-right tabular-nums font-medium text-gray-900">{amount.toLocaleString()}</td>
                                <td>
                                  <span
                                    className="inline-flex items-center gap-1.5 text-xs font-medium capitalize text-gray-700"
                                    title={entry.workflow_status === "returned" && entry.return_reason ? `Sent back: ${entry.return_reason}` : undefined}
                                  >
                                    <span className={`h-1.5 w-1.5 rounded-full ${JOURNAL_STAGE_DOTS[stage] || "bg-gray-400"}`} />
                                    {stage}
                                  </span>
                                </td>
                                <td className="text-gray-600">{entry.created_by_name || <span className="text-gray-300">—</span>}</td>
                                <td className="text-right" onClick={(event) => event.stopPropagation()}>
                                  <span className="inline-flex items-center justify-end gap-0.5">
                                    {entry.status === "draft" && (
                                      <>
                                        {myUnsubmitted(entry, entry.created_by) && (
                                          <button type="button" className={journalActionClass} onClick={() => editJournal(entry)}>
                                            <Edit className="h-3.5 w-3.5" /> Edit
                                          </button>
                                        )}
                                        {canInitiate && ["draft", "returned"].includes(entry.workflow_status || "") && (
                                          <button type="button" className={journalActionClass} onClick={() => submitJournalForApproval.mutate(entry.id)}>Submit</button>
                                        )}
                                        {awaitingDecision && canReview && (
                                          <button
                                            type="button"
                                            className={`${journalActionClass} text-amber-700 hover:bg-amber-50`}
                                            onClick={() => {
                                              const reason = window.prompt(`What needs to change on ${entry.entry_number}?`)?.trim();
                                              if (reason) returnJournal.mutate({ id: entry.id, reason });
                                            }}
                                          >Return</button>
                                        )}
                                        {awaitingDecision && canApprove && (
                                          <button type="button" className={journalActionClass} onClick={() => postJournal.mutate(entry.id)}>Approve &amp; post</button>
                                        )}
                                        {awaitingDecision && (
                                          <button
                                            type="button"
                                            className={`${journalActionClass} text-red-600 hover:bg-red-50`}
                                            onClick={() => {
                                              const reason = window.prompt(`Reason for rejecting ${entry.entry_number}`)?.trim();
                                              if (reason) rejectJournal.mutate({ id: entry.id, reason });
                                            }}
                                          >Reject</button>
                                        )}
                                        <button
                                          type="button"
                                          className="sheet-action text-red-600"
                                          title={`Delete draft ${entry.entry_number}`}
                                          aria-label={`Delete draft ${entry.entry_number}`}
                                          onClick={() => { if (window.confirm(`Delete draft ${entry.entry_number}?`)) deleteJournal.mutate(entry.id); }}
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                      </>
                                    )}
                                    {canReverse && entry.status === "posted" && !entry.source_type && (
                                      <button
                                        type="button"
                                        className={`${journalActionClass} text-red-600 hover:bg-red-50`}
                                        onClick={() => {
                                          const reason = window.prompt(`Reason for reversing ${entry.entry_number}`)?.trim();
                                          if (reason) reverseJournal.mutate({ id: entry.id, reason });
                                        }}
                                      >Reverse</button>
                                    )}
                                  </span>
                                </td>
                              </tr>
                              {expanded && (
                                <tr className="!bg-white hover:!bg-white">
                                  <td className="sheet-rownum" />
                                  <td colSpan={9} className="whitespace-normal bg-gray-50/40 px-3 py-2">
                                    {entry.workflow_status === "returned" && entry.return_reason && (
                                      <p className="mb-2 text-xs text-amber-700">Sent back: {entry.return_reason}</p>
                                    )}
                                    <table className="w-full max-w-3xl text-xs">
                                      <thead className="text-[11px] uppercase tracking-wide text-gray-500">
                                        <tr>
                                          <th className="py-1 pr-3 text-left font-semibold">Account</th>
                                          <th className="py-1 pr-3 text-left font-semibold">Line note</th>
                                          <th className="py-1 pr-3 text-right font-semibold">Debit</th>
                                          <th className="py-1 text-right font-semibold">Credit</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-100">
                                        {entry.lines.map((line, lineIndex) => (
                                          <tr key={line.id ?? lineIndex}>
                                            <td className="py-1 pr-3 text-gray-800">
                                              <span className="font-mono">{line.account_code}</span> · {line.account_name}
                                            </td>
                                            <td className="py-1 pr-3 text-gray-500">{line.description || "—"}</td>
                                            <td className="py-1 pr-3 text-right tabular-nums">{Number(line.debit) ? Number(line.debit).toLocaleString() : ""}</td>
                                            <td className="py-1 text-right tabular-nums">{Number(line.credit) ? Number(line.credit).toLocaleString() : ""}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                    {entry.posted_by_name && <p className="mt-2 text-xs text-gray-500">Posted by {entry.posted_by_name}</p>}
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <Pager
                    first={firstJournalRow}
                    shown={visibleJournals.length}
                    total={journalCount}
                    noun={journalCount === 1 ? "entry" : "entries"}
                    page={journalPage}
                    pageCount={journalPageCount}
                    onPage={setJournalPage}
                    pageSize={journalPageSize}
                    pageSizes={JOURNAL_PAGE_SIZES}
                    onPageSize={(size) => {
                      setJournalPageSize(size);
                      setJournalPage(1);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "periods" && (
        <AccountingPeriodsPanel
          periods={periods.data}
          isLoading={periods.isLoading}
          currentUserId={user?.id}
          canConfigure={canConfigureAccounting}
          canClose={canClosePeriod}
          canRequestReopen={canRequestPeriodReopen}
          canReview={canReview}
          canReopen={canReopenPeriod}
          onCreate={(payload, done) => createPeriod.mutate(payload, { onSuccess: done })}
          onClose={(id) => closePeriod.mutate(id)}
          onRequestReopen={(payload) => requestPeriodReopen.mutate(payload)}
          onVerifyReopen={(payload) => verifyPeriodReopen.mutate(payload)}
          onReturnReopen={(payload) => returnPeriodReopen.mutate(payload)}
          onRejectReopen={(payload) => rejectPeriodReopen.mutate(payload)}
          onApproveReopen={(id) => approvePeriodReopen.mutate(id)}
        />
      )}

      {activeTab === "payables" && (() => {
        const bill = bills.data?.find((entry) => entry.id === viewingBillId) ?? null;
        const allBills = bills.data ?? [];
        const openBill = (id: number) => {
          const target = allBills.find((entry) => entry.id === id);
          setBillDecisionNotes("");
          setMatchBillId(target?.purchase_order ? id : null);
          setViewingBillId(id);
        };
        const closeBill = () => {
          setViewingBillId(null);
          setMatchBillId(null);
        };
        return (
        <div className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-primary-100 bg-primary-50 p-5">
            <div><h2 className="font-semibold text-primary-950">Supplier payables</h2><p className="mt-1 max-w-2xl text-sm text-primary-800">Record what suppliers bill the school, get each bill reviewed and approved, then pay it from the right account.</p></div>
            <div className="flex items-center gap-3 text-sm">
              <span className="rounded-md bg-white px-3 py-2 font-medium text-amber-700">{allBills.filter((entry) => ["submitted", "verified"].includes(entry.workflow_status ?? "")).length} awaiting approval</span>
              <span className="rounded-md bg-white px-3 py-2 font-medium text-gray-700">UGX {allBills.filter(billPayable).reduce((sum, entry) => sum + Number(entry.balance_due), 0).toLocaleString()} to pay</span>
              <span className="rounded-md bg-white px-3 py-2 font-medium text-red-700">{allBills.filter(billOverdue).length} overdue</span>
            </div>
          </div>

          {canManageSuppliers && showSupplierForm && (
            <form onSubmit={submitSupplier} className="card">
              <div className="card-titlebar">
                <div>
                  <h3 className="font-semibold text-gray-900">New supplier</h3>
                  <p className="mt-1 text-sm text-gray-500">A vendor the school has not dealt with before. It can be billed straight away.</p>
                </div>
                <button type="button" onClick={() => setShowSupplierForm(false)} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close the new supplier form">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="card-content pt-5">
                <FormSection title="Supplier" divided={false} columns={3}>
                  <Field label="Name"><input required name="name" className="input mt-1" placeholder="e.g. Kampala Stationers Ltd" /></Field>
                  <Field label="Contact person" optional><input name="contact_person" className="input mt-1" /></Field>
                  <Field label="Phone" optional><input name="phone" className="input mt-1" /></Field>
                  <Field label="Email" optional><input type="email" name="email" className="input mt-1" /></Field>
                  <Field label="Payment terms" optional><input name="terms" className="input mt-1" placeholder="e.g. 30 days" /></Field>
                </FormSection>
              </div>
              <div className="card-footer">
                <button disabled={createSupplier.isPending} className="btn btn-primary">Add supplier</button>
              </div>
            </form>
          )}

          {canInitiate && (showBillForm || editingBill) && <form key={editingBillId ?? "new"} onSubmit={submitBill} className="card">
            <div className="card-titlebar">
              <div>
                <h3 className="font-semibold text-gray-900">{editingBill ? `Edit ${billNumber(editingBill)}` : "Record a supplier bill"}</h3>
                <p className="mt-1 text-sm text-gray-500">Saved as a draft; submit it for review from the register.</p>
              </div>
              <button type="button" onClick={resetBillForm} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close the bill form">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="card-content space-y-5 pt-5">
              {editingBill && (
                <EditingNotice
                  label={editingBill.internal_number || editingBill.bill_number}
                  reason={editingBill.workflow_status === "returned" ? editingBill.return_reason : undefined}
                  onCancel={resetBillForm}
                />
              )}
              <VendorBillFields
                editing={editingBill}
                suppliers={suppliers.data ?? []}
                purchaseOrders={purchaseOrders.data ?? []}
                accounts={accounts.data}
                orderId={billOrderId}
                onOrderChange={setBillOrderId}
                chargeable={chargeableForABill}
                canHoldPayable={(a) => canReceivePostings(a) && a.account_type === "liability"}
                today={today}
              />
            {billOrderId && (
              <div className="md:col-span-4">
                <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                  What is being charged for
                </div>
                <p className="mb-2 text-xs text-gray-500">
                  Taken from the order. Quantities start at what has actually been received, since
                  a bill for more than arrived will not be approved. The lines must add up to the
                  amount above.
                </p>
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                      <th className="py-1 pr-3">Item</th>
                      <th className="py-1 pr-3">Ordered</th>
                      <th className="py-1 pr-3">Received</th>
                      <th className="py-1 pr-3">Charging for</th>
                      <th className="py-1">Unit price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(purchaseOrders.data?.find((order) => String(order.id) === billOrderId)?.items ?? []).map((line) => (
                      <tr key={line.id}>
                        <td className="py-1 pr-3">
                          {line.item_name}
                          <input type="hidden" name="line_item" value={line.item} />
                          <input type="hidden" name="line_description" value={line.item_name || "Item"} />
                        </td>
                        <td className="py-1 pr-3 tabular-nums text-gray-500">{line.quantity_ordered}</td>
                        <td className="py-1 pr-3 tabular-nums text-gray-500">{line.quantity_received}</td>
                        <td className="py-1 pr-3">
                          {/* While a bill is being corrected the rows start from
                              what it already charges, not from what the order
                              says arrived. */}
                          <input
                            name="line_quantity"
                            type="number"
                            min="0"
                            step="1"
                            defaultValue={
                              editingBill?.lines?.find((billed) => billed.item === line.item)?.quantity
                              ?? line.quantity_received
                            }
                            className="input w-24"
                          />
                        </td>
                        <td className="py-1">
                          <input
                            name="line_unit_amount"
                            type="number"
                            min="0"
                            step="0.01"
                            defaultValue={
                              editingBill?.lines?.find((billed) => billed.item === line.item)?.unit_amount
                              ?? line.unit_cost
                            }
                            className="input w-32"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            </div>
            <div className="card-footer">
              <button type="button" onClick={resetBillForm} className="btn-outline">Cancel</button>
              <button disabled={createBill.isPending || updateBill.isPending} className="btn btn-primary">{editingBillId === null ? "Record supplier bill" : "Save changes"}</button>
            </div>
          </form>}

          <ClientRegister
            title="Supplier bills"
            description="Open a bill to check it against its order, review, approve or pay it."
            actions={
              <>
                {canManageSuppliers && !showSupplierForm && (
                  <button type="button" onClick={() => setShowSupplierForm(true)} className="btn-outline h-8 min-h-8 px-3 text-xs">
                    <Plus className="mr-1.5 h-3.5 w-3.5" /> New supplier
                  </button>
                )}
                {canInitiate && !showBillForm && !editingBill && (
                  <button type="button" onClick={() => setShowBillForm(true)} className="btn-primary h-8 min-h-8 px-3 text-xs">
                    <Plus className="mr-1.5 h-3.5 w-3.5" /> Record bill
                  </button>
                )}
              </>
            }
            rows={bills.data}
            config={payablesConfig}
            columns={payablesColumns}
            rowKey={(entry) => entry.id}
            onOpen={(entry) => openBill(entry.id)}
            openLabel={(entry) =>
              (canReview && entry.workflow_status === "submitted" && entry.prepared_by !== user?.id) ||
              (canApprove && entry.workflow_status === "verified" && entry.prepared_by !== user?.id)
                ? "Review"
                : canInitiate && billPayable(entry) ? "Pay" : "Open"
            }
            noun={["bill", "bills"]}
            exportName={{ file: "supplier-bills", title: "Supplier Bills" }}
            isLoading={bills.isLoading}
            isError={bills.isError}
            onRetry={() => bills.refetch()}
            emptyText="No supplier bills recorded yet."
          />

          {bill && (() => {
            const b = bill;
            const stage = billStage(b);
            const mine = b.prepared_by === user?.id;
            const inReview = ["submitted", "verified"].includes(b.workflow_status ?? "");
            const canVerify = canReview && b.workflow_status === "submitted" && !mine;
            const canApproveThis = canApprove && b.workflow_status === "verified" && !mine;
            const canSendBack = canReview && inReview && !mine;
            const notes = billDecisionNotes.trim();
            return (
              <DetailsModal
                size="3xl"
                title={`Bill ${billNumber(b)} · ${b.supplier_name}`}
                headline={{
                  label: "Balance due",
                  value: `UGX ${Number(b.balance_due).toLocaleString()}`,
                  note: `of UGX ${Number(b.total_amount).toLocaleString()} · UGX ${Number(b.paid_amount).toLocaleString()} paid`,
                }}
                status={stage}
                facts={[
                  ["Supplier", b.supplier_name],
                  ["Their invoice no.", b.bill_number],
                  ["Our bill no.", b.internal_number],
                  ["Invoice date", b.bill_date],
                  ["Due", <>{b.due_date}{billOverdue(b) && <span className="ml-1.5 text-red-600">· overdue</span>}</>],
                  ["Purchase order", b.purchase_order_number],
                  ["Payable account", b.supplier_payable_account_name],
                  ["Prepared by", mine ? "You" : b.prepared_by_name],
                  ["Approved by", b.approved_by_name ? `${b.approved_by_name}${b.approved_at ? ` · ${b.approved_at.slice(0, 10)}` : ""}` : ""],
                ]}
                sections={[
                  { title: "What it is for", body: b.description },
                  { title: "Sent back", body: b.workflow_status === "returned" ? b.return_reason : "", tone: "warning" },
                  { title: "Rejected", body: b.workflow_status === "rejected" ? b.rejection_reason : "", tone: "danger" },
                ]}
                onClose={closeBill}
                footer={
                  <>
                    {myUnsubmitted(b, b.prepared_by) && (
                      <button
                        type="button"
                        onClick={() => {
                          closeBill();
                          editBill(b);
                        }}
                        className="mr-auto inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50"
                      >
                        <Edit className="h-4 w-4" /> Edit
                      </button>
                    )}
                    <button type="button" onClick={closeBill} className="btn-outline">Close</button>
                    {canInitiate && ["draft", "returned"].includes(b.workflow_status || "") && (
                      <button type="button" disabled={submitBillForApproval.isPending} onClick={() => submitBillForApproval.mutate(b.id)} className="btn btn-primary">
                        Submit for review
                      </button>
                    )}
                  </>
                }
              >
                {!!b.lines?.length && (
                  <section>
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Lines</h4>
                    <div className="sheet-wrap mt-2 max-h-none">
                      <table className="sheet">
                        <thead><tr><th>Item</th><th className="text-right">Qty</th><th className="text-right">Unit (UGX)</th><th className="text-right">Total (UGX)</th></tr></thead>
                        <tbody>
                          {b.lines.map((line, index) => (
                            <tr key={line.id ?? index}>
                              <td className="text-gray-900">{line.item_name || line.description}</td>
                              <td className="text-right tabular-nums">{Number(line.quantity)}</td>
                              <td className="text-right tabular-nums text-gray-600">{Number(line.unit_amount).toLocaleString()}</td>
                              <td className="text-right tabular-nums text-gray-900">{Number(line.line_total ?? Number(line.quantity) * Number(line.unit_amount)).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}
                {b.purchase_order && !billMatch.data && billMatch.isLoading && <p className="text-sm text-gray-500">Checking against the order…</p>}
                {billMatch.data && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h4 className="font-medium text-gray-900">
                        Ordered, received, charged for
                        {billMatch.data.purchase_order ? ` · ${billMatch.data.purchase_order.order_number}` : ""}
                      </h4>
                      <span className={billMatch.data.matched ? "text-sm font-medium text-green-700" : "text-sm font-medium text-red-700"}>
                        {billMatch.data.matched ? "Agrees with the order" : "Does not agree"}
                      </span>
                    </div>
                    <table className="mt-2 min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                          <th className="py-1 pr-4">Item</th>
                          <th className="py-1 pr-4 text-right">Ordered</th>
                          <th className="py-1 pr-4 text-right">Received</th>
                          <th className="py-1 pr-4 text-right">Charged for</th>
                          <th className="py-1 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {billMatch.data.lines.map((line) => (
                          <tr key={`${line.item}-${line.name}`} className="border-t border-gray-200">
                            <td className="py-1 pr-4">{line.name}</td>
                            <td className="py-1 pr-4 text-right tabular-nums">{Number(line.quantity_ordered)}</td>
                            <td className="py-1 pr-4 text-right tabular-nums">{Number(line.quantity_received)}</td>
                            <td className={`py-1 pr-4 text-right tabular-nums ${Number(line.quantity_billed) > Number(line.quantity_received) ? "font-semibold text-red-700" : ""}`}>
                              {Number(line.quantity_billed)}
                            </td>
                            <td className="py-1 text-right tabular-nums">UGX {Number(line.amount_billed).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {billMatch.data.blocking.map((message) => (
                      <p key={message} className="mt-2 text-sm font-medium text-red-700">{message}</p>
                    ))}
                    {billMatch.data.warnings.map((message) => (
                      <p key={message} className="mt-1 text-sm text-amber-700">{message}</p>
                    ))}
                    {billMatch.data.blocking.length > 0 && (
                      <p className="mt-2 text-xs text-gray-500">
                        A bill charging for more than was received cannot be approved. Either the
                        delivery has not been recorded yet, or the invoice is wrong.
                      </p>
                    )}
                  </div>
                )}
                {inReview && mine && (
                  <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">You prepared this bill, so someone else reviews and approves it.</p>
                )}
                {(canVerify || canApproveThis || canSendBack) && (
                  <ActionPanel
                    title={canApproveThis ? "Approve this bill" : "Review this bill"}
                    description={canApproveThis ? "Approving puts it in the list to pay." : "Mark it reviewed to pass it to an approver, or send it back if something needs to change."}
                  >
                    <Field as="div" label="Comments" hint="Needed to send it back, so the preparer knows what to change.">
                      <AIAssistField
                        rows={2}
                        value={billDecisionNotes}
                        onChange={(next) => setBillDecisionNotes(next)}
                        context={{ key: "financial.approval_note", params: { document_type: "financial.vendorbill", id: b.id, purpose: "return" } }}
                        draftLabel="Draft what to change"
                        draftHint="Reads this bill. Jot rough notes first for a better draft."
                      />
                    </Field>
                    <div className="flex flex-wrap justify-end gap-2">
                      {canSendBack && (
                        <button
                          type="button"
                          disabled={!notes || returnBill.isPending}
                          title={notes ? undefined : "Write what needs to change"}
                          onClick={() => returnBill.mutate({ id: b.id, reason: notes }, { onSuccess: closeBill })}
                          className="rounded-md border border-amber-200 px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-50 disabled:opacity-50"
                        >
                          Send back
                        </button>
                      )}
                      {canVerify && (
                        <button type="button" disabled={verifyBill.isPending} onClick={() => verifyBill.mutate({ id: b.id }, { onSuccess: closeBill })} className="btn btn-primary">
                          Mark reviewed
                        </button>
                      )}
                      {canApproveThis && (
                        <button
                          type="button"
                          disabled={approveBill.isPending || Boolean(billMatch.data?.blocking.length)}
                          title={billMatch.data?.blocking.length ? "It charges for more than was received" : undefined}
                          onClick={() => {
                            if (window.confirm(`Approve ${billNumber(b)} from ${b.supplier_name} for UGX ${Number(b.total_amount).toLocaleString()}?`)) {
                              approveBill.mutate(b.id, { onSuccess: closeBill });
                            }
                          }}
                          className="btn btn-primary"
                        >
                          Approve bill
                        </button>
                      )}
                    </div>
                  </ActionPanel>
                )}
                {canInitiate && billPayable(b) && (
                  <ActionPanel title="Pay this bill" description="A voucher number is issued for the payment; the reference is the bank's or the cheque's.">
                    <form onSubmit={(event) => submitBillPayment(event, b)} className="grid gap-4 sm:grid-cols-2">
                      <Field label="Amount (UGX)" hint={`Up to UGX ${Number(b.balance_due).toLocaleString()}.`}>
                        <input required name="amount" type="number" min="0.01" step="0.01" max={b.balance_due} defaultValue={b.balance_due} className="input mt-1 tabular-nums" />
                      </Field>
                      <Field as="div" label="Paid from">
                        <AccountSelect required name="payment_account" placeholder="Cash or bank account" accounts={accounts.data} isSelectable={(a) => moneyLandsHere(a) && a.can_pay_from !== false} />
                      </Field>
                      <Field label="Bank or cheque reference">
                        <input required name="reference" className="input mt-1" placeholder="e.g. CHQ-00214" />
                      </Field>
                      <Field label="Payment date">
                        <input required name="payment_date" type="date" defaultValue={today} max={today} className="input mt-1" />
                      </Field>
                      <div className="flex justify-end sm:col-span-2">
                        <button disabled={payBill.isPending} className="btn btn-primary">Record payment</button>
                      </div>
                    </form>
                  </ActionPanel>
                )}
              </DetailsModal>
            );
          })()}
        </div>
        );
      })()}

      {activeTab === "banking" && (() => {
        const bankName = (id: number) => banks.data?.find((bank) => bank.id === id)?.name ?? "";
        const suggestionsFor = (lineId: number) => bankSuggestions.data?.find((item) => item.bank_line === lineId)?.suggestions ?? [];
        const lines = bankLineRegister(bankName, (line) => suggestionsFor(line.id)[0]?.score);
        const line = bankLines.data?.find((entry) => entry.id === viewingBankLineId) ?? null;
        const deposit = bankDeposits.data?.find((entry) => entry.id === viewingDepositId) ?? null;
        const unidentified = unidentifiedReceipts.data?.find((entry) => entry.id === viewingUnidentifiedId) ?? null;
        const openRecord = (setter: (id: number | null) => void, id: number) => {
          setBankingNote("");
          setter(id);
        };
        const activeBanks = banks.data?.filter((bank) => bank.is_active) ?? [];
        const unmatchedCount = bankLines.data?.filter((entry) => !entry.matched_entry).length ?? 0;
        const waitingDeposits = bankDeposits.data?.filter((entry) => depositStatus(entry).key === "waiting").length ?? 0;
        const unresolvedCount = unidentifiedReceipts.data?.filter((entry) => entry.status === "unresolved").length ?? 0;
        const formButton = (form: typeof bankingForm, label: string, primary = true) => (
          <button
            type="button"
            onClick={() => setBankingForm(bankingForm === form ? "" : form)}
            className={`${primary ? "btn-primary" : "btn-outline"} h-8 min-h-8 px-3 text-xs`}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" /> {label}
          </button>
        );
        const closeButton = (label: string) => (
          <button type="button" onClick={() => setBankingForm("")} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label={`Close the ${label} form`}>
            <X className="h-4 w-4" />
          </button>
        );
        return (
        <div className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-primary-100 bg-primary-50 p-5">
            <div><h2 className="font-semibold text-primary-950">Banking</h2><p className="mt-1 max-w-2xl text-sm text-primary-800">Bring in the bank's statements, match each line to the books, and account for money banked or received from unknown payers.</p></div>
            <div className="flex items-center gap-3 text-sm">
              <span className="rounded-md bg-white px-3 py-2 font-medium text-amber-700">{unmatchedCount} lines to match</span>
              <span className="rounded-md bg-white px-3 py-2 font-medium text-gray-700">{waitingDeposits} deposits not on a statement</span>
              <span className="rounded-md bg-white px-3 py-2 font-medium text-amber-700">{unresolvedCount} unidentified</span>
            </div>
          </div>

          <div role="tablist" aria-label="Banking" className="segmented">
            {([
              ["reconcile", "Reconcile", unmatchedCount],
              ["deposits", "Money banked", bankDeposits.data?.length ?? 0],
              ["unidentified", "Unidentified money", unresolvedCount],
              ["setup", "Accounts & imports", banks.data?.length ?? 0],
            ] as const).map(([view, label, count]) => (
              <button
                key={view}
                type="button"
                role="tab"
                aria-selected={bankingView === view}
                aria-pressed={bankingView === view}
                onClick={() => {
                  setBankingView(view);
                  setBankingForm("");
                }}
                className="segmented-option px-4 py-1.5 text-sm"
              >
                {label}
                <span className="ml-2 tabular-nums opacity-70">{count}</span>
              </button>
            ))}
          </div>

          {bankingView === "reconcile" && (
            <>
              {canReconcile && bankingForm === "import" && (
                <form onSubmit={submitBankImport} className="card">
                  <div className="card-titlebar">
                    <div>
                      <h3 className="font-semibold text-gray-900">Import a bank statement</h3>
                      <p className="mt-1 text-sm text-gray-500">The CSV file as the bank gives it. Lines already imported are skipped.</p>
                    </div>
                    {closeButton("import statement")}
                  </div>
                  <div className="card-content space-y-4 pt-5">
                    <FormSection title="Statement" divided={false} columns={3}>
                      <Field label="Bank account">
                        <select required name="bank" className="input mt-1">
                          <option value="">Choose a bank account</option>
                          {activeBanks.map((bank) => <option key={bank.id} value={bank.id}>{bank.name}</option>)}
                        </select>
                      </Field>
                      <Field
                        label="CSV file"
                        className="sm:col-span-2"
                        hint="Account details above the columns are skipped. Columns may be named Date or Tran Date, Description, Narration or Particulars, Reference, and either Amount or Debit and Credit. A Balance column is checked line by line."
                      >
                        <input required name="file" type="file" accept=".csv,text/csv" className="input-file mt-1" />
                      </Field>
                    </FormSection>
                {bankImportResult && (
                  <div className={`rounded-md border p-3 text-xs ${bankImportResult.error_rows ? "border-red-200 bg-red-50 text-red-800" : "border-green-200 bg-green-50 text-green-800"}`} role="status">
                    <p className="font-medium">
                      {bankImportResult.imported_rows} new line{bankImportResult.imported_rows === 1 ? "" : "s"}
                      {bankImportResult.period_start ? `, ${bankImportResult.period_start} to ${bankImportResult.period_end}` : ""}
                      {bankImportResult.duplicate_rows ? `; ${bankImportResult.duplicate_rows} already imported` : ""}
                      {bankImportResult.error_rows ? `; ${bankImportResult.error_rows} could not be read` : ""}
                    </p>
                    {[...bankImportResult.errors, ...bankImportResult.warnings].slice(0, 6).map((item) => (
                      <p key={`${item.row}-${item.message}`} className={bankImportResult.errors.includes(item as { row: number; message: string }) ? "text-red-700" : "text-amber-800"}>
                        {item.row ? `Row ${item.row}: ` : ""}{item.message}
                      </p>
                    ))}
                  </div>
                )}
                  </div>
                  <div className="card-footer">
                    <button className="btn btn-primary" disabled={importBankStatement.isPending}>{importBankStatement.isPending ? "Importing…" : "Import statement"}</button>
                  </div>
                </form>
              )}

              {canReconcile && bankingForm === "line" && (
                <form onSubmit={submitBankLine} className="card">
                  <div className="card-titlebar">
                    <div>
                      <h3 className="font-semibold text-gray-900">Add a statement line by hand</h3>
                      <p className="mt-1 text-sm text-gray-500">For a line the bank shows that is not in an imported file.</p>
                    </div>
                    {closeButton("manual line")}
                  </div>
                  <div className="card-content pt-5">
                    <FormSection title="Line" divided={false} columns={3}>
                      <Field label="Bank account">
                        <select required name="bank" className="input mt-1">
                          <option value="">Choose a bank account</option>
                          {banks.data?.map((bank) => <option key={bank.id} value={bank.id}>{bank.name}</option>)}
                        </select>
                      </Field>
                      <Field label="Date"><input required name="date" type="date" max={today} className="input mt-1" /></Field>
                      <Field label="Amount (UGX)" hint="Positive for money in, negative for money out.">
                        <input required name="amount" type="number" step="0.01" className="input mt-1 tabular-nums" />
                      </Field>
                      <Field label="Description" className="sm:col-span-2"><input required name="description" className="input mt-1" placeholder="As the statement shows it" /></Field>
                      <Field label="Reference" optional><input name="reference" className="input mt-1" /></Field>
                    </FormSection>
                  </div>
                  <div className="card-footer">
                    <button disabled={createBankLine.isPending} className="btn btn-primary">Add line</button>
                  </div>
                </form>
              )}

              <ClientRegister
                title="Statement lines"
                description="Open a line to match it to the books. Auto-match only takes a unique suggestion scoring 90% or more."
                actions={canReconcile && (
                  <>
                    {activeBanks.map((bank) => (
                      <button
                        key={bank.id}
                        type="button"
                        disabled={autoMatchBankLines.isPending}
                        onClick={() => {
                          if (window.confirm(`Auto-match only high-confidence lines for ${bank.name}?`)) autoMatchBankLines.mutate(bank.id);
                        }}
                        className="btn-outline h-8 min-h-8 px-3 text-xs"
                      >
                        Auto-match {bank.name}
                      </button>
                    ))}
                    {formButton("line", "Add a line", false)}
                    {formButton("import", "Import statement")}
                  </>
                )}
                rows={bankLines.data}
                config={lines.config}
                columns={lines.columns}
                rowKey={(entry) => entry.id}
                onOpen={(entry) => openRecord(setViewingBankLineId, entry.id)}
                openLabel={(entry) => (canReconcile && !entry.matched_entry ? "Match" : "Open")}
                noun={["line", "lines"]}
                exportName={{ file: "bank-statement-lines", title: "Bank Statement Lines" }}
                isLoading={bankLines.isLoading}
                isError={bankLines.isError}
                onRetry={() => bankLines.refetch()}
                emptyText="No statement lines yet. Import the bank's statement to start."
              />
            </>
          )}

          {bankingView === "deposits" && (
            <>
              {canInitiate && bankingForm === "deposit" && (
                <form
                  className="card"
                  aria-label="Record money banked"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const form = event.currentTarget;
                    const d = new FormData(form);
                    recordBankDeposit.mutate({
                      bank_account: Number(d.get("bank")),
                      source_account: Number(d.get("source_account")),
                      amount: String(d.get("amount")),
                      deposit_date: String(d.get("deposit_date")),
                      reference: String(d.get("reference")),
                      notes: String(d.get("notes") || ""),
                    }, { onSuccess: () => { form.reset(); setBankingForm(""); } });
                  }}
                >
                  <div className="card-titlebar">
                    <div>
                      <h3 className="font-semibold text-gray-900">Record money banked</h3>
                      <p className="mt-1 max-w-3xl text-sm text-gray-500">
                        The day's cash, cheques on one deposit slip, or a SurePay payout: one entry for the one line the bank will show.
                        It is in the books at once, and waits on the reconciliation until the statement shows it.
                      </p>
                    </div>
                    {closeButton("money banked")}
                  </div>
                  <div className="card-content pt-5">
                    <FormSection title="Deposit" divided={false} columns={3}>
                      <Field label="Into bank">
                        <select required name="bank" className="input mt-1">
                          <option value="">Choose a bank account</option>
                          {activeBanks.map((bank) => <option key={bank.id} value={bank.id}>{bank.name}</option>)}
                        </select>
                      </Field>
                      <Field as="div" label="From" hint="Cash, SurePay collections, cheques…">
                        <AccountSelect required name="source_account" placeholder="Where the money was held" accounts={accounts.data} isSelectable={moneyLandsHere} />
                      </Field>
                      <Field label="Amount banked (UGX)"><input required name="amount" type="number" min="0.01" step="0.01" className="input mt-1 tabular-nums" /></Field>
                      <Field label="Date banked"><input required name="deposit_date" type="date" max={today} defaultValue={today} className="input mt-1" /></Field>
                      <Field label="Deposit slip or payout reference"><input required name="reference" className="input mt-1" /></Field>
                      <Field label="Notes" optional><input name="notes" className="input mt-1" /></Field>
                    </FormSection>
                  </div>
                  <div className="card-footer">
                    <button className="btn btn-primary" disabled={recordBankDeposit.isPending}>Record money banked</button>
                  </div>
                </form>
              )}
              <ClientRegister
                title="Money banked"
                description="Open a deposit to see its entry, or cancel one recorded in error before the statement shows it."
                actions={canInitiate && formButton("deposit", "Record money banked")}
                rows={bankDeposits.data}
                config={depositConfig}
                columns={depositColumns}
                rowKey={(entry) => entry.id}
                onOpen={(entry) => openRecord(setViewingDepositId, entry.id)}
                noun={["deposit", "deposits"]}
                exportName={{ file: "money-banked", title: "Money Banked" }}
                isLoading={bankDeposits.isLoading}
                isError={bankDeposits.isError}
                onRetry={() => bankDeposits.refetch()}
                emptyText="No money banked recorded yet."
              />
            </>
          )}

          {bankingView === "unidentified" && (
            <>
              {canInitiate && bankingForm === "unidentified" && (
                <form onSubmit={submitUnidentifiedReceipt} className="card">
                  <div className="card-titlebar">
                    <div>
                      <h3 className="font-semibold text-gray-900">Record unidentified money received</h3>
                      <p className="mt-1 max-w-3xl text-sm text-gray-500">When a bank, mobile-money or cash reference cannot yet be matched to a student. It is held as a liability until another authorised person resolves it.</p>
                    </div>
                    {closeButton("unidentified money")}
                  </div>
                  <div className="card-content pt-5">
                    <FormSection title="Money received" divided={false} columns={3}>
                      <Field label="Bank or provider reference" hint="Must be unique."><input required name="reference" className="input mt-1" /></Field>
                      <Field label="Payer name shown" optional><input name="payer_name" className="input mt-1" /></Field>
                      <Field label="Amount (UGX)"><input required name="amount" type="number" min="0.01" step="0.01" className="input mt-1 tabular-nums" /></Field>
                      <Field label="Method">
                        <select required name="payment_method" className="input mt-1">
                          <option value="bank_transfer">Bank transfer</option>
                          <option value="mobile_money">Mobile money</option>
                          <option value="cash">Cash</option>
                          <option value="cheque">Cheque</option>
                          <option value="other">Other</option>
                        </select>
                      </Field>
                      <Field as="div" label="Received into">
                        <AccountSelect required name="receipt_account" placeholder="Cash or bank account" accounts={accounts.data} isSelectable={moneyLandsHere} />
                      </Field>
                      <Field label="Received at"><input required name="received_at" type="datetime-local" defaultValue={nowLocalInput()} max={`${today}T23:59`} className="input mt-1" /></Field>
                      <Field label="Bank narration or investigation notes" optional className="sm:col-span-2 lg:col-span-3">
                        <textarea name="notes" rows={2} className="input mt-1 h-auto min-h-[3.5rem] resize-y py-2 leading-5" />
                      </Field>
                    </FormSection>
                  </div>
                  <div className="card-footer">
                    <p className="mr-auto text-xs text-gray-500">Held as student credit, never as school income, until resolved.</p>
                    <button disabled={createUnidentifiedReceipt.isPending} className="btn btn-primary">Record unidentified money</button>
                  </div>
                </form>
              )}
              <ClientRegister
                title="Unidentified money"
                description="Open one to match it to a student or return it to the payer."
                actions={canInitiate && formButton("unidentified", "Record unidentified money")}
                rows={unidentifiedReceipts.data}
                config={unidentifiedConfig}
                columns={unidentifiedColumns}
                rowKey={(entry) => entry.id}
                onOpen={(entry) => openRecord(setViewingUnidentifiedId, entry.id)}
                openLabel={(entry) => (entry.status === "unresolved" && canApprove && entry.recorded_by !== user?.id ? "Resolve" : "Open")}
                noun={["receipt", "receipts"]}
                exportName={{ file: "unidentified-money", title: "Unidentified Money" }}
                isLoading={unidentifiedReceipts.isLoading}
                isError={unidentifiedReceipts.isError}
                onRetry={() => unidentifiedReceipts.refetch()}
                emptyText="No unidentified money recorded."
              />
            </>
          )}

          {bankingView === "setup" && (
            <>
              {canConfigureAccounting && bankingForm === "bank" && (
                <form onSubmit={submitBank} className="card">
                  <div className="card-titlebar">
                    <div>
                      <h3 className="font-semibold text-gray-900">Add a bank account</h3>
                      <p className="mt-1 text-sm text-gray-500">Each bank account posts to its own asset account in the chart.</p>
                    </div>
                    {closeButton("bank account")}
                  </div>
                  <div className="card-content pt-5">
                    <FormSection title="Account" divided={false} columns={3}>
                      <Field label="Label"><input required name="name" className="input mt-1" placeholder="e.g. Stanbic – Fees collections" /></Field>
                      <Field label="Bank"><input required name="bank" className="input mt-1" placeholder="e.g. Stanbic Bank" /></Field>
                      <Field label="Account number"><input required name="number" className="input mt-1" /></Field>
                      <Field as="div" label="Ledger account" className="sm:col-span-2 lg:col-span-1" hint="An asset account not already used by another bank.">
                        <AccountSelect
                          required
                          name="account"
                          placeholder="Choose an asset account"
                          accounts={accounts.data}
                          isSelectable={(a) => a.account_type === "asset" && canReceivePostings(a) && !banks.data?.some((bank) => bank.ledger_account === a.id)}
                        />
                      </Field>
                      <Field label="Balance on the bank's statement (UGX)"><input name="opening" type="number" min="0" step="0.01" defaultValue="0" className="input mt-1 tabular-nums" /></Field>
                      <Field label="As at" hint="The day before the first statement you will import.">
                        <input name="opening_date" type="date" max={today} defaultValue={today} className="input mt-1" />
                      </Field>
                    </FormSection>
                  </div>
                  <div className="card-footer">
                    <button disabled={createBank.isPending} className="btn btn-primary">Add bank account</button>
                  </div>
                </form>
              )}
              <div className="card">
                <div className="card-header pb-4">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-gray-900">Bank accounts</h3>
                      <p className="mt-1 text-sm text-gray-500">The accounts statements are imported for.</p>
                    </div>
                    {canConfigureAccounting && formButton("bank", "Add bank account")}
                  </div>
                </div>
                <div className="card-content">
                  {banks.data?.length ? (
                    <div className="sheet-wrap">
                      <table className="sheet">
                        <thead><tr><th>Account</th><th>Bank</th><th>Number</th><th>Ledger account</th><th className="text-right">Opening balance (UGX)</th><th>As at</th><th>Status</th></tr></thead>
                        <tbody>
                          {banks.data.map((bank) => (
                            <tr key={bank.id}>
                              <td className="font-medium text-gray-900">{bank.name}</td>
                              <td className="text-gray-700">{bank.bank_name}</td>
                              <td className="font-mono text-xs text-gray-600">{bank.account_number}</td>
                              <td className="text-gray-600">{bank.ledger_account_name}</td>
                              <td className="text-right tabular-nums">{Number(bank.opening_balance).toLocaleString()}</td>
                              <td className="tabular-nums text-gray-600">{bank.opening_balance_date || "—"}</td>
                              <td><StatusDot label={bank.is_active ? "Active" : "Inactive"} dot={bank.is_active ? "bg-emerald-500" : "bg-gray-400"} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">No bank accounts yet.</div>
                  )}
                </div>
              </div>
              <div className="card">
                <div className="card-header pb-4">
                  <h3 className="font-semibold text-gray-900">Statement imports</h3>
                  <p className="mt-1 text-sm text-gray-500">Every file imported, with what could and could not be read.</p>
                </div>
                <div className="card-content">
                  {bankImports.data?.length ? (
                    <div className="sheet-wrap">
                      <table className="sheet">
                        <thead><tr><th>File</th><th>Bank</th><th>Covers</th><th>Uploaded by</th><th className="text-right">Imported</th><th className="text-right">Duplicates</th><th className="text-right">Errors</th><th>Status</th></tr></thead>
                        <tbody>
                          {bankImports.data.map((batch) => (
                            <tr key={batch.id} title={[...batch.errors, ...(batch.warnings ?? [])].slice(0, 5).map((item) => `${item.row ? `Row ${item.row}: ` : ""}${item.message}`).join("\n") || undefined}>
                              <td className="max-w-[14rem] truncate text-gray-900">{batch.filename}</td>
                              <td className="text-gray-700">{batch.bank_account_name}</td>
                              <td className="tabular-nums text-gray-600">{batch.period_start ? `${batch.period_start} → ${batch.period_end}` : "—"}</td>
                              <td className="text-gray-600">{batch.uploaded_by_name}</td>
                              <td className="text-right tabular-nums">{batch.imported_rows}</td>
                              <td className="text-right tabular-nums text-gray-500">{batch.duplicate_rows}</td>
                              <td className={`text-right tabular-nums ${batch.error_rows ? "font-medium text-red-700" : "text-gray-500"}`}>{batch.error_rows}</td>
                              <td><StatusDot label={humanise(batch.status)} dot={batch.status === "completed" ? "bg-emerald-500" : batch.status === "partial" ? "bg-amber-500" : "bg-red-500"} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">No statements imported yet.</div>
                  )}
                </div>
              </div>
            </>
          )}

          {line && (() => {
            const suggestions = suggestionsFor(line.id);
            const suggestedIds = new Set(suggestions.map((item) => item.journal_entry));
            // Only an entry that moved this bank by exactly the line's amount can be matched to it.
            const ledgerId = banks.data?.find((bank) => bank.id === line.bank_account)?.ledger_account;
            const cents = (value: number) => Math.round(value * 100);
            const availableJournals = (matchableJournals.data?.results ?? []).filter((entry) =>
              cents(entry.lines.reduce(
                (total, item) => (item.account === ledgerId ? total + Number(item.debit) - Number(item.credit) : total),
                0,
              )) === cents(Number(line.amount)),
            );
            const amount = Number(line.amount);
            return (
              <DetailsModal
                title={`Bank line · ${line.transaction_date}`}
                headline={{ label: amount < 0 ? "Money out" : "Money in", value: `UGX ${Math.abs(amount).toLocaleString()}`, note: bankName(line.bank_account) }}
                status={lineStatus(line, suggestions[0]?.score)}
                facts={[
                  ["Date", line.transaction_date],
                  ["Bank", bankName(line.bank_account)],
                  ["Reference", line.reference],
                  ["Came from", line.import_filename || "Typed in"],
                  ["Bank balance after", line.balance != null ? `UGX ${Number(line.balance).toLocaleString()}` : ""],
                  ["Matched by", line.reconciled_by_name ? `${line.reconciled_by_name}${line.reconciled_at ? ` · ${line.reconciled_at.slice(0, 10)}` : ""}` : ""],
                ]}
                sections={[{ title: "As the bank describes it", body: line.description }]}
                onClose={() => setViewingBankLineId(null)}
              >
                {line.matched_entry ? (
                  <ActionPanel title={`Matched to ${line.matched_entry_number}`} description={line.matched_entry_description || undefined}>
                    {canReconcile && (
                      <form
                        className="space-y-3"
                        onSubmit={(event) => {
                          event.preventDefault();
                          unmatchLine.mutate({ id: line.id, reason: bankingNote.trim() }, { onSuccess: () => setBankingNote("") });
                        }}
                      >
                        <Field as="div" label="Wrong match? Say why before undoing it">
                        <AIAssistField
                          rows={2}
                          value={bankingNote}
                          onChange={(next) => setBankingNote(next)}
                          context={{ key: "financial.banking_note", params: { kind: "unmatch", id: line.id } }}
                          draftLabel="Draft the reason"
                          draftHint="Reads this line and its match. Jot why first."
                        />
                      </Field>
                        <div className="flex justify-end">
                          <button disabled={!bankingNote.trim() || unmatchLine.isPending} className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">Undo match</button>
                        </div>
                      </form>
                    )}
                  </ActionPanel>
                ) : canReconcile ? (
                  <ActionPanel title="Match to the books" description="Choose the posted journal entry that moved this bank by exactly this amount.">
                    {suggestions.length > 0 && (
                      <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
                        {suggestions.slice(0, 5).map((item) => (
                          <li key={item.journal_entry} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                            <span>
                              <span className="font-semibold tabular-nums text-primary-700">{item.score}%</span>{" "}
                              <span className="font-mono text-xs">{item.entry_number}</span> · {item.description}
                              <span className="block text-xs text-gray-500">{item.reasons.join(", ")}</span>
                            </span>
                            <button type="button" onClick={() => setBankMatches((current) => ({ ...current, [line.id]: String(item.journal_entry) }))} className="rounded px-2 py-0.5 text-xs font-medium text-primary-700 hover:bg-primary-50">Use</button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <Field label="Journal entry">
                      <select
                        className="input mt-1"
                        value={bankMatches[line.id] || ""}
                        onChange={(event) => setBankMatches((current) => ({ ...current, [line.id]: event.target.value }))}
                      >
                        <option value="">Choose a posted entry</option>
                        {suggestions.length > 0 && (
                          <optgroup label="Suggested matches">
                            {suggestions.map((item) => (
                              <option key={item.journal_entry} value={item.journal_entry}>{item.score}% · {item.entry_number} · {item.description}</option>
                            ))}
                          </optgroup>
                        )}
                        <optgroup label="Other entries for this amount">
                          {availableJournals.filter((entry) => !suggestedIds.has(entry.id)).map((entry) => (
                            <option key={entry.id} value={entry.id}>{entry.entry_number} · {entry.description}</option>
                          ))}
                        </optgroup>
                      </select>
                    </Field>
                    <div className="flex flex-wrap justify-end gap-2">
                      {!suggestions.length && line.import_batch && amount < 0 && amount >= -100000 && (
                        <button
                          type="button"
                          disabled={recordBankCharge.isPending}
                          title="Enter this as a bank charge and match it"
                          onClick={() => recordBankCharge.mutate(line.id)}
                          className="rounded-md border border-amber-200 px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-50"
                        >
                          Record as a bank charge
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={!bankMatches[line.id] || reconcileLine.isPending}
                        onClick={() => reconcileLine.mutate({ id: line.id, journal_entry: Number(bankMatches[line.id]) })}
                        className="btn btn-primary"
                      >
                        Match
                      </button>
                    </div>
                  </ActionPanel>
                ) : null}
              </DetailsModal>
            );
          })()}

          {deposit && (
            <DetailsModal
              title={`Money banked · ${deposit.reference}`}
              headline={{ label: "Amount banked", value: `UGX ${Number(deposit.amount).toLocaleString()}`, note: `${deposit.source_account_name} → ${deposit.bank_account_name}` }}
              status={depositStatus(deposit)}
              facts={[
                ["Banked on", deposit.deposit_date],
                ["From", `${deposit.source_account_code ? `${deposit.source_account_code} · ` : ""}${deposit.source_account_name}`],
                ["Into", deposit.bank_account_name],
                ["Reference", deposit.reference],
                ["Journal entry", deposit.entry_number],
                ["Recorded by", deposit.recorded_by_name],
              ]}
              sections={[
                { title: "Notes", body: deposit.notes },
                { title: "Cancelled", body: deposit.cancelled_at ? `By ${deposit.cancelled_by_name}: ${deposit.cancellation_reason}` : "", tone: "danger" },
              ]}
              onClose={() => setViewingDepositId(null)}
            >
              {canApprove && !deposit.on_statement && !deposit.cancelled_at && (
                <ActionPanel title="Recorded in error?" description="It can be cancelled until the bank statement shows it.">
                  <form
                    className="space-y-3"
                    onSubmit={(event) => {
                      event.preventDefault();
                      cancelBankDeposit.mutate({ id: deposit.id, reason: bankingNote.trim() }, { onSuccess: () => setBankingNote("") });
                    }}
                  >
                    <Field as="div" label="Why cancel it">
                        <AIAssistField
                          rows={2}
                          value={bankingNote}
                          onChange={(next) => setBankingNote(next)}
                          context={{ key: "financial.banking_note", params: { kind: "deposit_cancel", id: deposit.id } }}
                          draftLabel="Draft the reason"
                          draftHint="Reads this deposit. Jot why first."
                        />
                      </Field>
                    <div className="flex justify-end">
                      <button disabled={!bankingNote.trim() || cancelBankDeposit.isPending} className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">Cancel deposit</button>
                    </div>
                  </form>
                </ActionPanel>
              )}
            </DetailsModal>
          )}

          {unidentified && (
            <DetailsModal
              title={`Unidentified money · ${unidentified.reference}`}
              headline={{ label: "Amount received", value: `UGX ${Number(unidentified.amount).toLocaleString()}`, note: unidentified.payer_name || "Payer unknown" }}
              status={unidentifiedStatus(unidentified)}
              facts={[
                ["Received", unidentified.received_at ? new Date(unidentified.received_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : ""],
                ["Method", humanise(unidentified.payment_method)],
                ["Into", unidentified.receipt_account_name],
                ["Recorded by", unidentified.recorded_by === user?.id ? "You" : unidentified.recorded_by_name],
                ["Matched to", unidentified.student_name ? `${unidentified.student_name}${unidentified.invoice_number ? ` · ${unidentified.invoice_number}` : " · as credit"}` : ""],
                ["Resolved by", unidentified.resolved_by_name ? `${unidentified.resolved_by_name}${unidentified.resolved_at ? ` · ${unidentified.resolved_at.slice(0, 10)}` : ""}` : ""],
              ]}
              sections={[
                { title: "Notes when recorded", body: unidentified.notes },
                { title: unidentified.status === "refunded" ? "Why it was returned" : "Evidence for the match", body: unidentified.resolution_notes },
              ]}
              onClose={() => setViewingUnidentifiedId(null)}
            >
              {unidentified.status === "unresolved" && canApprove && unidentified.recorded_by === user?.id && (
                <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">You recorded this money, so someone else resolves it.</p>
              )}
              {unidentified.status === "unresolved" && canApprove && unidentified.recorded_by !== user?.id && (
                <ActionPanel title="Resolve it" description="Match it to the student it belongs to, or return it to the payer. The note is kept on the record.">
                  <form onSubmit={(event) => submitUnidentifiedResolution(event, unidentified.id)} className="grid gap-4 sm:grid-cols-2">
                    <Field as="div" label="Student"><StudentSearchField /></Field>
                    <Field label="Invoice" optional>
                      <select name="invoice" className="input mt-1">
                        <option value="">No invoice: hold as student credit</option>
                        {(outstandingInvoices.data?.results ?? []).map((invoice) => (
                          <option key={invoice.id} value={invoice.id}>{invoice.invoice_number} · {invoice.student_name} · UGX {Number(invoice.balance_due).toLocaleString()} due</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Fee" optional>
                      <select name="fee" className="input mt-1">
                        <option value="">Fee not identified</option>
                        {fees.data?.filter((fee) => fee.is_active).map((fee) => <option key={fee.id} value={fee.id}>{fee.name}</option>)}
                      </select>
                    </Field>
                    <div className="sm:col-span-2">
                      <Field as="div" label="Evidence or reason" hint="What shows it is this student's, or why it goes back to the payer.">
                        <AIAssistField
                          name="notes"
                          rows={2}
                          value={bankingNote}
                          onChange={(next) => setBankingNote(next)}
                          context={{ key: "financial.banking_note", params: { kind: "unidentified_resolution", id: unidentified.id } }}
                          draftLabel="Draft the note"
                          draftHint="Reads this receipt. Jot the evidence or reason first."
                        />
                      </Field>
                    </div>
                    <div className="flex flex-wrap justify-end gap-2 sm:col-span-2">
                      <button
                        type="button"
                        disabled={!bankingNote.trim() || refundUnidentifiedReceipt.isPending}
                        title={bankingNote.trim() ? undefined : "Write why it goes back first"}
                        onClick={() => {
                          if (window.confirm(`Return UGX ${Number(unidentified.amount).toLocaleString()} (${unidentified.reference}) to the payer?`)) {
                            refundUnidentifiedReceipt.mutate({ id: unidentified.id, reason: bankingNote.trim() });
                          }
                        }}
                        className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        Return to payer
                      </button>
                      <button disabled={resolveUnidentifiedReceipt.isPending} className="btn btn-primary">Match to this student</button>
                    </div>
                  </form>
                </ActionPanel>
              )}
            </DetailsModal>
          )}
        </div>
        );
      })()}

      {activeTab === "budgets" && (() => {
        const budget = budgets.data?.find((entry) => entry.id === selectedBudgetId) ?? null;
        const list = budgets.data ?? [];
        const closeBudget = () => setSelectedBudgetId(null);
        return (
        <div className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-primary-100 bg-primary-50 p-5">
            <div><h2 className="font-semibold text-primary-950">Budgets</h2><p className="mt-1 max-w-2xl text-sm text-primary-800">Plan income and spending by account for a period, get the plan approved, then watch budget against actual.</p></div>
            <div className="flex items-center gap-3 text-sm">
              <span className="rounded-md bg-white px-3 py-2 font-medium text-gray-700">{list.filter((entry) => approvalStage(entry).key === "approved").length} approved</span>
              <span className="rounded-md bg-white px-3 py-2 font-medium text-amber-700">{list.filter((entry) => ["submitted", "verified"].includes(entry.workflow_status ?? "")).length} awaiting approval</span>
            </div>
          </div>

          {canInitiate && (showBudgetForm || editingBudget) && <form key={editingBudgetId ?? "new"} onSubmit={submitBudget} className="card">
            <div className="card-titlebar">
              <div>
                <h3 className="font-semibold text-gray-900">{editingBudget ? `Edit ${editingBudget.name}` : "New budget"}</h3>
                <p className="mt-1 text-sm text-gray-500">Saved as a draft; submit it for review from the register.</p>
              </div>
              <button type="button" onClick={resetBudgetForm} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close the budget form">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="card-content space-y-5 pt-5">
              {editingBudget && (
                <EditingNotice
                  label={editingBudget.name}
                  reason={editingBudget.workflow_status === "returned" ? editingBudget.return_reason : undefined}
                  onCancel={resetBudgetForm}
                />
              )}
              <FormSection title="Budget" divided={false} columns={3}>
                <Field label="Name"><input required name="name" className="input mt-1" placeholder="e.g. Term 3 2026 operating budget" defaultValue={editingBudget?.name ?? ""} /></Field>
                <Field label="Starts"><input required name="start" type="date" className="input mt-1" defaultValue={editingBudget?.start_date ?? ""} /></Field>
                <Field label="Ends"><input required name="end" type="date" className="input mt-1" defaultValue={editingBudget?.end_date ?? ""} /></Field>
              </FormSection>
              <FormSection title="What is being budgeted for" description="One row per income or expense account." columns={1}>
                <div className="overflow-hidden rounded-md border border-gray-200">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                      <tr>
                        <th className="w-10 px-3 py-2">#</th>
                        <th className="px-3 py-2">Account</th>
                        <th className="w-44 px-3 py-2 text-right">Amount (UGX)</th>
                        <th className="px-3 py-2">Notes</th>
                        <th className="w-20 px-3 py-2" aria-label="Remove" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {budgetLines.map((line, index) => (
                        <tr key={index}>
                          <td className="px-3 py-2 tabular-nums text-gray-400">{index + 1}</td>
                          <td className="px-3 py-2">
                            <AccountSelect
                              placeholder="Income or expense account"
                              accounts={accounts.data}
                              isSelectable={(a) => canReceivePostings(a) && ["income", "expense"].includes(a.account_type)}
                              value={line.account}
                              onChange={(next) => setBudgetLines((current) => current.map((row, i) => (i === index ? { ...row, account: next } : row)))}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              aria-label={`Amount for line ${index + 1}`}
                              type="number"
                              min="0.01"
                              step="0.01"
                              className="input text-right tabular-nums"
                              value={line.amount}
                              onChange={(event) => setBudgetLines((current) => current.map((row, i) => (i === index ? { ...row, amount: event.target.value } : row)))}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              aria-label={`Notes for line ${index + 1}`}
                              className="input"
                              placeholder="Optional"
                              value={line.notes}
                              onChange={(event) => setBudgetLines((current) => current.map((row, i) => (i === index ? { ...row, notes: event.target.value } : row)))}
                            />
                          </td>
                          <td className="px-3 py-2 text-right">
                            {budgetLines.length > 1 && (
                              <button type="button" className="rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50" onClick={() => setBudgetLines((current) => current.filter((_, i) => i !== index))}>
                                Remove
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t border-gray-200 bg-gray-50">
                      <tr>
                        <td colSpan={2} className="px-3 py-2">
                          <button type="button" className="inline-flex items-center text-sm font-medium text-primary-700 hover:underline" onClick={() => setBudgetLines((current) => [...current, { account: "", amount: "", notes: "" }])}>
                            <Plus className="mr-1 h-4 w-4" /> Add an account
                          </button>
                        </td>
                        <td className="px-3 py-2 text-right font-semibold tabular-nums text-gray-900">
                          {budgetLines.reduce((sum, line) => sum + Number(line.amount || 0), 0).toLocaleString()}
                        </td>
                        <td colSpan={2} className="px-3 py-2 text-xs text-gray-500">total</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </FormSection>
            </div>
            <div className="card-footer">
              <button type="button" onClick={resetBudgetForm} className="btn-outline">Cancel</button>
              <button disabled={createBudget.isPending || updateBudget.isPending} className="btn btn-primary">{editingBudgetId === null ? "Create budget" : "Save changes"}</button>
            </div>
          </form>}

          <ClientRegister
            title="Budget register"
            description="Open a budget to see its accounts, budget against actual, and to move it through approval."
            actions={canInitiate && !showBudgetForm && !editingBudget && (
              <button type="button" onClick={() => setShowBudgetForm(true)} className="btn-primary h-8 min-h-8 px-3 text-xs">
                <Plus className="mr-1.5 h-3.5 w-3.5" /> New budget
              </button>
            )}
            rows={budgets.data}
            config={budgetConfig}
            columns={budgetColumns}
            rowKey={(entry) => entry.id}
            onOpen={(entry) => {
              setPlanningNotes("");
              setSelectedBudgetId(entry.id);
            }}
            openLabel={(entry) =>
              (canReview && entry.workflow_status === "submitted" && entry.prepared_by !== user?.id) ||
              (canApprove && entry.workflow_status === "verified" && entry.prepared_by !== user?.id)
                ? "Review"
                : "Open"
            }
            noun={["budget", "budgets"]}
            exportName={{ file: "budgets", title: "Budgets" }}
            isLoading={budgets.isLoading}
            isError={budgets.isError}
            onRetry={() => budgets.refetch()}
            emptyText="No budgets yet."
          />

          {budget && (() => {
            const b = budget;
            const mine = b.prepared_by === user?.id;
            const inReview = ["submitted", "verified"].includes(b.workflow_status ?? "");
            const canVerify = canReview && b.workflow_status === "submitted" && !mine;
            const canApproveThis = canApprove && b.workflow_status === "verified" && !mine;
            const canSendBack = canReview && inReview && !mine;
            const notes = planningNotes.trim();
            const variance = budgetVariance.data?.id === b.id ? budgetVariance.data : null;
            return (
              <DetailsModal
                size="3xl"
                title={b.name}
                headline={{ label: "Budgeted", value: `UGX ${budgetTotal(b).toLocaleString()}`, note: `${b.start_date} to ${b.end_date} · ${b.lines.length} account${b.lines.length === 1 ? "" : "s"}` }}
                status={approvalStage(b)}
                facts={[
                  ["Starts", b.start_date],
                  ["Ends", b.end_date],
                  ["Prepared by", mine ? "You" : b.prepared_by_name],
                  ["Approved by", b.approved_by_name ? `${b.approved_by_name}${b.approved_at ? ` · ${b.approved_at.slice(0, 10)}` : ""}` : ""],
                ]}
                sections={[
                  { title: "Sent back", body: b.workflow_status === "returned" ? b.return_reason : "", tone: "warning" },
                  { title: "Rejected", body: b.workflow_status === "rejected" ? b.rejection_reason : "", tone: "danger" },
                ]}
                onClose={closeBudget}
                footer={
                  <>
                    {myUnsubmitted(b, b.prepared_by) && (
                      <button type="button" onClick={() => { closeBudget(); setShowBudgetForm(true); editBudget(b); }} className="mr-auto inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50">
                        <Edit className="h-4 w-4" /> Edit
                      </button>
                    )}
                    <button type="button" onClick={closeBudget} className="btn-outline">Close</button>
                    {canInitiate && ["draft", "returned"].includes(b.workflow_status || "") && (
                      <button type="button" disabled={submitBudgetForApproval.isPending} onClick={() => submitBudgetForApproval.mutate(b.id)} className="btn btn-primary">Submit for review</button>
                    )}
                  </>
                }
              >
                <section>
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Budget against actual</h4>
                    {variance && canExportReports && (
                      <span className="flex gap-2">
                        <button type="button" className="pager-button h-7" onClick={() => financialService.downloadBudgetVariance(variance.id, "csv")}><Download className="h-3.5 w-3.5" /> CSV</button>
                        <button type="button" className="pager-button h-7" onClick={() => financialService.downloadBudgetVariance(variance.id, "pdf")}><Printer className="h-3.5 w-3.5" /> PDF</button>
                      </span>
                    )}
                  </div>
                  {variance ? (
                    <>
                      <p className="mt-1 text-sm text-gray-600">
                        {Number(variance.utilization_percent).toFixed(1)}% used · variance UGX {Number(variance.total_variance).toLocaleString()}
                      </p>
                      <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200 px-3">
                <table className="mt-4 min-w-full divide-y text-sm">
                  <thead><tr className="text-left text-xs uppercase text-gray-500"><th className="py-2">Account</th><th className="py-2 text-right">Budget</th><th className="py-2 text-right">Actual</th><th className="py-2 text-right">Variance</th></tr></thead>
                  <tbody className="divide-y">{variance.lines.map((line) => <tr key={line.account}><td className="py-2">{line.code} · {line.name}</td><td className="py-2 text-right">{Number(line.budget).toLocaleString()}</td><td className="py-2 text-right">{Number(line.actual).toLocaleString()}</td><td className="py-2 text-right">{Number(line.variance).toLocaleString()}</td></tr>)}</tbody>
                  <tfoot><tr className="font-semibold"><td className="py-2">Totals</td><td className="py-2 text-right">{Number(variance.total_budget).toLocaleString()}</td><td className="py-2 text-right">{Number(variance.total_actual).toLocaleString()}</td><td className="py-2 text-right">{Number(variance.total_variance).toLocaleString()}</td></tr></tfoot>
                </table>
                      </div>
                    </>
                  ) : budgetVariance.isLoading ? (
                    <p className="mt-1.5 text-sm text-gray-500">Working out budget against actual…</p>
                  ) : (
                    <p className="mt-1.5 text-sm text-gray-500">Budget against actual could not be loaded.</p>
                  )}
                </section>
                {inReview && mine && <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">You prepared this budget, so someone else reviews and approves it.</p>}
                {(canVerify || canApproveThis || canSendBack) && (
                  <ActionPanel
                    title={canApproveThis ? "Approve this budget" : "Review this budget"}
                    description={canApproveThis ? "Approving it puts it into use." : "Mark it reviewed to pass it to an approver, or send it back if something needs to change."}
                  >
                    <Field as="div" label="Comments" hint="Needed to send it back, so the preparer knows why.">
                      <AIAssistField
                        rows={2}
                        value={planningNotes}
                        onChange={(next) => setPlanningNotes(next)}
                        context={{ key: "financial.approval_note", params: { document_type: "financial.budget", id: b.id, purpose: "return" } }}
                        draftLabel="Draft what to change"
                        draftHint="Reads this budget. Jot rough notes first for a better draft."
                      />
                    </Field>
                    <div className="flex flex-wrap justify-end gap-2">
                      {canSendBack && (
                        <button type="button" disabled={!notes || returnBudget.isPending} title={notes ? undefined : "Write what needs to change"} onClick={() => returnBudget.mutate({ id: b.id, reason: notes }, { onSuccess: closeBudget })} className="rounded-md border border-amber-200 px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-50 disabled:opacity-50">Send back</button>
                      )}
                      {canVerify && (
                        <button type="button" disabled={verifyBudgetRequest.isPending} onClick={() => verifyBudgetRequest.mutate({ id: b.id }, { onSuccess: closeBudget })} className="btn btn-primary">Mark reviewed</button>
                      )}
                      {canApproveThis && (
                        <button
                          type="button"
                          disabled={approveBudget.isPending}
                          onClick={() => {
                            if (window.confirm(`Approve ${b.name}, UGX ${budgetTotal(b).toLocaleString()} across ${b.lines.length} accounts?`)) approveBudget.mutate(b.id, { onSuccess: closeBudget });
                          }}
                          className="btn btn-primary"
                        >
                          Approve budget
                        </button>
                      )}
                    </div>
                  </ActionPanel>
                )}
              </DetailsModal>
            );
          })()}
        </div>
        );
      })()}

      {activeTab === "grants" && (() => {
        const grant = grants.data?.find((entry) => entry.id === expandedGrant) ?? null;
        const list = grants.data ?? [];
        const closeGrant = () => setExpandedGrant(null);
        return (
        <div className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-primary-100 bg-primary-50 p-5">
            <div><h2 className="font-semibold text-primary-950">Grants</h2><p className="mt-1 max-w-2xl text-sm text-primary-800">Grant money is tracked apart from fee income. A grant must be approved before receipts or spending can be posted against it.</p></div>
            <div className="flex items-center gap-3 text-sm">
              <span className="rounded-md bg-white px-3 py-2 font-medium text-gray-700">UGX {list.filter((entry) => approvalStage(entry).key === "approved").reduce((sum, entry) => sum + Number(entry.balance), 0).toLocaleString()} unspent</span>
              <span className="rounded-md bg-white px-3 py-2 font-medium text-amber-700">{list.filter((entry) => ["submitted", "verified"].includes(entry.workflow_status ?? "")).length} awaiting approval</span>
            </div>
          </div>

          {canInitiate && (showGrantForm || editingGrant) && (
            <form key={editingGrantId ?? "new"} onSubmit={submitGrant} className="card">
              <div className="card-titlebar">
                <div>
                  <h3 className="font-semibold text-gray-900">{editingGrant ? `Edit ${editingGrant.name}` : "New grant"}</h3>
                  <p className="mt-1 text-sm text-gray-500">Saved as a draft; submit it for review from the register.</p>
                </div>
                <button type="button" onClick={() => { setEditingGrantId(null); setShowGrantForm(false); }} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close the grant form">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="card-content space-y-5 pt-5">
                {editingGrant && (
                  <EditingNotice
                    label={editingGrant.name}
                    reason={editingGrant.workflow_status === "returned" ? editingGrant.return_reason : undefined}
                    onCancel={() => setEditingGrantId(null)}
                  />
                )}
                <FormSection title="Grant" divided={false} columns={3}>
                  <Field label="Name"><input required name="name" className="input mt-1" placeholder="e.g. Library books 2026" defaultValue={editingGrant?.name ?? ""} /></Field>
                  <Field label="Donor"><input required name="donor" className="input mt-1" defaultValue={editingGrant?.donor ?? ""} /></Field>
                  <Field label="Project" optional><input name="project" className="input mt-1" defaultValue={editingGrant?.project ?? ""} /></Field>
                  <Field label="Restriction" hint="How freely the money may be spent.">
                    <select required name="restriction" className="input mt-1" defaultValue={editingGrant?.restriction ?? "unrestricted"}>
                      <option value="unrestricted">Unrestricted</option>
                      <option value="temporarily_restricted">Temporarily restricted</option>
                      <option value="restricted">Restricted</option>
                    </select>
                  </Field>
                  <Field label="Funding starts"><input required name="start" type="date" className="input mt-1" defaultValue={editingGrant?.funding_period_start ?? ""} /></Field>
                  <Field label="Funding ends"><input required name="end" type="date" className="input mt-1" defaultValue={editingGrant?.funding_period_end ?? ""} /></Field>
                  <Field label="Total committed (UGX)"><input required name="amount" type="number" min="0.01" step="0.01" className="input mt-1 tabular-nums" defaultValue={editingGrant?.total_amount ?? ""} /></Field>
                  <Field label="Notes" optional className="sm:col-span-2" hint="The donor's conditions, reporting dates, anything the next person needs.">
                    <textarea name="notes" rows={2} className="input mt-1 h-auto min-h-[3.5rem] resize-y py-2 leading-5" defaultValue={editingGrant?.notes ?? ""} />
                  </Field>
                </FormSection>
              </div>
              <div className="card-footer">
                <button disabled={createGrant.isPending || updateGrant.isPending} className="btn btn-primary">{editingGrantId === null ? "Create grant" : "Save changes"}</button>
              </div>
            </form>
          )}

          <ClientRegister
            title="Grant register"
            description="Open a grant to see its money in and out, record receipts or spending, and move it through approval."
            actions={canInitiate && !showGrantForm && !editingGrant && (
              <button type="button" onClick={() => setShowGrantForm(true)} className="btn-primary h-8 min-h-8 px-3 text-xs">
                <Plus className="mr-1.5 h-3.5 w-3.5" /> New grant
              </button>
            )}
            rows={grants.data}
            config={grantConfig}
            columns={grantColumns}
            rowKey={(entry) => entry.id}
            onOpen={(entry) => {
              setPlanningNotes("");
              setExpandedGrant(entry.id);
            }}
            openLabel={(entry) =>
              (canReview && ["submitted", "verified"].includes(entry.workflow_status ?? "") && entry.prepared_by !== user?.id) ||
              (canApprove && entry.workflow_status === "verified" && entry.prepared_by !== user?.id)
                ? "Review"
                : "Open"
            }
            noun={["grant", "grants"]}
            exportName={{ file: "grants", title: "Grants" }}
            isLoading={grants.isLoading}
            isError={grants.isError}
            onRetry={() => grants.refetch()}
            emptyText="No grants recorded yet."
          />

          {grant && (() => {
            const g = grant;
            const mine = g.prepared_by === user?.id;
            const inReview = ["submitted", "verified"].includes(g.workflow_status ?? "");
            const canVerify = canReview && g.workflow_status === "submitted" && !mine;
            const canApproveThis = canApprove && g.workflow_status === "verified" && !mine;
            const canSendBack = canReview && inReview && !mine;
            const notes = planningNotes.trim();
            return (
              <DetailsModal
                size="3xl"
                title={g.name}
                headline={{
                  label: "Balance",
                  value: `UGX ${Number(g.balance).toLocaleString()}`,
                  note: `of UGX ${Number(g.total_amount).toLocaleString()} committed · UGX ${Number(g.received_amount).toLocaleString()} received · UGX ${Number(g.spent_amount).toLocaleString()} spent`,
                }}
                status={approvalStage(g)}
                facts={[
                  ["Donor", g.donor],
                  ["Project", g.project],
                  ["Restriction", restrictionLabel(g.restriction)],
                  ["Funding period", `${g.funding_period_start} to ${g.funding_period_end}`],
                  ["Prepared by", mine ? "You" : g.prepared_by_name],
                  ["Approved by", g.approved_by_name ? `${g.approved_by_name}${g.approved_at ? ` · ${g.approved_at.slice(0, 10)}` : ""}` : ""],
                ]}
                sections={[
                  { title: "Notes", body: g.notes },
                  { title: "Sent back", body: g.workflow_status === "returned" ? g.return_reason : "", tone: "warning" },
                  { title: "Rejected", body: g.workflow_status === "rejected" ? g.rejection_reason : "", tone: "danger" },
                ]}
                onClose={closeGrant}
                footer={
                  <>
                    {myUnsubmitted(g, g.prepared_by) && (
                      <button type="button" onClick={() => { closeGrant(); setEditingGrantId(g.id); scrollPageToTop(); }} className="mr-auto inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50">
                        <Edit className="h-4 w-4" /> Edit
                      </button>
                    )}
                    <button type="button" onClick={closeGrant} className="btn-outline">Close</button>
                    {canInitiate && ["draft", "returned"].includes(g.workflow_status || "") && (
                      <button type="button" disabled={submitGrantForApproval.isPending} onClick={() => submitGrantForApproval.mutate(g.id)} className="btn btn-primary">Submit for review</button>
                    )}
                  </>
                }
              >
                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Money in and out</h4>
                  <div className="mt-2 rounded-lg border border-gray-200 px-3">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-gray-500">
                            <th className="py-1 pr-3">Date</th>
                            <th className="py-1 pr-3">Type</th>
                            <th className="py-1 pr-3">Account</th>
                            <th className="py-1 pr-3">Description</th>
                            <th className="py-1 pr-3">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {grantTransactions.data?.map((t) => (
                            <tr key={t.id} className="border-t">
                              <td className="py-1 pr-3">{t.transaction_date}</td>
                              <td className="py-1 pr-3 capitalize">{t.transaction_type}</td>
                              <td className="py-1 pr-3">{t.account_name}</td>
                              <td className="py-1 pr-3">{t.description}</td>
                              <td className="py-1 pr-3">UGX {Number(t.amount).toLocaleString()}</td>
                            </tr>
                          ))}
                          {!grantTransactions.data?.length && (
                            <tr><td className="py-2 text-gray-500" colSpan={5}>No transactions recorded yet.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </section>
                {canPostTransactions && g.workflow_status === "approved" && g.status === "active" && (
                  <div className="grid gap-4 md:grid-cols-2">
                    <ActionPanel title="Record a receipt" description="Money the donor has paid in.">
                      <form onSubmit={(event) => submitGrantReceipt(event, g.id)} className="space-y-3">
                        <Field label="Amount received (UGX)"><input required name="amount" type="number" min="0.01" step="0.01" className="input mt-1 tabular-nums" /></Field>
                        <Field as="div" label="Received into">
                          <AccountSelect required name="receipt_account" placeholder="Receiving account" accounts={accounts.data} isSelectable={(a) => canReceivePostings(a) && a.account_type === "asset"} />
                        </Field>
                        <Field label="Description" optional><input name="description" className="input mt-1" /></Field>
                        <div className="flex justify-end"><button disabled={recordGrantReceipt.isPending} className="btn btn-primary">Record receipt</button></div>
                      </form>
                    </ActionPanel>
                    <ActionPanel title="Record spending" description="What the grant paid for.">
                      <form onSubmit={(event) => submitGrantExpense(event, g.id)} className="space-y-3">
                        <Field label="Amount spent (UGX)"><input required name="amount" type="number" min="0.01" step="0.01" className="input mt-1 tabular-nums" /></Field>
                        <Field as="div" label="Expense account">
                          <AccountSelect required name="expense_account" placeholder="Expense account" accounts={accounts.data} isSelectable={(a) => canReceivePostings(a) && a.account_type === "expense"} />
                        </Field>
                        <Field as="div" label="Paid from">
                          <AccountSelect required name="payment_account" placeholder="Cash or bank account" accounts={accounts.data} isSelectable={canPayFrom} />
                        </Field>
                        <Field label="Description" optional><input name="description" className="input mt-1" /></Field>
                        <div className="flex justify-end"><button disabled={recordGrantExpense.isPending} className="btn btn-primary">Record spending</button></div>
                      </form>
                    </ActionPanel>
                  </div>
                )}
                {inReview && mine && <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">You prepared this grant, so someone else reviews and approves it.</p>}
                {(canVerify || canApproveThis || canSendBack) && (
                  <ActionPanel
                    title={canApproveThis ? "Approve this grant" : "Review this grant"}
                    description={canApproveThis ? "Approving it puts it into use." : "Mark it reviewed to pass it to an approver, or send it back if something needs to change."}
                  >
                    <Field as="div" label="Comments" hint="Needed to send it back or reject it, so the preparer knows why.">
                      <AIAssistField
                        rows={2}
                        value={planningNotes}
                        onChange={(next) => setPlanningNotes(next)}
                        context={{ key: "financial.approval_note", params: { document_type: "financial.grant", id: g.id, purpose: "return" } }}
                        draftLabel="Draft what to change"
                        draftHint="Reads this grant. Jot rough notes first for a better draft."
                      />
                    </Field>
                    <div className="flex flex-wrap justify-end gap-2">
                      {canSendBack && (
                        <>
                          <button type="button" disabled={!notes || rejectGrant.isPending} title={notes ? undefined : "Write why before rejecting"} onClick={() => rejectGrant.mutate({ id: g.id, reason: notes }, { onSuccess: closeGrant })} className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">Reject</button>
                          <button type="button" disabled={!notes || returnGrant.isPending} title={notes ? undefined : "Write what needs to change"} onClick={() => returnGrant.mutate({ id: g.id, reason: notes }, { onSuccess: closeGrant })} className="rounded-md border border-amber-200 px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-50 disabled:opacity-50">Send back</button>
                        </>
                      )}
                      {canVerify && (
                        <button type="button" disabled={verifyGrantRequest.isPending} onClick={() => verifyGrantRequest.mutate({ id: g.id }, { onSuccess: closeGrant })} className="btn btn-primary">Mark reviewed</button>
                      )}
                      {canApproveThis && (
                        <button
                          type="button"
                          disabled={approveGrant.isPending}
                          onClick={() => {
                            if (window.confirm(`Approve ${g.name} from ${g.donor}, UGX ${Number(g.total_amount).toLocaleString()} committed?`)) approveGrant.mutate(g.id, { onSuccess: closeGrant });
                          }}
                          className="btn btn-primary"
                        >
                          Approve grant
                        </button>
                      )}
                    </div>
                  </ActionPanel>
                )}
              </DetailsModal>
            );
          })()}
        </div>
        );
      })()}

      {activeTab === "payroll" && (() => {
        const runs = payrollPeriods.data ?? [];
        const period = runs.find((run) => run.id === viewingPayrollId) ?? null;
        const openRun = (id: number) => {
          setPayrollDecisionNotes("");
          setEditingDeduction(null);
          setViewingPayrollId(id);
        };
        const closeRun = () => {
          setViewingPayrollId(null);
          setEditingDeduction(null);
        };
        return (
        <div className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-primary-100 bg-primary-50 p-5">
            <div><h2 className="font-semibold text-primary-950">Payroll</h2><p className="mt-1 max-w-2xl text-sm text-primary-800">Generate a month's pay run, have it reviewed and approved, then pay staff and hand on what was withheld.</p></div>
            <div className="flex items-center gap-3 text-sm">
              <span className="rounded-md bg-white px-3 py-2 font-medium text-amber-700">{runs.filter((run) => ["submitted", "verified"].includes(run.workflow_status ?? "")).length} awaiting approval</span>
              <span className="rounded-md bg-white px-3 py-2 font-medium text-gray-700">UGX {runs.filter((run) => run.status === "approved").reduce((sum, run) => sum + Number(run.balance_due), 0).toLocaleString()} still owed to staff</span>
            </div>
          </div>

          <details className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600">
            <summary className="cursor-pointer font-medium text-gray-800">How pay is worked out</summary>
            <p className="mt-2">
              Payroll calculates PAYE, NSSF (5% employee / 10% employer) and Local Service Tax using standard Uganda bands.
              Confirm current rates with your accountant or URA before relying on this for a live run.
            </p>
            <p className="mt-2">
              A run must cover exactly one calendar month: salaries are not prorated, so a part month would still pay a
              full month. Active staff with no salary on file are listed on the run rather than quietly left out.
            </p>
          </details>

          {canInitiate && showPayrollForm && <form onSubmit={submitPayroll} className="card">
            <div className="card-titlebar">
              <div>
                <h3 className="font-semibold text-gray-900">Generate a pay run</h3>
                <p className="mt-1 text-sm text-gray-500">Works out every active staff member's pay for one calendar month.</p>
              </div>
              <button type="button" onClick={() => setShowPayrollForm(false)} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close the pay run form">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="card-content pt-5">
              <FormSection title="Run" divided={false} columns={3}>
                <Field label="Run name"><input required name="name" className="input mt-1" placeholder="e.g. September 2026" /></Field>
                <Field label="Month starts" hint="The 1st of the month."><input required name="start" type="date" className="input mt-1" /></Field>
                <Field label="Month ends" hint="The last day of the same month."><input required name="end" type="date" className="input mt-1" /></Field>
                <Field label="Notes" optional className="sm:col-span-2 lg:col-span-3">
                  <textarea name="notes" rows={2} className="input mt-1 h-auto min-h-[3.5rem] resize-y py-2 leading-5" />
                </Field>
              </FormSection>
            </div>
            <div className="card-footer">
              <p className="mr-auto text-xs text-gray-500">Generated as a draft; nothing is posted until it is approved.</p>
              <button disabled={generatePayroll.isPending} className="btn btn-primary">{generatePayroll.isPending ? "Generating…" : "Generate payroll"}</button>
            </div>
          </form>}

          <ClientRegister
            title="Pay runs"
            description="Open a run to see every payslip, adjust deductions, move it through approval and pay it."
            actions={canInitiate && !showPayrollForm && (
              <button type="button" onClick={() => setShowPayrollForm(true)} className="btn-primary h-8 min-h-8 px-3 text-xs">
                <Plus className="mr-1.5 h-3.5 w-3.5" /> Generate payroll
              </button>
            )}
            rows={payrollPeriods.data}
            config={payrollConfig}
            columns={payrollColumns}
            rowKey={(run) => run.id}
            onOpen={(run) => openRun(run.id)}
            openLabel={(run) =>
              (canReview && run.workflow_status === "submitted" && run.prepared_by !== user?.id) ||
              (canApprove && run.workflow_status === "verified" && run.prepared_by !== user?.id)
                ? "Review"
                : canInitiate && ["unpaid", "part_paid"].includes(payrollStage(run).key) ? "Pay" : "Open"
            }
            noun={["pay run", "pay runs"]}
            exportName={{ file: "pay-runs", title: "Pay Runs" }}
            isLoading={payrollPeriods.isLoading}
            isError={payrollPeriods.isError}
            onRetry={() => payrollPeriods.refetch()}
            emptyText="No pay runs yet. Generate the first month's payroll to start."
          />

          <div className="card">
            <div className="card-content">
              <h3 className="font-medium text-gray-900">Deductions held on someone else's behalf</h3>
              <p className="mt-0.5 text-sm text-gray-500">
                Withheld from staff pay when a run is approved, and owed onward until it is paid
                over. These are the totals across every month. Paying any of them happens under
                the run it belongs to, below, so every payment says which month it settles.
              </p>
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                {statutoryBalances.data?.map((balance) => (
                  <div key={balance.role} className="rounded-md border border-gray-200 p-3">
                    <p className="text-sm text-gray-500">{balance.label}</p>
                    {balance.configured ? (
                      <>
                        <p className="mt-1 text-lg font-semibold text-gray-900">
                          UGX {Number(balance.outstanding).toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500">
                          {balance.account} · {balance.account_name}
                        </p>
                        {(balance.runs ?? []).length > 0 && (
                          <p className="mt-1 text-xs text-gray-500">
                            Owed for {(balance.runs ?? []).map((run) => run.name).join(", ")}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="mt-1 text-sm text-amber-700">
                        No account assigned to this role yet — set it up under Chart of Accounts → Account roles.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <SalaryAdvancesPanel
            accounts={accounts.data}
            isSelectable={canPayFrom}
            canInitiate={canInitiate}
            canReview={canReview}
            canApprove={canApprove}
            userId={user?.id}
          />

          {period && (() => {
            const mine = period.prepared_by === user?.id;
            const inReview = ["submitted", "verified"].includes(period.workflow_status || "");
            const canVerify = canReview && period.workflow_status === "submitted" && !mine;
            const canApproveThis = canApprove && period.workflow_status === "verified" && !mine;
            const canSendBack = canReview && inReview && !mine;
            const notes = payrollDecisionNotes.trim();
            const gross = period.payslips.reduce((sum, slip) => sum + Number(slip.gross_salary), 0);
            return (
              <DetailsModal
                size="4xl"
                title={`Pay run · ${period.name}`}
                headline={{
                  label: "Net pay",
                  value: `UGX ${Number(period.total_net_pay).toLocaleString()}`,
                  note: `${period.staff_count} staff · gross UGX ${gross.toLocaleString()}`,
                }}
                status={payrollStage(period)}
                notice={period.excluded_staff?.length > 0 ? (
                  <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    <span className="font-medium">{period.excluded_staff.length} active staff left out</span>, with no salary on file when this run was generated:{" "}
                    {period.excluded_staff.map((staff) => `${staff.name}${staff.employee_id ? ` (${staff.employee_id})` : ""}`).join(", ")}.
                    Set their salary, then cancel and re-run this month.
                  </p>
                ) : undefined}
                facts={[
                  ["Month", `${period.period_start} to ${period.period_end}`],
                  ["Prepared by", mine ? "You" : period.prepared_by_name],
                  ["Approved by", period.approved_by_name ? `${period.approved_by_name}${period.approved_at ? ` · ${period.approved_at.slice(0, 10)}` : ""}` : ""],
                  ["Paid so far", period.status === "approved" ? `UGX ${Number(period.paid_amount).toLocaleString()}` : ""],
                  ["Still owed to staff", period.status === "approved" ? `UGX ${Number(period.balance_due).toLocaleString()}` : ""],
                  ["Paid from", period.payment_account_name],
                ]}
                sections={[
                  { title: "Notes", body: period.notes },
                  { title: "Sent back", body: period.workflow_status === "returned" ? period.return_reason : "", tone: "warning" },
                ]}
                onClose={closeRun}
                footer={
                  <>
                    <div className="mr-auto flex flex-wrap items-center gap-2">
                      {period.staff_count > 0 && (
                        <button type="button" onClick={() => financialService.downloadPayrollPayslipsPdf(period.id)} className="btn-outline">
                          <Download className="mr-1.5 h-4 w-4" /> Payslips PDF
                        </button>
                      )}
                      {period.status === "approved" && canInitiate && (
                        <button
                          type="button"
                          className="btn-outline"
                          onClick={async () => {
                            setError("");
                            try {
                              await financialService.downloadPayrollPaymentFile(period.id);
                            } catch (failure) {
                              setError(await blobError(failure));
                            }
                          }}
                        >
                          <Download className="mr-1.5 h-4 w-4" /> Bank file
                        </button>
                      )}
                      {canInitiate && period.status === "draft" && (
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm(`Cancel the ${period.name} pay run? It can be generated again.`)) {
                              cancelPayroll.mutate(period.id, { onSuccess: closeRun });
                            }
                          }}
                          className="rounded-md px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                        >
                          Cancel run
                        </button>
                      )}
                    </div>
                    <button type="button" onClick={closeRun} className="btn-outline">Close</button>
                    {canInitiate && ["draft", "returned"].includes(period.workflow_status || "") && (
                      <button type="button" disabled={submitPayrollForApproval.isPending} onClick={() => submitPayrollForApproval.mutate(period.id)} className="btn btn-primary">
                        Submit for review
                      </button>
                    )}
                  </>
                }
              >
                {inReview && mine && (
                  <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">You prepared this run, so someone else reviews and approves it.</p>
                )}
                {(canVerify || canApproveThis || canSendBack) && (
                  <ActionPanel
                    title={canApproveThis ? "Approve this pay run" : "Review this pay run"}
                    description={canApproveThis
                      ? "Approving records what the school owes. No money leaves the bank until a payment is raised against this run and approved in its own right."
                      : "Mark it reviewed to pass it to an approver, or send it back if something needs to change."}
                  >
                    <Field as="div" label="Comments" hint="Needed to send it back, so the preparer knows what to change.">
                      <AIAssistField
                        rows={2}
                        value={payrollDecisionNotes}
                        onChange={(next) => setPayrollDecisionNotes(next)}
                        context={{ key: "financial.approval_note", params: { document_type: "financial.payrollperiod", id: period.id, purpose: "return" } }}
                        draftLabel="Draft what to change"
                        draftHint="Reads this pay run. Jot rough notes first for a better draft."
                      />
                    </Field>
                    <div className="flex flex-wrap justify-end gap-2">
                      {canSendBack && (
                        <button
                          type="button"
                          disabled={!notes || returnPayroll.isPending}
                          title={notes ? undefined : "Write what needs to change"}
                          onClick={() => returnPayroll.mutate({ id: period.id, reason: notes }, { onSuccess: () => setPayrollDecisionNotes("") })}
                          className="rounded-md border border-amber-200 px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-50 disabled:opacity-50"
                        >
                          Send back
                        </button>
                      )}
                      {canVerify && (
                        <button type="button" disabled={verifyPayroll.isPending} onClick={() => verifyPayroll.mutate({ id: period.id })} className="btn btn-primary">
                          Mark reviewed
                        </button>
                      )}
                      {canApproveThis && (
                        <button
                          type="button"
                          disabled={approvePayroll.isPending}
                          onClick={() => {
                            if (window.confirm(`Approve and post ${period.name}: net pay UGX ${Number(period.total_net_pay).toLocaleString()} for ${period.staff_count} staff?`)) {
                              approvePayroll.mutate({ id: period.id });
                            }
                          }}
                          className="btn btn-primary"
                        >
                          Approve &amp; post
                        </button>
                      )}
                    </div>
                  </ActionPanel>
                )}
                {(payrollSettlements.data ?? [])
                  .filter((s) => s.payroll_period === period.id && !s.journal_entry)
                  .filter((s) => !["rejected", "cancelled"].includes(s.workflow_status || ""))
                  .map((settlement) => (
                    <PendingSettlement
                      key={settlement.id}
                      settlement={settlement}
                      canReview={canReview}
                      canApprove={canApprove}
                      canInitiate={canInitiate}
                      userId={user?.id}
                      onVerify={() => verifySettlement.mutate({ id: settlement.id })}
                      onApprove={() => approveSettlement.mutate({ id: settlement.id })}
                      onReturn={(reason) => returnSettlement.mutate({ id: settlement.id, reason })}
                      onCancel={() => cancelSettlement.mutate({ id: settlement.id })}
                    />
                  ))}
                {period.status === "approved" && canInitiate && (
                  <PayrollPaymentPanel
                    periodId={period.id}
                    accounts={accounts.data}
                    isSelectable={canPayFrom}
                    onSubmit={submitPayrollPayment}
                    pending={settlePayroll.isLoading || payPayrollComponent.isLoading}
                  />
                )}
                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Payslips ({period.payslips.length})</h4>
                  <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200 p-3">
                    <table className="w-full min-w-[56rem] text-sm tabular-nums">
                      <thead>
                        <tr className="text-left text-gray-500">
                          <th className="py-1 pr-3">Staff</th>
                          <th className="py-1 pr-3">Staff ID</th>
                          <th className="py-1 pr-3 text-right">Basic pay</th>
                          <th className="py-1 pr-3 text-right">Allowances</th>
                          <th className="py-1 pr-3 text-right">Gross</th>
                          <th className="py-1 pr-3 text-right">PAYE</th>
                          <th className="py-1 pr-3 text-right">NSSF (employee)</th>
                          <th className="py-1 pr-3 text-right">LST</th>
                          <th className="py-1 pr-3 text-right">Deductions</th>
                          <th className="py-1 pr-3 text-right">Net pay</th>
                          <th className="py-1 pr-3 text-right">NSSF (employer)</th>
                          <th className="py-1 pr-3">Payslip</th>
                        </tr>
                      </thead>
                      <tbody>
                        {period.payslips.map((slip) => (
                          <tr key={slip.id} className="border-t">
                            <td className="py-1 pr-3">
                              {slip.staff_name}
                              {slip.tax_residency === "non_resident" && (
                                <span className="ml-1 rounded bg-amber-100 px-1 text-xs text-amber-800">non-resident</span>
                              )}
                            </td>
                            <td className="py-1 pr-3 text-gray-500">{slip.employee_id || "—"}</td>
                            <td className="py-1 pr-3 text-right">{Number(slip.basic_pay).toLocaleString()}</td>
                            <td className="py-1 pr-3 text-right">{Number(slip.allowances).toLocaleString()}</td>
                            <td className="py-1 pr-3 text-right">{Number(slip.gross_salary).toLocaleString()}</td>
                            <td className="py-1 pr-3 text-right">{Number(slip.paye_tax).toLocaleString()}</td>
                            <td className="py-1 pr-3 text-right">{Number(slip.nssf_employee).toLocaleString()}</td>
                            <td className="py-1 pr-3 text-right">{Number(slip.local_service_tax).toLocaleString()}</td>
                            {/* A salary advance being taken back, filled in by payroll.
                                Editable only while the run is still being prepared. */}
                            <td className="py-1 pr-3 text-right">
                              {canEditDeductions(period) ? (
                                <button
                                  className="text-primary-600 underline decoration-dotted"
                                  title={slip.other_deductions_notes || "Add a deduction"}
                                  onClick={() => setEditingDeduction(slip)}
                                >
                                  {Number(slip.other_deductions).toLocaleString()}
                                </button>
                              ) : (
                                <span title={slip.other_deductions_notes || ""}>
                                  {Number(slip.other_deductions).toLocaleString()}
                                </span>
                              )}
                            </td>
                            <td className="py-1 pr-3 text-right font-medium">{Number(slip.net_pay).toLocaleString()}</td>
                            {/* The school's own contribution, sitting apart from the
                                staff columns because it is a cost on top of gross
                                rather than anything deducted from it. */}
                            <td className="py-1 pr-3 text-right text-gray-500">{Number(slip.nssf_employer).toLocaleString()}</td>
                            <td className="py-1 pr-3">
                              <button
                                className="text-primary-600"
                                onClick={() => financialService.downloadPayslipPdf(slip.id)}
                              >PDF</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        {(() => {
                          const sum = (pick: (slip: Payslip) => string) =>
                            period.payslips.reduce((running, slip) => running + Number(pick(slip)), 0);
                          const cell = "py-1 pr-3 text-right";
                          return (
                            <tr className="border-t-2 border-gray-300 font-semibold">
                              <td className="py-1 pr-3" colSpan={2}>Total · {period.payslips.length} staff</td>
                              <td className={cell}>{sum((x) => x.basic_pay).toLocaleString()}</td>
                              <td className={cell}>{sum((x) => x.allowances).toLocaleString()}</td>
                              <td className={cell}>{sum((x) => x.gross_salary).toLocaleString()}</td>
                              <td className={cell}>{sum((x) => x.paye_tax).toLocaleString()}</td>
                              <td className={cell}>{sum((x) => x.nssf_employee).toLocaleString()}</td>
                              <td className={cell}>{sum((x) => x.local_service_tax).toLocaleString()}</td>
                              <td className={cell}>{sum((x) => x.other_deductions).toLocaleString()}</td>
                              <td className={cell}>{sum((x) => x.net_pay).toLocaleString()}</td>
                              <td className={`${cell} text-gray-500`}>{sum((x) => x.nssf_employer).toLocaleString()}</td>
                              <td />
                            </tr>
                          );
                        })()}
                      </tfoot>
                    </table>
                    {editingDeduction &&
                      period.payslips.some((slip) => slip.id === editingDeduction.id) && (
                        <form
                          onSubmit={submitDeductionEdit}
                          className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3"
                        >
                          <p className="text-sm font-medium text-gray-900">
                            Deduction for {editingDeduction.staff_name}
                          </p>
                          <p className="mt-0.5 text-xs text-gray-500">
                            Recovers this person's salary advance, off the take-home pay after
                            PAYE, NSSF and LST. It cannot be more than they still owe.
                          </p>
                          <div className="mt-2 grid gap-2 md:grid-cols-4">
                            <label className="text-sm text-gray-600">
                              Amount
                              <input
                                required
                                name="other_deductions"
                                type="number"
                                step="0.01"
                                min="0"
                                defaultValue={editingDeduction.other_deductions}
                                className="input mt-1"
                              />
                            </label>
                            <label className="text-sm text-gray-600 md:col-span-3">
                              What it is for
                              <input
                                name="notes"
                                defaultValue={editingDeduction.other_deductions_notes}
                                className="input mt-1"
                                placeholder="Salary advance recovery"
                              />
                            </label>
                          </div>
                          <div className="mt-2 flex gap-3">
                            <button className="btn btn-primary" disabled={editDeductions.isLoading}>
                              Save deduction
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline"
                              onClick={() => setEditingDeduction(null)}
                            >Cancel</button>
                          </div>
                        </form>
                      )}
                    <p className="mt-2 text-xs text-gray-500">
                      Gross is basic pay plus allowances. NSSF is worked out on gross. LST is a year's tax set by gross, taken in instalments from July to October, and it comes off before PAYE, which is then worked out on what is left — on non-resident bands for non-residents.
                      Employer NSSF is the school's own contribution on top of gross, not a deduction
                      from anyone's pay. Deductions are salary advances being taken back: payroll
                      fills them in from what each person owes, and they come off the take-home pay
                      at the end, so gross and every tax on it stay where they are.
                      {canEditDeductions(period) && " Click a deduction figure to change it."}
                    </p>
                  </div>
                </section>
              </DetailsModal>
            );
          })()}
        </div>
        );
      })()}

      {activeTab === "trial-balance" && (
        <div className="space-y-4">
          {trialBalanceAccountId ? (
            <>
              <Link to="/financial/trial-balance" className="inline-flex items-center gap-1 text-sm text-primary-700 hover:underline">
                <ArrowLeft className="h-4 w-4" /> Back to trial balance
              </Link>
              <div className="card">
                <div className="card-content flex flex-wrap items-end justify-between gap-4">
                  <div className="flex flex-wrap items-end gap-3">
                    <label className="text-sm">
                      <div className="text-xs font-medium text-gray-500">From</div>
                      <input type="date" className="input" value={reportStart} onChange={(event) => setReportStart(event.target.value)} />
                    </label>
                    <label className="text-sm">
                      <div className="text-xs font-medium text-gray-500">To</div>
                      <input type="date" className="input" value={reportEnd} onChange={(event) => setReportEnd(event.target.value)} />
                    </label>
                    <button
                      type="button"
                      className="btn btn-outline px-3 py-2 text-sm"
                      onClick={() => { setReportStart(""); setReportEnd(""); }}
                    >
                      Whole history
                    </button>
                  </div>
                  {canExportReports && (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="btn btn-outline inline-flex items-center gap-1.5 px-3 py-2 text-sm"
                        onClick={() => financialService.downloadAccountLedger(Number(trialBalanceAccountId), "csv", { start_date: reportStart, end_date: reportEnd, include_subaccounts: drillIncludesSubaccounts })}
                      >
                        <FileSpreadsheet className="h-4 w-4" /> CSV
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline inline-flex items-center gap-1.5 px-3 py-2 text-sm"
                        onClick={() => financialService.downloadAccountLedger(Number(trialBalanceAccountId), "pdf", { start_date: reportStart, end_date: reportEnd, include_subaccounts: drillIncludesSubaccounts })}
                      >
                        <Download className="h-4 w-4" /> PDF
                      </button>
                    </div>
                  )}
                  <p className="basis-full text-xs text-gray-500">
                    Leave the dates empty to run from when the account was opened up to today.
                  </p>
                </div>
              </div>
              <div className="card overflow-x-auto">
                <div className="card-content">
                  {drillAccount.isLoading && <p className="text-sm text-gray-500">Loading account…</p>}
                  {drillAccount.data && (
                    <>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <h3 className="font-semibold">
                            <span className="font-mono">{drillAccount.data.account.code}</span> · {drillAccount.data.account.name}
                          </h3>
                          <p className="text-xs capitalize text-gray-500">
                            {drillAccount.data.account.account_type}
                            {drillAccount.data.account.is_subaccount && " · subaccount"}
                            {drillAccount.data.account.is_reporting_account && " · summary only"}
                            {" · "}{drillAccount.data.opening_date} to {drillAccount.data.closing_date}
                          </p>
                        </div>
                        {!!drillAccount.data.subaccounts?.length && (
                          <label className="flex items-center gap-2 text-xs text-gray-600">
                            <input
                              type="checkbox"
                              checked={drillIncludesSubaccounts}
                              onChange={(event) => setDrillIncludesSubaccounts(event.target.checked)}
                              disabled={drillAccount.data.account.is_reporting_account}
                            />
                            Include its {drillAccount.data.subaccounts.length} subaccount
                            {drillAccount.data.subaccounts.length === 1 ? "" : "s"}
                          </label>
                        )}
                      </div>

                      {!!drillAccount.data.subaccounts?.length && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {drillAccount.data.subaccounts.map((child) => (
                            <Link
                              key={child.id}
                              to={`/financial/trial-balance/${child.id}`}
                              className="rounded border border-secondary-200 px-2 py-1 text-xs hover:border-primary-400 hover:text-primary-700"
                            >
                              <span className="font-mono">{child.code}</span> · {child.name}
                            </Link>
                          ))}
                        </div>
                      )}

                      <table className="mt-4 min-w-full divide-y text-sm">
                        <thead>
                          <tr className="text-left text-xs uppercase text-gray-500">
                            <th className="py-2">Date</th><th className="py-2">Transaction ID</th><th className="py-2">Description</th>
                            {drillAccount.data.includes_subaccounts && <th className="py-2">Posted to</th>}
                            <th className="py-2">Corresponding account(s)</th>
                            <th className="py-2 text-right">Debit</th><th className="py-2 text-right">Credit</th><th className="py-2 text-right">Balance</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          <tr className="text-gray-500">
                            <td className="py-2">{drillAccount.data.opening_date}</td>
                            <td className="py-2" />
                            <td className="py-2">Opening balance</td>
                            <td className="py-2" colSpan={drillAccount.data.includes_subaccounts ? 4 : 3} />
                            <td className="py-2 text-right">{Number(drillAccount.data.opening_balance).toLocaleString()}</td>
                          </tr>
                          {drillAccount.data.transactions.map((item, index) => (
                            <tr key={index}>
                              <td className="py-2">{item.date}</td>
                              <td className="py-2">{item.entry_number}</td>
                              <td className="py-2">{item.description}</td>
                              {drillAccount.data?.includes_subaccounts && (
                                <td className="py-2 whitespace-nowrap font-mono text-xs">{item.posted_to_code}</td>
                              )}
                              <td className="py-2">{item.corresponding_accounts.map((other) => `${other.account_code} · ${other.account_name}`).join(", ") || "—"}</td>
                              <td className="py-2 text-right">{Number(item.debit).toLocaleString()}</td>
                              <td className="py-2 text-right">{Number(item.credit).toLocaleString()}</td>
                              <td className="py-2 text-right">{Number(item.balance).toLocaleString()}</td>
                            </tr>
                          ))}
                          {!drillAccount.data.transactions.length && (
                            <tr>
                              <td className="py-3 text-gray-500" colSpan={drillAccount.data.includes_subaccounts ? 8 : 7}>
                                No transactions in this period.
                              </td>
                            </tr>
                          )}
                        </tbody>
                        <tfoot>
                          <tr className="font-semibold">
                            <td className="py-2">{drillAccount.data.closing_date}</td>
                            <td className="py-2" />
                            <td className="py-2">Closing balance</td>
                            <td className="py-2" colSpan={drillAccount.data.includes_subaccounts ? 4 : 3} />
                            <td className="py-2 text-right">{Number(drillAccount.data.closing_balance).toLocaleString()}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (
            (() => {
              const trialBalance = accounting.data?.trial_balance;
              const rows = trialBalance?.accounts ?? [];
              const amount = (value: string | number) => {
                const figure = Number(value);
                return figure ? figure.toLocaleString() : "—";
              };
              const totalDebit = Number(trialBalance?.total_debit ?? 0);
              const totalCredit = Number(trialBalance?.total_credit ?? 0);
              const difference = Math.abs(totalDebit - totalCredit);
              // Any type the list above does not name still shows, under its own heading.
              const knownTypes = new Set(TRIAL_BALANCE_GROUPS.map((group) => group.type));
              const groups = [
                ...TRIAL_BALANCE_GROUPS,
                ...Array.from(new Set(rows.map((row) => row.account_type)))
                  .filter((type) => !knownTypes.has(type))
                  .map((type) => ({ type, label: type })),
              ]
                .map((group) => ({ ...group, rows: rows.filter((row) => row.account_type === group.type) }))
                .filter((group) => group.rows.length);

              return (
                <div className="card">
                  <div className="card-content space-y-4">
                    <div className="flex flex-wrap items-end justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Trial balance</h3>
                        <p className="text-sm text-gray-500">
                          Cumulative as at {trialBalance?.as_at || reportEnd}, opening balances included.
                        </p>
                      </div>
                      <div className="flex flex-wrap items-end gap-3">
                        <label className="text-sm">
                          <div className="text-xs font-medium text-gray-500">As at</div>
                          <input type="date" className="input" value={reportEnd} onChange={(event) => setReportEnd(event.target.value)} />
                        </label>
                        {canExportReports && (
                          <>
                            <button
                              type="button"
                              className="btn btn-outline inline-flex items-center gap-1.5 px-3 py-2 text-sm"
                              onClick={() => financialService.downloadAccountingReport("trial_balance", "csv", { start_date: reportStart, end_date: reportEnd })}
                            >
                              <FileSpreadsheet className="h-4 w-4" /> CSV
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline inline-flex items-center gap-1.5 px-3 py-2 text-sm"
                              onClick={() => financialService.downloadAccountingReport("trial_balance", "pdf", { start_date: reportStart, end_date: reportEnd })}
                            >
                              <Download className="h-4 w-4" /> PDF
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {trialBalance && (
                      <div className="grid gap-3 sm:grid-cols-3">
                        <div className="rounded-lg border border-gray-200 px-4 py-3">
                          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Total debit</div>
                          <div className="mt-1 text-xl font-semibold tabular-nums text-gray-900">{totalDebit.toLocaleString()}</div>
                        </div>
                        <div className="rounded-lg border border-gray-200 px-4 py-3">
                          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Total credit</div>
                          <div className="mt-1 text-xl font-semibold tabular-nums text-gray-900">{totalCredit.toLocaleString()}</div>
                        </div>
                        {trialBalance.is_balanced ? (
                          <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-800">
                            <CheckCircle2 className="h-6 w-6 shrink-0" />
                            <div>
                              <div className="text-sm font-semibold">Balanced</div>
                              <div className="text-xs">Debits equal credits.</div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-800">
                            <AlertTriangle className="h-6 w-6 shrink-0" />
                            <div>
                              <div className="text-sm font-semibold">Out of balance</div>
                              <div className="text-xs">Difference of {difference.toLocaleString()}.</div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                            <th className="w-28 px-4 py-2.5">Code</th>
                            <th className="px-4 py-2.5">Account</th>
                            <th className="w-40 px-4 py-2.5 text-right">Debit</th>
                            <th className="w-40 px-4 py-2.5 text-right">Credit</th>
                          </tr>
                        </thead>
                        {accounting.isLoading && (
                          <tbody>
                            <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500">Loading trial balance…</td></tr>
                          </tbody>
                        )}
                        {!accounting.isLoading && !groups.length && (
                          <tbody>
                            <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500">Nothing has been posted up to this date.</td></tr>
                          </tbody>
                        )}
                        {groups.map((group) => {
                          const groupDebit = group.rows.reduce((sum, row) => sum + Number(row.trial_debit), 0);
                          const groupCredit = group.rows.reduce((sum, row) => sum + Number(row.trial_credit), 0);
                          return (
                            <tbody key={group.type} className="border-t border-gray-200">
                              <tr className="bg-gray-50/60">
                                <td colSpan={4} className="px-4 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-gray-700">
                                  {group.label}
                                </td>
                              </tr>
                              {group.rows.map((row) => (
                                <tr key={row.id} className="border-t border-gray-100 hover:bg-gray-50">
                                  <td className="px-4 py-2 font-mono text-xs text-gray-600">{row.code}</td>
                                  <td className="px-4 py-2" style={{ paddingLeft: `${1 + row.depth * 1.25}rem` }}>
                                    <Link
                                      to={`/financial/trial-balance/${row.id}`}
                                      className="text-primary-700 hover:underline"
                                    >
                                      {row.name}
                                    </Link>
                                    {!row.is_active && <span className="ml-2 text-xs capitalize text-amber-700">{row.status}</span>}
                                    {row.is_control_account && <span className="ml-2 rounded bg-purple-100 px-1.5 py-0.5 text-xs text-purple-700">Control</span>}
                                    {row.is_reporting_account && (
                                      <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">Summary</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-right tabular-nums">{amount(row.trial_debit)}</td>
                                  <td className="px-4 py-2 text-right tabular-nums">{amount(row.trial_credit)}</td>
                                </tr>
                              ))}
                              <tr className="border-t border-gray-100 text-gray-600">
                                <td />
                                <td className="px-4 py-2 text-xs font-medium">Total {group.label.toLowerCase()}</td>
                                <td className="px-4 py-2 text-right text-xs font-medium tabular-nums">{amount(groupDebit)}</td>
                                <td className="px-4 py-2 text-right text-xs font-medium tabular-nums">{amount(groupCredit)}</td>
                              </tr>
                            </tbody>
                          );
                        })}
                        {!!groups.length && (
                          <tfoot>
                            <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold text-gray-900">
                              <td />
                              <td className="px-4 py-3">Total</td>
                              <td className="px-4 py-3 text-right tabular-nums">{totalDebit.toLocaleString()}</td>
                              <td className="px-4 py-3 text-right tabular-nums">{totalCredit.toLocaleString()}</td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>

                    <p className="text-xs text-gray-500">
                      Main accounts only; each carries what was posted to it directly plus everything
                      posted to its subaccounts. Select an account to see its transactions.
                    </p>
                  </div>
                </div>
              );
            })()
          )}
        </div>
      )}

      {activeTab === "account-statement" && (
        <div className="space-y-4">
          <div className="card">
            <div className="card-content flex flex-wrap items-end gap-4">
              <label className="text-sm">
                <div className="text-xs font-medium text-gray-500">From</div>
                <input type="date" className="input" value={ledgerStart} onChange={(event) => setLedgerStart(event.target.value)} />
              </label>
              <label className="text-sm">
                <div className="text-xs font-medium text-gray-500">To</div>
                <input type="date" className="input" value={ledgerEnd} onChange={(event) => setLedgerEnd(event.target.value)} />
              </label>
              <button
                className="btn btn-outline px-3 py-1.5 text-sm"
                onClick={() => { setLedgerStart(""); setLedgerEnd(""); }}
              >
                Whole history
              </button>
              <p className="text-xs text-gray-500">
                Leave the dates empty to run from when the account was opened up to today.
              </p>
            </div>
          </div>
          <div className="card">
            <div className="card-content">
              <h3 className="font-semibold">Account statement / general ledger</h3>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <AccountSelect
                  className="w-full sm:w-96"
                  placeholder="Select an account…"
                  accounts={accounts.data}
                  isSelectable={isApproved}
                  value={ledgerAccountId}
                  onChange={(next) => {
                    setLedgerAccountId(next);
                    // Start on the branch view so the statement agrees with the
                    // account's trial balance figure, which covers its
                    // subaccounts too. Untick to see its own postings alone.
                    setLedgerIncludesSubaccounts(true);
                  }}
                />
                {canExportReports && ledgerAccountId && (
                  <div className="flex gap-3 text-xs">
                    <button className="text-primary-600 underline" onClick={() => financialService.downloadAccountLedger(Number(ledgerAccountId), "csv", { start_date: ledgerStart, end_date: ledgerEnd, include_subaccounts: ledgerIncludesSubaccounts })}>CSV</button>
                    <button className="text-primary-600 underline" onClick={() => financialService.downloadAccountLedger(Number(ledgerAccountId), "pdf", { start_date: ledgerStart, end_date: ledgerEnd, include_subaccounts: ledgerIncludesSubaccounts })}>PDF</button>
                  </div>
                )}
              </div>
              {ledgerReport.data && (
                <div className="mt-3 max-h-72 overflow-y-auto">
                  <table className="min-w-full divide-y text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase text-gray-500">
                        <th className="py-1">Date</th><th className="py-1">Transaction ID</th><th className="py-1">Description</th>
                        {ledgerReport.data.includes_subaccounts && <th className="py-1">Posted to</th>}
                        <th className="py-1">Corresponding account(s)</th>
                        <th className="py-1 text-right">Debit</th><th className="py-1 text-right">Credit</th><th className="py-1 text-right">Balance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      <tr className="text-gray-500">
                        <td className="py-1">{ledgerReport.data.opening_date}</td>
                        <td className="py-1" />
                        <td className="py-1">Opening balance</td>
                        <td className="py-1" colSpan={ledgerReport.data.includes_subaccounts ? 4 : 3} />
                        <td className="py-1 text-right">{Number(ledgerReport.data.opening_balance).toLocaleString()}</td>
                      </tr>
                      {ledgerReport.data.transactions.map((t, i) => (
                        <tr key={i}>
                          <td className="py-1">{t.date}</td><td className="py-1">{t.entry_number}</td><td className="py-1">{t.description}</td>
                          {ledgerReport.data?.includes_subaccounts && (
                            <td className="py-1 whitespace-nowrap font-mono text-xs">{t.posted_to_code}</td>
                          )}
                          <td className="py-1">{t.corresponding_accounts.map((item) => `${item.account_code} · ${item.account_name}`).join(", ") || "—"}</td>
                          <td className="py-1 text-right">{Number(t.debit).toLocaleString()}</td>
                          <td className="py-1 text-right">{Number(t.credit).toLocaleString()}</td>
                          <td className="py-1 text-right">{Number(t.balance).toLocaleString()}</td>
                        </tr>
                      ))}
                      {!ledgerReport.data.transactions.length && (
                        <tr><td className="py-3 text-gray-500" colSpan={ledgerReport.data.includes_subaccounts ? 8 : 7}>
                          No transactions in this period.
                        </td></tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="font-semibold">
                        <td className="py-2">{ledgerReport.data.closing_date}</td>
                        <td className="py-2" />
                        <td className="py-2">Closing balance</td>
                        <td className="py-2" colSpan={ledgerReport.data.includes_subaccounts ? 4 : 3} />
                        <td className="py-2 text-right">{Number(ledgerReport.data.closing_balance).toLocaleString()}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "financial-statements" && (
        <div className="space-y-4">
          <div className="card">
            <div className="card-content space-y-4">
              <div className="flex flex-wrap items-end gap-4">
                <div className="inline-flex rounded-md border border-secondary-200 p-0.5" role="group" aria-label="Statement">
                  {([
                    ["income_expenditure", "Income statement"],
                    ["financial_position", "Balance sheet"],
                    ["changes_in_equity", "Equity"],
                  ] as const).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={statementsShown === value}
                      onClick={() => setStatementsShown(value)}
                      className={`rounded px-3 py-1 text-xs font-medium ${
                        statementsShown === value ? "bg-primary-600 text-white" : "text-gray-600 hover:bg-secondary-50"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <label className="text-sm">
                  <div className="text-xs font-medium text-gray-500">From</div>
                  <input type="date" className="input" value={statementsStart} onChange={(e) => setStatementsStart(e.target.value)} />
                </label>
                <label className="text-sm">
                  <div className="text-xs font-medium text-gray-500">To</div>
                  <input type="date" className="input" value={statementsEnd} onChange={(e) => setStatementsEnd(e.target.value)} />
                </label>
                {canExportReports && (
                  <div className="ml-auto flex gap-3 text-xs">
                    <button className="text-primary-600 underline" onClick={() => financialService.downloadFinancialStatement(statementsShown, "csv", { start_date: statementsStart, end_date: statementsEnd, comparative_start_date: comparativeStart, comparative_end_date: comparativeEnd })}>CSV</button>
                    <button className="text-primary-600 underline" onClick={() => financialService.downloadFinancialStatement(statementsShown, "pdf", { start_date: statementsStart, end_date: statementsEnd, comparative_start_date: comparativeStart, comparative_end_date: comparativeEnd })}>PDF</button>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-end gap-4 border-t border-secondary-200 pt-3">
                <div className="text-sm">
                  <div className="font-medium text-gray-700">Compare with an earlier period</div>
                  <p className="text-xs text-gray-500">
                    Adds a second column, so you can see whether things moved rather than only where they stand.
                  </p>
                </div>
                <label className="text-sm">
                  <div className="text-xs font-medium text-gray-500">From</div>
                  <input type="date" className="input" value={comparativeStart} onChange={(e) => setComparativeStart(e.target.value)} />
                </label>
                <label className="text-sm">
                  <div className="text-xs font-medium text-gray-500">To</div>
                  <input type="date" className="input" value={comparativeEnd} onChange={(e) => setComparativeEnd(e.target.value)} />
                </label>
                {(comparativeStart || comparativeEnd) && (
                  <button
                    className="btn btn-outline px-3 py-1.5 text-sm"
                    onClick={() => { setComparativeStart(""); setComparativeEnd(""); }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>
          {canConfigureAccounting && (
            <div className="flex justify-end">
              <button
                className="btn btn-outline px-3 py-1.5 text-sm"
                aria-pressed={editingTemplate}
                onClick={() => setEditingTemplate((open) => !open)}
              >
                {editingTemplate ? "Done editing template" : "Edit template"}
              </button>
            </div>
          )}
          {canConfigureAccounting && editingTemplate ? (
            <StatementTemplateEditor
              sections={statementSections.data}
              onAddSection={(data) => addSection.mutate(data)}
              onRenameSection={(data) => renameSection.mutate(data)}
              onDeleteSection={(id) => removeSection.mutate(id)}
              onAddCategory={(data) => addCategory.mutate(data)}
              onRenameCategory={(data) => renameCategory.mutate(data)}
              onDeleteCategory={(id) => removeCategory.mutate(id)}
              onReorder={(data) => reorderTemplate.mutate(data)}
            />
          ) : (
            <FinancialStatementsView
              data={financialStatements.data}
              statement={statementsShown}
              sections={statementSections.data}
              canFile={canConfigureAccounting}
              onFile={(data) => fileAccountInStatement.mutate(data)}
            />
          )}
        </div>
      )}

      {activeTab === "reports" && (
        <ManagementReportsView
          reportStart={reportStart}
          reportEnd={reportEnd}
          asOfDate={asOfDate}
          onPeriodChange={(start, end) => {
            setReportStart(start);
            setReportEnd(end);
          }}
          onAsOfChange={setAsOfDate}
          canExport={canExportReports}
          canViewAuditLogs={canViewAuditLogs}
          onOpenStudent={setViewStudent}
          bankSignOff={{
            busy: prepareBankSignOff.isPending || withdrawBankSignOff.isPending,
            onPrepare: canReconcile ? (bankAccountId, as_at, notes) => prepareBankSignOff.mutate({ bankAccountId, as_at, notes }) : undefined,
            onWithdraw: canApprove ? (id, reason) => withdrawBankSignOff.mutate({ id, reason }) : undefined,
          }}
        />
      )}

      {viewStudent && (
        <StudentFinancialRecord
          student={viewStudent}
          canExport={canExportReports}
          onClose={() => setViewStudent(null)}
        />
      )}

      <ConfirmDialog request={dialog} onClose={() => setDialog(null)} />
    </div>
  );
}
